import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type FormEvent, type MouseEvent, type ReactNode } from "react";
import { api, ApiError, apiUpload, jsonBody } from "./api";
import { issueLabel, uniqueProductIssues } from "./issues";
import type {
  CancelJobTarget,
  CatalogData,
  CopyStatus,
  Decision,
  DoubaoExportFile,
  DoubaoExportFolder,
  DoubaoExportRequest,
  DoubaoExportResult,
  DoubaoExportsData,
  DoubaoExportVersion,
  Filters,
  FinalStatus,
  GenerationJobCancelData,
  GenerationQueueData,
  ImageQualityGrade,
  MarketingCollectionsData,
  MarketingCollectionSummary,
  MarketingOfferType,
  OwnerPhoto,
  OwnerPhotoUploadRequest,
  PinterestBoardReference,
  PinterestBoardSnapshot,
  PinterestReferencesData,
  PinterestShotType,
  ProductDetailData,
  ProductSummary,
  QueueEntry,
  Role,
  SessionInfo,
  SlotSkipRequest,
  VisualCatalogProduct,
  VisualCurrentProjectData,
  VisualGenerationJob,
  VisualGenerationOutput,
  VisualGenerationSnapshot,
  VisualOutputReviewData,
  VisualSourceUpscaleData,
  VisualOutputReviewDecision,
  VisualPhase,
  VisualProductImageJobType,
  VisualProduct,
  VisualProject,
  VisualProjectDetailData,
  VisualProjectsData,
  VisualReviewStatus,
  VisualWorkbenchManifest,
} from "./types";

type WorkspaceMode = "catalog" | "visual";
type VisualShotTypeFilter = PinterestShotType | "all";
type ReferenceSyncStatus = "idle" | "syncing" | "retrying" | "waiting" | "synced" | "failed";
// One board sync: the board to show (a stale one too) and whether it is fresh.
interface ReferenceSyncResult { ok: boolean; snapshot: PinterestBoardSnapshot | null }
// 刷新 on step 3: the shuffled order (null = the board's own), the pins that
// get a 新 badge and the toast. `undefined` keeps the order already on screen.
interface ReferenceRefreshOutcome { order: string[] | null | undefined; newPinIds: string[]; message: string }

const MAX_SCENE_PRODUCTS = 15;
// Colours (variants) chosen per product; the first one chosen is its 主色.
const MAX_VARIANTS_PER_PRODUCT = 8;
const MAX_REFERENCES_PER_RUN = 12;
const MAX_JOBS_PER_RUN = 30;
// A combo image shows each of its variants once (those in stock plus any the owner
// chose although the 货盘 shows them sold out); the generator takes 2–12.
const MAX_COMBO_SKUS = 12;
// A poll stuck on the slow link is dropped so polling can start again.
const GENERATION_POLL_TIMEOUT_MS = 30_000;
// The server validates and retries the whole Board traversal too. The page
// gives a dropped request a shorter bound, then reuses the server's single
// flight instead of leaving the button stuck indefinitely.
const REFERENCE_SYNC_TIMEOUT_MS = 90_000;
const REFERENCE_SYNC_RETRY_DELAYS_MS = [0, 2_000, 8_000] as const;
const REFERENCE_SYNC_BACKGROUND_RETRY_MS = 120_000;
// The generator refuses longer 修改 notes.
const MAX_REVISION_NOTE_LENGTH = 300;
// Every request that asks the generator for work (生成, 重做, 修改 → 保存, 保留 / 不用)
// gives up after a minute, so 提交中… never sticks; the work may still go on.
const GENERATION_POST_TIMEOUT_MS = 60_000;
const GENERATION_SLOW_MESSAGE = "网络有点慢，已在后台继续";
// 全部任务: the one queue of every theme, read often only while it is open.
const GENERATION_QUEUE_POLL_MS = 15_000;
const GENERATION_QUEUE_OPEN_POLL_MS = 5_000;
// Images over the slow link: wait, then try again a few times before asking for a tap.
const IMAGE_RETRY_DELAY_MS = 8_000;
const IMAGE_MAX_RETRIES = 3;
// 5 宝贝: reading a product's earlier exports uses the one-minute limit;
// 导出到本地文件夹 resizes every image on the Mac and answers over the slow
// link, so it gets longer.
const LISTING_REQUEST_TIMEOUT_MS = 60_000;
const LISTING_EXPORT_TIMEOUT_MS = 150_000;
// Taobao takes 5 主图; the exporter takes at most 30 详情 images.
const LISTING_MAX_MAIN_IMAGES = 5;
const LISTING_MAX_DETAIL_IMAGES = 30;
// Step 3 reads the board's 缩略图 marks again this often while some are still on their way.
const REFERENCE_THUMB_POLL_MS = 30_000;

const visualShotTypeLabels: Record<PinterestShotType, string> = {
  scene: "场景",
  still: "静物",
  studio: "棚拍",
};

const referenceFilters: Array<[VisualShotTypeFilter, string]> = [
  ["all", "全部"],
  ["scene", "场景"],
  ["still", "静物"],
  ["studio", "棚拍"],
];

interface VisualQueueItem {
  id: string;
  projectId?: string;
  /** Composite board reference id (pin:<snapshotId>:<pinId>) or a legacy id. */
  referenceId: string;
  pinId?: string;
  referenceImageUrl?: string;
  productIds: string[];
  createdAt: string;
}

interface VisualGeneratedFeedback {
  status: VisualReviewStatus;
  note: string;
}

/** A tap in 全部任务: open this theme at the step that shows the job. */
interface VisualJump {
  collectionId: string;
  phase: Extract<VisualPhase, "product-images" | "creative">;
  catalogProductId: string | null;
  sourceSkuId: string | null;
}

/** The queue item id of a request whose answer never came, and what it asked for. */
interface PendingRequest {
  queueItemId: string;
  requestKey: string;
}

const generationStageLabels: Record<string, string> = {
  queued: "排队中",
  "starting-worker": "准备中",
  "loading-sources": "读取商品图",
  generating: "生成中",
  "saving-output": "保存图片",
  "validating-output": "检查图片",
  completed: "已完成",
  failed: "生成失败",
};

const VISUAL_REVIEW_KEY = "furryjoy.visual-workbench.review.v1";
const VISUAL_FEEDBACK_KEY = "furryjoy.visual-workbench.generated-feedback.v1";
const VISUAL_QUEUE_KEY = "furryjoy.visual-workbench.queue.v1";
const VISUAL_CURRENT_THEME_KEY = "furryjoy.visual-workbench.current-theme.v1";
// Per theme: step, chosen products, chosen variants and chosen board pins, so a
// refresh (or switching back to a theme) continues where it was left.
const VISUAL_STATE_KEY = "furryjoy.visual-workbench.state.v1";
const VISUAL_PHASES: readonly VisualPhase[] = ["truth", "product-images", "references", "creative", "listing"];
// 5 宝贝: what was typed on a product card but not saved yet, so a refresh keeps it.
// 5 宝贝: per card, the arranged 主图 and the 上架 switches (this browser only).
const VISUAL_LISTING_CARDS_KEY = "furryjoy.visual-workbench.listing-cards.v2";

const legacyCollectionProjectIds: Record<string, string> = {
  "buy3gift-mini-snacks-ready": "dog-mini-snack-shop",
  "single-ready-snacks": "in-stock-snacks",
  "single-plush-squeakers-ready": "plush-squeakers-playground",
};

function legacyProjectIdForCollection(collectionId: string): string {
  return legacyCollectionProjectIds[collectionId] ?? collectionId;
}

function collectionIdForLegacyProject(projectId: string): string | undefined {
  return Object.entries(legacyCollectionProjectIds).find(([, candidate]) => candidate === projectId)?.[0];
}

const EMPTY_FILTERS: Filters = {
  q: "",
  supplier: "",
  category: "",
  minPrice: "",
  maxPrice: "",
  stockStatus: "",
  hasRisk: false,
  finalStatus: "",
  copyStatus: "",
  myVote: "",
  imageQuality: "upload_ready",
  sort: "recommendation",
  page: 1,
};

const decisionLabels: Record<Decision, string> = {
  selected: "选",
  pending: "再看看",
  rejected: "不选",
};

const finalLabels: Record<FinalStatus, string> = {
  unreviewed: "未处理",
  selected: "已入选",
  pending: "未决定",
  rejected: "不用",
};

const copyLabels: Record<CopyStatus, string> = {
  draft_pending: "草稿待确认",
  approved: "文案已确认",
  needs_edit: "文案需修改",
};

const imageQualityLabels: Record<ImageQualityGrade, string> = {
  ready: "图片优秀",
  usable: "高清可用",
  poor: "需补原图",
};

function formatPrice(value: number | null): string {
  return value === null ? "待询价" : `¥${value.toFixed(value % 1 === 0 ? 0 : 2)}`;
}

function compactNumber(value: number | null): string {
  if (value === null) return "—";
  return new Intl.NumberFormat("zh-CN", { notation: value > 9999 ? "compact" : "standard" }).format(value);
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "发生了未知错误";
}

function buildSearch(filters: Filters, collectionId = ""): string {
  const params = new URLSearchParams({ page: String(filters.page), pageSize: "36", sort: filters.sort });
  for (const key of ["q", "supplier", "category", "minPrice", "maxPrice", "finalStatus", "copyStatus", "myVote", "imageQuality"] as const) {
    if (filters[key]) params.set(key, String(filters[key]));
  }
  if (filters.stockStatus) params.set("stockStatus", filters.stockStatus);
  if (filters.hasRisk) params.set("hasRisk", "true");
  if (collectionId) params.set("collection", collectionId);
  return params.toString();
}

/**
 * A link into 做图: `?theme=<collectionId>&step=<step>&product=<catalogProductId>`.
 * The step names in the address, and the step each one opens.
 */
const visualLinkSteps: ReadonlyArray<readonly [string, VisualPhase]> = [
  ["products", "truth"],
  ["product-images", "product-images"],
  ["references", "references"],
  ["generate", "creative"],
  ["listing", "listing"],
];
const VISUAL_LINK_KEYS: readonly string[] = ["theme", "step", "product"];
// Theme and product ids as the generator accepts them; anything else is ignored.
const VISUAL_LINK_ID = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,159}$/;

/** What a link asks for; "" / null = not given (or not usable). */
interface VisualLink {
  theme: string;
  phase: VisualPhase | null;
  /** One 宝贝 of step 5; only ever set together with phase "listing". */
  product: string;
}

/**
 * Reads a link from a query string. Unknown or broken values are dropped, never
 * thrown: a product opens 5 宝贝 (with another step it is ignored).
 */
function parseVisualLink(search: string): VisualLink {
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(search);
  } catch {
    return { theme: "", phase: null, product: "" };
  }
  const id = (key: string) => {
    const value = (params.get(key) ?? "").trim();
    return VISUAL_LINK_ID.test(value) ? value : "";
  };
  const step = params.get("step") ?? "";
  const phase = visualLinkSteps.find(([name]) => name === step)?.[1] ?? null;
  const product = id("product");
  if (product && (phase === null || phase === "listing")) return { theme: id("theme"), phase: "listing", product };
  return { theme: id("theme"), phase, product: "" };
}

/** True when the link asks for anything at all. */
function visualLinkActive(link: VisualLink | null): boolean {
  return Boolean(link && (link.theme || link.phase || link.product));
}

/** A link without a theme is for whichever theme opens. */
function visualLinkFor(link: VisualLink | null, collectionId: string): VisualLink | null {
  if (!link || !visualLinkActive(link)) return null;
  return !link.theme || link.theme === collectionId ? link : null;
}

/**
 * The query for a view of 做图: theme, step, and the product on 5 宝贝 only.
 * Other keys of `current` (such as entry=…) are kept after them.
 */
function visualLinkQuery(view: { theme: string; phase: VisualPhase; product?: string }, current = ""): string {
  const next = new URLSearchParams();
  if (VISUAL_LINK_ID.test(view.theme)) next.set("theme", view.theme);
  next.set("step", visualLinkSteps.find(([, phase]) => phase === view.phase)?.[0] ?? "products");
  if (view.phase === "listing" && view.product && VISUAL_LINK_ID.test(view.product)) next.set("product", view.product);
  for (const [key, value] of new URLSearchParams(current)) {
    if (!VISUAL_LINK_KEYS.includes(key)) next.append(key, value);
  }
  return next.toString();
}

/** The query once 做图 is left: no theme, step, product or entry=…, so a reload stays on 选品. */
function catalogLinkQuery(current: string): string {
  const next = new URLSearchParams();
  for (const [key, value] of new URLSearchParams(current)) {
    if (!VISUAL_LINK_KEYS.includes(key) && key !== "entry") next.append(key, value);
  }
  return next.toString();
}

/** The full address of a view, to copy and send: always on `/`. */
function visualLinkHref(origin: string, view: { theme: string; phase: VisualPhase; product?: string }): string {
  return `${origin}/?${visualLinkQuery(view)}`;
}

function readVisualLink(): VisualLink {
  try {
    return parseVisualLink(window.location.search);
  } catch {
    return { theme: "", phase: null, product: "" };
  }
}

/**
 * Puts a query in the address bar: replace (the usual: no history entry per
 * tap) or push (opening one 宝贝, so Back returns to the list). True when the
 * address was written (false: already right, or the browser refused).
 */
function writeAddressQuery(query: string, mode: "replace" | "push" = "replace"): boolean {
  try {
    const { pathname, search, hash } = window.location;
    const next = `${query ? `?${query}` : ""}`;
    if (next === search) return false;
    if (mode === "push") window.history.pushState(window.history.state, "", `${pathname}${next}${hash}`);
    else window.history.replaceState(window.history.state, "", `${pathname}${next}${hash}`);
    return true;
  } catch {
    // A browser that limits history calls keeps the page working without them.
    return false;
  }
}

function initialWorkspaceMode(): WorkspaceMode {
  const entry = new URLSearchParams(window.location.search).get("entry") ?? "";
  if (entry.startsWith("visual")) return "visual";
  // A link to a theme, step or 宝贝 opens 做图 directly.
  if (visualLinkActive(readVisualLink())) return "visual";
  return localStorage.getItem("fj-workspace-mode") === "visual" ? "visual" : "catalog";
}

export function App() {
  const [session, setSession] = useState<SessionInfo | null>(null);
  const [bootError, setBootError] = useState("");

  const loadSession = useCallback(async () => {
    try {
      const next = await api<SessionInfo>("/api/session");
      setSession(next);
      setBootError("");
    } catch (error) {
      setBootError(errorMessage(error));
    }
  }, []);

  useEffect(() => { void loadSession(); }, [loadSession]);

  if (!session) {
    return <CenteredStatus message={bootError || "正在打开选品台…"} action={bootError ? loadSession : undefined} />;
  }
  const openAccess = session.authMode === "open";
  if (!openAccess && !session.authenticated) return <LoginScreen onLogin={loadSession} />;
  if (!openAccess && session.role === "reviewer" && !session.profileId) {
    return <ProfileGate session={session} onSelected={loadSession} />;
  }
  return <CatalogWorkspace session={session} onSessionChanged={loadSession} />;
}

function CenteredStatus({ message, action }: { message: string; action?: () => void }) {
  return (
    <main className="centered-shell">
      <div className="brand-mark" aria-hidden="true">FJ</div>
      <p>{message}</p>
      {action && <button className="button primary" onClick={action}>重试</button>}
    </main>
  );
}

function LoginScreen({ onLogin }: { onLogin: () => Promise<void> }) {
  const [role, setRole] = useState<Role>("reviewer");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api("/api/session", { method: "POST", ...jsonBody({ role, password }) });
      await onLogin();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="login-page">
      <form className="login-card glass-panel" onSubmit={submit}>
        <div className="brand-lockup"><div className="brand-mark">FJ</div><strong>FurryJoy</strong></div>
        <h1>选品台</h1>
        <div className="segmented" role="group" aria-label="登录角色">
          <button type="button" className={role === "reviewer" ? "active" : ""} aria-pressed={role === "reviewer"} onClick={() => setRole("reviewer")}>伙伴</button>
          <button type="button" className={role === "admin" ? "active" : ""} aria-pressed={role === "admin"} onClick={() => setRole("admin")}>管理员</button>
        </div>
        <label className="field"><span>密码</span><input autoFocus type="password" value={password} onChange={(event) => setPassword(event.target.value)} placeholder="输入密码" autoComplete="current-password" /></label>
        {error && <div className="inline-error" role="alert">{error}</div>}
        <button className="button primary wide" disabled={busy || !password}>{busy ? "验证中…" : "进入"}</button>
      </form>
    </main>
  );
}

function ProfileGate({ session, onSelected }: { session: SessionInfo; onSelected: () => Promise<void> }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState("");

  async function select(profileId: string) {
    setBusy(profileId);
    setError("");
    try {
      await api("/api/session/profile", { method: "PUT", ...jsonBody({ profileId }) });
      await onSelected();
    } catch (caught) {
      setError(errorMessage(caught));
      setBusy(null);
    }
  }

  return (
    <main className="identity-page">
      <section className="identity-card glass-panel">
        <header><div className="brand-mark">FJ</div><h1>选择身份</h1></header>
        <div className="profile-grid">
          {session.profiles.map((profile) => (
            <button key={profile.id} className="profile-option" style={{ "--profile-color": profile.color } as React.CSSProperties} onClick={() => void select(profile.id)} disabled={Boolean(busy)} aria-label={`选择${profile.displayName}`}>
              <span>{profile.id}</span><strong>{profile.displayName}</strong>{busy === profile.id && <small>进入中…</small>}
            </button>
          ))}
        </div>
        {error && <div className="inline-error">{error}</div>}
      </section>
    </main>
  );
}

function CatalogWorkspace({ session, onSessionChanged }: { session: SessionInfo; onSessionChanged: () => Promise<void> }) {
  const [workspaceMode, setWorkspaceMode] = useState<WorkspaceMode>(initialWorkspaceMode);
  const [draftFilters, setDraftFilters] = useState(EMPTY_FILTERS);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [view, setView] = useState<"grid" | "table">(() => localStorage.getItem("fj-view") === "table" ? "table" : "grid");
  const [catalog, setCatalog] = useState<CatalogData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [detailId, setDetailId] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [toast, setToast] = useState("");
  const [marketing, setMarketing] = useState<MarketingCollectionsData | null>(null);
  const [marketingError, setMarketingError] = useState("");
  const [offerFilter, setOfferFilter] = useState<"all" | MarketingOfferType>("all");
  const [activeCollectionId, setActiveCollectionId] = useState("");
  const [campaignDetailId, setCampaignDetailId] = useState("");
  // A link to a theme opens that theme; 做图 checks it exists before keeping it.
  const [visualCollectionId, setVisualCollectionId] = useState(() => readVisualLink().theme || readStoredString(VISUAL_CURRENT_THEME_KEY));
  const [switchingIdentity, setSwitchingIdentity] = useState(false);
  // 全部任务: one queue for every theme, product images and scenes alike.
  const [loadedGenerationQueue, setGenerationQueue] = useState<GenerationQueueData | null>(null);
  const [generationQueueUnavailable, setGenerationQueueUnavailable] = useState(false);
  const [queueOpen, setQueueOpen] = useState(false);
  const [visualJump, setVisualJump] = useState<VisualJump | null>(null);
  const queuePillRef = useRef<HTMLButtonElement>(null);
  // 取消: the job asked about (confirm dialog), the request on its way, and
  // jobs cancelled here (job id → when) that leave every list at once.
  const [cancelTarget, setCancelTarget] = useState<CancelJobTarget | null>(null);
  const [cancellingJob, setCancellingJob] = useState(false);
  const [cancelledJobs, setCancelledJobs] = useState<ReadonlyMap<string, number>>(() => new Map());
  const cancelledJobIds = useMemo(() => new Set(cancelledJobs.keys()), [cancelledJobs]);
  const generationQueue = useMemo(() => hideCancelledQueueEntries(loadedGenerationQueue, cancelledJobIds), [cancelledJobIds, loadedGenerationQueue]);

  // One request at a time over the slow link; a refresh asked for while one
  // is loading runs once more right after it.
  const loadGenerationQueue = useMemo(() => singleFlight(async () => {
    try {
      const data = normalizeGenerationQueue(await fetchGenerationQueue());
      setGenerationQueue(data);
      setCancelledJobs((current) => pruneCancelledJobs(current, data, Date.now()));
      setGenerationQueueUnavailable(false);
    } catch {
      setGenerationQueueUnavailable(true);
    }
  }), []);
  const refreshGenerationQueue = useCallback(() => { void loadGenerationQueue({ again: true }); }, [loadGenerationQueue]);
  useEffect(() => {
    void loadGenerationQueue();
    const timer = window.setInterval(() => { if (!document.hidden) void loadGenerationQueue(); }, queueOpen ? GENERATION_QUEUE_OPEN_POLL_MS : GENERATION_QUEUE_POLL_MS);
    function refreshWhenShown() { if (!document.hidden) void loadGenerationQueue(); }
    document.addEventListener("visibilitychange", refreshWhenShown);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", refreshWhenShown);
    };
  }, [loadGenerationQueue, queueOpen]);

  useEffect(() => {
    if (workspaceMode !== "catalog") return;
    const controller = new AbortController();
    setLoading(true);
    setError("");
    void api<CatalogData>(`/api/catalog?${buildSearch(filters, activeCollectionId)}`, { signal: controller.signal })
      .then((data) => setCatalog(data))
      .catch((caught: unknown) => {
        if (!(caught instanceof DOMException && caught.name === "AbortError")) setError(errorMessage(caught));
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [activeCollectionId, filters, refreshKey, workspaceMode]);

  useEffect(() => {
    const controller = new AbortController();
    void api<MarketingCollectionsData>("/api/marketing-collections", { signal: controller.signal })
      .then((data) => { setMarketing(data); setMarketingError(""); })
      .catch((caught: unknown) => {
        if (!(caught instanceof DOMException && caught.name === "AbortError")) setMarketingError(errorMessage(caught));
      });
    return () => controller.abort();
  }, [refreshKey]);

  // On 选品 the address carries no 做图 view, so a reload stays here.
  useEffect(() => {
    if (workspaceMode === "catalog") writeAddressQuery(catalogLinkQuery(window.location.search));
  }, [workspaceMode]);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(""), 3_000);
    return () => window.clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    if (!queueOpen) return;
    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setQueueOpen(false);
        queuePillRef.current?.focus();
      }
    }
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [queueOpen]);

  function applyFilters(event?: FormEvent) {
    event?.preventDefault();
    setFilters({ ...draftFilters, page: 1 });
  }

  function changeView(next: "grid" | "table") {
    setView(next);
    localStorage.setItem("fj-view", next);
  }

  function changeWorkspace(next: WorkspaceMode) {
    setWorkspaceMode(next);
    localStorage.setItem("fj-workspace-mode", next);
    setDetailId(null);
    setCampaignDetailId("");
  }

  function selectCollection(id: string) {
    setActiveCollectionId(id);
    setDraftFilters(EMPTY_FILTERS);
    setFilters(EMPTY_FILTERS);
    window.setTimeout(() => document.querySelector(".active-campaign")?.scrollIntoView({ behavior: "smooth", block: "start" }), 0);
  }

  function persistVisualCollection(id: string) {
    setVisualCollectionId(id);
    localStorage.setItem(VISUAL_CURRENT_THEME_KEY, id);
    void api<VisualCurrentProjectData>("/api/visual-projects/current", {
      method: "PUT",
      ...jsonBody({ collectionId: id }),
    }).then((result) => {
      if (result.currentCollectionId && result.currentCollectionId !== id) {
        setVisualCollectionId(result.currentCollectionId);
        localStorage.setItem(VISUAL_CURRENT_THEME_KEY, result.currentCollectionId);
      }
    }).catch(() => {
      setToast("主题已切换，但没保存到服务器");
    });
  }

  function startVisualProduction(id: string) {
    setActiveCollectionId(id);
    setCampaignDetailId("");
    persistVisualCollection(id);
    setWorkspaceMode("visual");
    localStorage.setItem("fj-workspace-mode", "visual");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  // 入选的去做图: 我入选的商品 in 做图 (the 选品 page's theme filter stays as it is).
  function startPickedVisual() {
    persistVisualCollection(PICKED_COLLECTION_ID);
    setWorkspaceMode("visual");
    localStorage.setItem("fj-workspace-mode", "visual");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function closeQueue() {
    setQueueOpen(false);
    queuePillRef.current?.focus();
  }

  // 取消 (全部任务 or 4 生成) asks first; nothing is sent until 取消任务.
  function askCancelJob(target: CancelJobTarget) {
    if (!cancellingJob) setCancelTarget(target);
  }

  function closeCancelDialog() {
    if (!cancellingJob) setCancelTarget(null);
  }

  // 取消任务: on success the job leaves the queue, 4 生成 and step 2 at once
  // (a step-2 image shows 缺 again, nothing is asked for again by itself).
  async function confirmCancelJob() {
    const target = cancelTarget;
    if (!target || cancellingJob) return;
    setCancellingJob(true);
    const outcome = await cancelGenerationJob(target.jobId);
    setCancellingJob(false);
    if (outcome.cancelled) setCancelledJobs((current) => new Map(current).set(target.jobId, Date.now()));
    if (outcome.close) setCancelTarget(null);
    setToast(outcome.message);
    if (outcome.cancelled || outcome.close) refreshGenerationQueue();
  }

  // A tap in 全部任务 opens the job's theme in 做图 at the step that shows it.
  function openQueueEntry(entry: QueueEntry) {
    const jump = queueEntryJump(entry);
    setQueueOpen(false);
    setVisualJump(jump);
    if (jump.collectionId !== visualCollectionId) persistVisualCollection(jump.collectionId);
    if (workspaceMode !== "visual") changeWorkspace("visual");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function logout() {
    await api("/api/session", { method: "DELETE" });
    await onSessionChanged();
  }

  async function switchIdentity(value: string) {
    setSwitchingIdentity(true);
    try {
      await api("/api/session", {
        method: "POST",
        ...jsonBody(value === "admin" ? { role: "admin" } : { role: "reviewer", profileId: value }),
      });
      await onSessionChanged();
      // Votes are per identity; the catalog reloads by itself when 选品 is opened again.
      if (workspaceMode === "catalog") setRefreshKey((key) => key + 1);
    } catch {
      setToast("切换身份失败，请重试");
    } finally {
      setSwitchingIdentity(false);
    }
  }

  async function quickVote(product: ProductSummary, value: Decision, event: MouseEvent) {
    event.stopPropagation();
    try {
      await api(`/api/products/${encodeURIComponent(product.id)}/vote`, { method: "PUT", ...jsonBody({ decision: value }) });
      setRefreshKey((current) => current + 1);
      setToast(`已记录“${decisionLabels[value]}”`);
    } catch (caught) {
      setToast(errorMessage(caught));
    }
  }

  async function createExport() {
    try {
      const result = await api<{ downloadUrl: string; item_count?: number; itemCount?: number }>("/api/exports", {
        method: "POST",
        ...jsonBody({ format: "master_csv", onlySelected: true, onlyApprovedCopy: true, onlyImageUploadReady: true }),
      });
      setToast("导出好了，正在下载…");
      window.location.href = result.downloadUrl;
    } catch (caught) {
      setToast(errorMessage(caught));
    }
  }

  const currentProfile = session.profiles.find((profile) => profile.id === session.profileId);
  const identityValue = session.role === "admin" ? "admin" : currentProfile?.id ?? "";
  const metrics = catalog?.metrics;
  const activeCollection = marketing?.items.find((item) => item.id === activeCollectionId) ?? null;
  const detailCollection = marketing?.items.find((item) => item.id === campaignDetailId) ?? null;
  const activeRoles = new Map(activeCollection?.members.map((member) => [member.productId, member.role]) ?? []);
  const activeCollectionIsCandidate = activeCollection?.offer.type === "candidate_pool";
  const queueCount = generationQueue ? generationQueue.running.length + generationQueue.queued.length : null;
  return (
    <div className="app-shell">
      <header className="topbar">
        <div className="brand-lockup"><div className="brand-mark small">FJ</div><strong>{workspaceMode === "catalog" ? "选品台" : "做图"}</strong></div>
        <nav className="top-actions">
          <div className="workspace-mode-switch" role="tablist" aria-label="工作台">
            <button type="button" role="tab" aria-selected={workspaceMode === "catalog"} className={workspaceMode === "catalog" ? "active" : ""} onClick={() => changeWorkspace("catalog")}>选品</button>
            <button type="button" role="tab" aria-selected={workspaceMode === "visual"} className={workspaceMode === "visual" ? "active" : ""} onClick={() => changeWorkspace("visual")}>做图</button>
          </div>
          <button type="button" ref={queuePillRef} className={`queue-pill ${generationQueue && generationQueue.running.length > 0 ? "busy" : ""}`} data-testid="queue-pill" aria-haspopup="dialog" aria-expanded={queueOpen} aria-label={queueCount === null ? "队列" : `队列 ${queueCount}`} onClick={() => setQueueOpen(true)}>
            <i aria-hidden="true" />队列{queueCount !== null && <b>{queueCount}</b>}
          </button>
          {workspaceMode === "catalog" && session.role === "admin" && <button className="button soft" onClick={() => void createExport()}>导出</button>}
          {session.authMode === "open"
            ? <select className="identity-select" aria-label="身份" value={identityValue} disabled={switchingIdentity} onChange={(event) => void switchIdentity(event.target.value)}>
              {!identityValue && <option value="" disabled>身份</option>}
              <option value="admin">管理员</option>
              {session.profiles.map((profile) => <option key={profile.id} value={profile.id}>{profile.displayName}</option>)}
            </select>
            : <>
              <span className="identity-pill" style={{ "--profile-color": currentProfile?.color ?? "#273a38" } as React.CSSProperties}>{session.role === "admin" ? "管理员" : currentProfile?.displayName}</span>
              <button className="icon-button" title="退出" aria-label="退出" onClick={() => void logout()}>↗</button>
            </>}
        </nav>
      </header>

      <main className="workspace">
        {workspaceMode === "catalog" ? <>
        <section className="metric-strip glass-panel" aria-label="商品概览">
          <Metric label="全部" value={metrics?.total} />
          <Metric label="有库存" value={metrics?.inStock} />
          <Metric label="管理员已入选" value={metrics?.finalSelected} accent />
          {session.role === "admin" && <Metric label="文案已确认" value={metrics?.copyApproved} />}
        </section>
        {/* 我入选的商品 has no theme: this is its way into 做图 (also in 做图's 主题 list). */}
        {Boolean(metrics?.finalSelected) && <div className="picks-shortcut">
          <button type="button" className="button soft" data-testid="start-visual-picks" onClick={startPickedVisual}>入选的 {metrics?.finalSelected} 款去做图 <span aria-hidden="true">→</span></button>
        </div>}

        <MarketingCollectionRail
          data={marketing}
          error={marketingError}
          offerFilter={offerFilter}
          activeId={activeCollectionId}
          onFilter={setOfferFilter}
          onSelect={selectCollection}
          onStartVisual={startVisualProduction}
        />

        {activeCollection && <ActiveCampaign collection={activeCollection} onDetails={() => setCampaignDetailId(activeCollection.id)} onStartVisual={() => startVisualProduction(activeCollection.id)} onExit={() => { setActiveCollectionId(""); setCampaignDetailId(""); }} />}

        <section className="filter-panel glass-panel" aria-label="筛选商品">
          <form className="filter-bar" onSubmit={applyFilters}>
            <label className="search-field"><span aria-hidden="true">⌕</span><input value={draftFilters.q} onChange={(event) => setDraftFilters({ ...draftFilters, q: event.target.value })} placeholder="搜索商品、货号或 1688 ID" aria-label="搜索商品" /></label>
            <select value={draftFilters.supplier} onChange={(event) => setDraftFilters({ ...draftFilters, supplier: event.target.value })} aria-label="供应商">
              <option value="">全部供应商</option>{catalog?.facets.suppliers.map((item) => <option key={item.value} value={item.value}>{item.label} · {item.count}</option>)}
            </select>
            <select value={draftFilters.category} onChange={(event) => setDraftFilters({ ...draftFilters, category: event.target.value })} aria-label="分类">
              <option value="">全部分类</option>{catalog?.facets.categories.map((item) => <option key={item.value} value={item.value}>{item.label} · {item.count}</option>)}
            </select>
            <div className="price-range" aria-label="价格区间"><span>¥</span><input inputMode="decimal" value={draftFilters.minPrice} onChange={(event) => setDraftFilters({ ...draftFilters, minPrice: event.target.value })} placeholder="最低" aria-label="最低价格" /><i>—</i><input inputMode="decimal" value={draftFilters.maxPrice} onChange={(event) => setDraftFilters({ ...draftFilters, maxPrice: event.target.value })} placeholder="最高" aria-label="最高价格" /></div>
            <button className="button primary" type="submit">筛选</button>
            <button className="button ghost" type="button" onClick={() => { setDraftFilters(EMPTY_FILTERS); setFilters(EMPTY_FILTERS); }}>清空</button>
          </form>

          <div className="subfilters">
            <div className="chip-group">
              <select aria-label="库存状态" value={draftFilters.stockStatus} onChange={(event) => setDraftFilters({ ...draftFilters, stockStatus: event.target.value })}><option value="">全部库存</option><option value="in_stock">有库存</option><option value="out_of_stock">零库存</option><option value="unknown">库存未知</option></select>
              <select aria-label="图片质量" value={draftFilters.imageQuality} onChange={(event) => setDraftFilters({ ...draftFilters, imageQuality: event.target.value })}><option value="upload_ready">图片合格</option><option value="ready">图片优秀</option><option value="usable">高清可用</option><option value="needs_hd">待补高清</option><option value="all">全部图片</option></select>
              <label className={`check-chip ${draftFilters.hasRisk ? "active" : ""}`}><input type="checkbox" checked={draftFilters.hasRisk} onChange={(event) => setDraftFilters({ ...draftFilters, hasRisk: event.target.checked })} />有问题</label>
              {session.role === "reviewer" && <select aria-label="我的判断" value={draftFilters.myVote} onChange={(event) => setDraftFilters({ ...draftFilters, myVote: event.target.value })}><option value="">我的判断</option><option value="selected">我选的</option><option value="pending">我再看看的</option><option value="rejected">我不选的</option></select>}
              <select aria-label="最终决定" value={draftFilters.finalStatus} onChange={(event) => setDraftFilters({ ...draftFilters, finalStatus: event.target.value })}><option value="">最终决定</option><option value="unreviewed">{finalLabels.unreviewed}</option><option value="selected">{finalLabels.selected}</option><option value="pending">{finalLabels.pending}</option><option value="rejected">{finalLabels.rejected}</option></select>
              {session.role === "admin" && <select aria-label="文案状态" value={draftFilters.copyStatus} onChange={(event) => setDraftFilters({ ...draftFilters, copyStatus: event.target.value })}><option value="">全部文案</option><option value="draft_pending">草稿待确认</option><option value="approved">已确认</option><option value="needs_edit">需修改</option></select>}
            </div>
            <div className="list-controls">
              <span>{loading ? "读取中…" : `${compactNumber(catalog?.pagination.total ?? 0)} 款`}</span>
              <select aria-label="排序" value={draftFilters.sort} onChange={(event) => { const sort = event.target.value; setDraftFilters({ ...draftFilters, sort }); setFilters({ ...filters, sort, page: 1 }); }}><option value="recommendation">综合推荐</option><option value="image_quality_desc">图片质量优先</option><option value="price_asc">价格升序</option><option value="price_desc">价格降序</option><option value="stock_desc">库存降序</option><option value="supplier">供应商</option><option value="newest">最近导入</option></select>
              <div className="view-toggle"><button className={view === "grid" ? "active" : ""} onClick={() => changeView("grid")} aria-label="画廊视图" aria-pressed={view === "grid"} type="button">▦</button><button className={view === "table" ? "active" : ""} onClick={() => changeView("table")} aria-label="表格视图" aria-pressed={view === "table"} type="button">☷</button></div>
            </div>
          </div>
        </section>

        {error && <div className="state-panel"><strong>暂时无法读取商品</strong><p>{error}</p><button className="button soft" onClick={() => setRefreshKey((key) => key + 1)}>重试</button></div>}
        {!error && catalog && (view === "grid" ? (
          <section className={`product-grid ${loading ? "loading" : ""}`}>
            {catalog.items.map((product) => <ProductCard key={product.id} product={product} campaignRole={activeRoles.get(product.id)} campaignIsCandidate={activeCollectionIsCandidate} canVote={session.role === "reviewer"} onOpen={setDetailId} onVote={quickVote} />)}
          </section>
        ) : (
          <ProductTable products={catalog.items} campaignRoles={activeRoles} campaignIsCandidate={activeCollectionIsCandidate} canVote={session.role === "reviewer"} onOpen={setDetailId} onVote={quickVote} />
        ))}
        {!error && catalog?.items.length === 0 && <div className="state-panel"><strong>没有匹配的商品</strong><p>试试清空筛选。</p></div>}

        {catalog && <Pagination pagination={catalog.pagination} onPage={(page) => { setFilters({ ...filters, page }); window.scrollTo({ top: 0, behavior: "smooth" }); }} />}
        </> : <VisualWorkbench
          requestedCollectionId={visualCollectionId}
          marketing={marketing}
          onCollectionChange={persistVisualCollection}
          notify={setToast}
          generationQueue={generationQueue}
          onQueueChanged={refreshGenerationQueue}
          jump={visualJump}
          onJumpHandled={() => setVisualJump(null)}
          canExportListing={session.role === "admin"}
          cancelledJobIds={cancelledJobIds}
          onCancelJob={askCancelJob}
        />}
      </main>
      {queueOpen && <GenerationQueueDrawer data={generationQueue} unavailable={generationQueueUnavailable} onClose={closeQueue} onOpenEntry={openQueueEntry} onCancelEntry={(entry) => askCancelJob({ jobId: entry.jobId, productLabel: entry.productLabel, kindLabel: entry.kindLabel || queueEntryKindText(entry) })} />}
      {cancelTarget && <CancelJobDialog target={cancelTarget} busy={cancellingJob} onConfirm={() => void confirmCancelJob()} onClose={closeCancelDialog} />}
      {workspaceMode === "catalog" && detailCollection && <CampaignDetailDrawer collection={detailCollection} onClose={() => setCampaignDetailId("")} onStartVisual={() => startVisualProduction(detailCollection.id)} onOpenProduct={(id) => { setCampaignDetailId(""); setDetailId(id); }} />}
      {workspaceMode === "catalog" && detailId && <ProductDrawer productId={detailId} session={session} onClose={() => setDetailId(null)} onChanged={() => setRefreshKey((key) => key + 1)} notify={setToast} />}
      {toast && <div className="toast" role="status">{toast}</div>}
    </div>
  );
}

function writeVisualStorage(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage can be full or disabled; the in-memory state keeps working.
  }
}

function readStoredString(key: string): string {
  try {
    return localStorage.getItem(key) ?? "";
  } catch {
    return "";
  }
}

interface VisualThemeState {
  phase: VisualPhase;
  /**
   * The chosen variants (colours), each product's together in the order they
   * were chosen: a product's first one is its 主色. Saves from before several
   * colours hold one per product, which reads the same way.
   */
  selectedProductIds: string[];
  /**
   * Per product, the colours last chosen (主色 first), kept while the product
   * is not ticked so ticking it again brings them back. Older saves hold a
   * single id per product.
   */
  selectedSkuByCatalogProduct: Record<string, string[]>;
  selectedPinIds: string[];
  updatedAt: string;
}

function readVisualThemeStates(): Record<string, VisualThemeState> {
  try {
    const value = JSON.parse(localStorage.getItem(VISUAL_STATE_KEY) ?? "{}") as unknown;
    if (!value || typeof value !== "object" || Array.isArray(value)) return {};
    const strings = (list: unknown): string[] => Array.isArray(list) ? list.filter((item): item is string => typeof item === "string") : [];
    // One id (saved before several colours) or a list of them.
    const variantList = (choice: unknown): string[] => [...new Set(typeof choice === "string" ? [choice] : strings(choice))];
    return Object.fromEntries(Object.entries(value).flatMap(([collectionId, entry]): Array<[string, VisualThemeState]> => {
      if (!entry || typeof entry !== "object" || Array.isArray(entry)) return [];
      const candidate = entry as Record<string, unknown>;
      const choices = candidate.selectedSkuByCatalogProduct;
      return [[collectionId, {
        phase: VISUAL_PHASES.includes(candidate.phase as VisualPhase) ? candidate.phase as VisualPhase : "truth",
        selectedProductIds: strings(candidate.selectedProductIds),
        selectedSkuByCatalogProduct: choices && typeof choices === "object" && !Array.isArray(choices)
          ? Object.fromEntries(Object.entries(choices).flatMap(([productId, choice]): Array<[string, string[]]> => {
            const list = variantList(choice);
            return list.length > 0 ? [[productId, list]] : [];
          }))
          : {},
        selectedPinIds: strings(candidate.selectedPinIds),
        updatedAt: typeof candidate.updatedAt === "string" ? candidate.updatedAt : "",
      }]];
    }));
  } catch {
    return {};
  }
}

function writeVisualThemeState(collectionId: string, state: Omit<VisualThemeState, "updatedAt">) {
  try {
    const states = readVisualThemeStates();
    states[collectionId] = { ...state, updatedAt: new Date().toISOString() };
    localStorage.setItem(VISUAL_STATE_KEY, JSON.stringify(states));
  } catch {
    // Storage can be full or disabled; the in-memory state keeps working.
  }
}

function savedVisualPhase(collectionId: string): VisualPhase {
  return collectionId ? readVisualThemeStates()[collectionId]?.phase ?? "truth" : "truth";
}

/** The step a theme opens at: a link's step for this theme wins over the theme's saved step. */
function openingVisualPhase(link: VisualLink | null, collectionId: string): VisualPhase {
  return visualLinkFor(link, collectionId)?.phase ?? savedVisualPhase(collectionId);
}

/**
 * A theme's saved state with a link's step put in (the saved choices stay);
 * restoring then applies the usual rules, e.g. step 2 or 3 without products
 * opens step 1. No step = the saved state as it is.
 */
function withLinkedPhase(saved: VisualThemeState | undefined, phase: VisualPhase | null): VisualThemeState | undefined {
  if (!phase) return saved;
  return { ...(saved ?? { selectedProductIds: [], selectedSkuByCatalogProduct: {}, selectedPinIds: [], updatedAt: "" }), phase };
}

interface VisualSelectionGroup {
  productId: string;
  /** Variants that can be used, in display order. */
  skuIds: string[];
  defaultSkuId: string | null;
}

/**
 * The chosen colours per product, in the order the products were chosen; a
 * product's first colour is its 主色. Ids of no known product are dropped.
 */
function chosenVariantsByProduct(
  selectedProductIds: readonly string[],
  productOf: ReadonlyMap<string, string>,
): Array<{ catalogProductId: string; skuIds: string[] }> {
  const byProduct = new Map<string, string[]>();
  for (const skuId of selectedProductIds) {
    const catalogProductId = productOf.get(skuId);
    if (!catalogProductId) continue;
    const skuIds = byProduct.get(catalogProductId) ?? [];
    if (!skuIds.includes(skuId)) skuIds.push(skuId);
    byProduct.set(catalogProductId, skuIds);
  }
  return [...byProduct].map(([catalogProductId, skuIds]) => ({ catalogProductId, skuIds }));
}

/**
 * The choice list with one product's colours set, in the product's place (or
 * last when it was not chosen); no colours = the product is no longer chosen.
 * Each product's colours stay together.
 */
function withProductVariants(selectedProductIds: readonly string[], groupSkuIds: readonly string[], variants: readonly string[]): string[] {
  const inGroup = new Set(groupSkuIds);
  const first = selectedProductIds.findIndex((id) => inGroup.has(id));
  const rest = selectedProductIds.filter((id) => !inGroup.has(id));
  const chosen = [...new Set(variants)].filter((id) => inGroup.has(id));
  if (first === -1) return [...rest, ...chosen];
  const before = selectedProductIds.slice(0, first).filter((id) => !inGroup.has(id)).length;
  return [...rest.slice(0, before), ...chosen, ...rest.slice(before)];
}

/**
 * A choice list made safe: only usable colours, each product's together in the
 * order they came, at most 8 colours per product and 15 products.
 */
function limitVariantSelection(selectedProductIds: readonly string[], groups: readonly Pick<VisualSelectionGroup, "productId" | "skuIds">[]): string[] {
  const productOf = new Map(groups.flatMap((group) => group.skuIds.map((skuId) => [skuId, group.productId] as const)));
  return chosenVariantsByProduct(selectedProductIds, productOf)
    .slice(0, MAX_SCENE_PRODUCTS)
    .flatMap((product) => product.skuIds.slice(0, MAX_VARIANTS_PER_PRODUCT));
}

/**
 * Applies a theme's saved state to the products and board that exist now:
 * vanished products, colours and pins are dropped, and a step that needs
 * products falls back to step 1 when none are left. Saves from before several
 * colours (one per product) come back as that product's 主色.
 */
function restoreVisualThemeState(
  saved: VisualThemeState | undefined,
  groups: readonly VisualSelectionGroup[],
  boardPinIds: ReadonlySet<string> | null,
): Omit<VisualThemeState, "updatedAt"> {
  const selectedProductIds = limitVariantSelection(saved?.selectedProductIds ?? [], groups);
  const selectedSkuByCatalogProduct: Record<string, string[]> = {};
  for (const group of groups) {
    const selected = selectedProductIds.filter((skuId) => group.skuIds.includes(skuId));
    const savedChoice = [...new Set(saved?.selectedSkuByCatalogProduct[group.productId] ?? [])]
      .filter((skuId) => group.skuIds.includes(skuId))
      .slice(0, MAX_VARIANTS_PER_PRODUCT);
    const choice = selected.length > 0 ? selected : savedChoice.length > 0 ? savedChoice : group.defaultSkuId ? [group.defaultSkuId] : [];
    if (choice.length > 0) selectedSkuByCatalogProduct[group.productId] = choice;
  }
  const selectedPinIds = [...new Set(saved?.selectedPinIds ?? [])]
    .filter((pinId) => !boardPinIds || boardPinIds.has(pinId))
    .slice(0, MAX_REFERENCES_PER_RUN);
  let phase = saved?.phase ?? "truth";
  if ((phase === "product-images" || phase === "references") && selectedProductIds.length === 0) phase = "truth";
  return { phase, selectedProductIds, selectedSkuByCatalogProduct, selectedPinIds };
}

// Stock 0 comes from the 9-14 货盘 snapshot and may be out of date, so a
// colour it shows sold out is only tagged: the owner still decides whether
// to make its images and put it on sale. (The note names the source on
// purpose, as the owner asked.)
const PALLET_SOLD_OUT_TEXT = "货盘显示没货";
const PALLET_STOCK_NOTE = "9 月 14 日的货盘数据，实际库存可能已经变了";

/** Stock 0 (or less) in the 货盘; an unknown stock is not. */
function palletSoldOut(stock: number | null | undefined): boolean {
  return typeof stock === "number" && Number.isFinite(stock) && stock <= 0;
}

const productImageSlotDefinitions: Array<{ jobType: VisualProductImageJobType; label: string }> = [
  { jobType: "truth_white", label: "白底图" },
  { jobType: "truth_detail", label: "细节图" },
  { jobType: "truth_neutral", label: "模特图" },
  { jobType: "truth_combo", label: "组合图" },
];

/** One product image to make: a single variant's 白底图/细节图/静物图, or one product's 组合图. */
interface ProductImageSlot {
  key: string;
  jobType: VisualProductImageJobType;
  label: string;
  /** The colour's name on a 白底图 (each chosen colour gets one); null on the others. */
  variantLabel: string | null;
  catalogProductId: string;
  /** null for a 组合图, which covers several variants. */
  sourceSkuId: string | null;
  productIds: string[];
}

interface ProductImageCard {
  /** Visual product id of the 主色 (the first colour chosen). */
  productId: string;
  catalogProductId: string;
  label: string;
  /** The 主色 with its stock. */
  variantLabel: string;
  imageUrl: string | null;
  /**
   * 白底图 / 细节图 / 静物图 of the 主色, then the 组合图, in slot order; null
   * where the slot does not apply (组合图 of a single-variant product).
   */
  slots: Array<ProductImageSlot | null>;
  /** A 白底图 for each other chosen colour, in the order chosen. */
  variantSlots: ProductImageSlot[];
}

function productImageSlotKey(jobType: string, catalogProductId: string, sourceSkuId: string | null): string {
  return jobType === "truth_combo" ? `${jobType}|${catalogProductId}` : `${jobType}|${catalogProductId}|${sourceSkuId ?? ""}`;
}

/** The slot a snapshot job or output belongs to, or null for scene and other jobs. */
function productImageJobSlotKey(job: Pick<VisualGenerationJob, "jobType" | "catalogProductId" | "sourceSkuId">): string | null {
  if (!job.jobType || !productImageSlotDefinitions.some((slot) => slot.jobType === job.jobType) || !job.catalogProductId) return null;
  if (job.jobType !== "truth_combo" && !job.sourceSkuId) return null;
  return productImageSlotKey(job.jobType, job.catalogProductId, job.sourceSkuId ?? null);
}

/**
 * Slots that need no new request: a job is queued or running, or a finished
 * job has an image that was not marked 不用.
 */
function satisfiedProductImageSlots(snapshot: Pick<VisualGenerationSnapshot, "jobs" | "outputs"> | null): Set<string> {
  const satisfied = new Set<string>();
  if (!snapshot) return satisfied;
  const outputByJobId = new Map(snapshot.outputs.map((output) => [output.jobId, output]));
  for (const job of snapshot.jobs) {
    const key = productImageJobSlotKey(job);
    if (!key) continue;
    const output = outputByJobId.get(job.jobId);
    if (isActiveJob(job) || (job.status === "completed" && output && output.reviewDecision !== "reject")) satisfied.add(key);
  }
  return satisfied;
}

/**
 * The colours a new 组合图 shows, in catalog order: every usable colour in
 * stock plus every chosen one (a colour the 货盘 shows sold out too — the
 * owner's call). At most 12; chosen colours never give way.
 */
function comboVariantIds(
  group: Pick<VisualCatalogProduct, "skus">,
  productById: ReadonlyMap<string, VisualProduct>,
  chosenSkuIds: readonly string[],
): string[] {
  const chosen = new Set(chosenSkuIds);
  const candidates = group.skus.filter((sku) => {
    const variant = productById.get(sku.id);
    if (!sku.sourceSkuId || !variant || !visualProductReady(variant)) return false;
    return chosen.has(sku.id) || (typeof sku.stock === "number" && sku.stock > 0);
  });
  const kept = new Set([
    ...candidates.filter((sku) => chosen.has(sku.id)),
    ...candidates.filter((sku) => !chosen.has(sku.id)),
  ].slice(0, MAX_COMBO_SKUS).map((sku) => sku.id));
  return candidates.filter((sku) => kept.has(sku.id)).map((sku) => sku.id);
}

/**
 * Cards for the chosen colours, one per catalog product, never mixing
 * products: every chosen colour gets its 白底图 (one the 货盘 shows sold out
 * too); 细节图 and 静物图 are made for the 主色 only; the 组合图 shows the
 * colours in stock plus the chosen ones, when that is two or more.
 */
function productImageCardsFor(
  selectedProductIds: readonly string[],
  productById: ReadonlyMap<string, VisualProduct>,
  groups: readonly VisualCatalogProduct[],
): ProductImageCard[] {
  const groupBySkuId = new Map(groups.flatMap((group) => group.skus.map((sku) => [sku.id, group] as const)));
  const chosen = new Map<string, Array<{ productId: string; sourceSkuId: string }>>();
  for (const productId of selectedProductIds) {
    const product = productById.get(productId);
    const group = groupBySkuId.get(productId);
    const sku = group?.skus.find((candidate) => candidate.id === productId);
    const sourceSkuId = product?.sourceSkuId ?? sku?.sourceSkuId ?? null;
    if (!product || !group || !sourceSkuId) continue;
    const variants = chosen.get(group.productId) ?? [];
    // One 白底图 per supplier variant, however it was chosen.
    if (variants.length < MAX_VARIANTS_PER_PRODUCT && !variants.some((variant) => variant.sourceSkuId === sourceSkuId)) variants.push({ productId, sourceSkuId });
    chosen.set(group.productId, variants);
  }
  return [...chosen].flatMap(([catalogProductId, variants]): ProductImageCard[] => {
    const group = groups.find((candidate) => candidate.productId === catalogProductId);
    const [main, ...others] = variants;
    if (!group || !main) return [];
    const comboIds = comboVariantIds(group, productById, variants.map((variant) => variant.productId));
    const whiteSlot = (variant: { productId: string; sourceSkuId: string }): ProductImageSlot => ({
      key: productImageSlotKey("truth_white", catalogProductId, variant.sourceSkuId),
      jobType: "truth_white",
      label: "白底图",
      variantLabel: visualVariantName(group, variant.productId),
      catalogProductId,
      sourceSkuId: variant.sourceSkuId,
      productIds: [variant.productId],
    });
    const slots = productImageSlotDefinitions.map(({ jobType, label }): ProductImageSlot | null => {
      if (jobType === "truth_white") return whiteSlot(main);
      if (jobType === "truth_combo") {
        if (comboIds.length < 2) return null;
        return { key: productImageSlotKey(jobType, catalogProductId, null), jobType, label, variantLabel: null, catalogProductId, sourceSkuId: null, productIds: comboIds };
      }
      return { key: productImageSlotKey(jobType, catalogProductId, main.sourceSkuId), jobType, label, variantLabel: null, catalogProductId, sourceSkuId: main.sourceSkuId, productIds: [main.productId] };
    });
    return [{
      productId: main.productId,
      catalogProductId,
      label: group.label,
      variantLabel: visualSkuLabel(group, main.productId),
      imageUrl: group.sourceImage?.url ?? productById.get(main.productId)?.imageUrl ?? null,
      slots,
      variantSlots: others.map(whiteSlot),
    }];
  });
}

/** Every image the cards ask for, in the order they are sent: each card's own slots, then its other colours' 白底图. */
function productImageSlotsFor(cards: readonly Pick<ProductImageCard, "slots" | "variantSlots">[]): ProductImageSlot[] {
  return cards.flatMap((card) => [
    ...card.slots.filter((slot): slot is ProductImageSlot => Boolean(slot)),
    ...card.variantSlots,
  ]);
}

/** Exactly the keys the generator accepts for a product-image job. */
function productImageJobBody(slot: ProductImageSlot, projectId: string, queueItemId: string) {
  return slot.jobType === "truth_combo"
    ? { queueItemId, projectId, jobType: slot.jobType, catalogProductId: slot.catalogProductId, productIds: slot.productIds }
    : { queueItemId, projectId, jobType: slot.jobType, catalogProductId: slot.catalogProductId, sourceSkuId: slot.sourceSkuId, productIds: slot.productIds };
}

/**
 * Step 2's plan: the product images the chosen products still lack — none
 * made (or every one marked 不用), none on the way, none being sent — in the
 * order they would be sent. Nothing is sent until the owner taps
 * 生成这些商品图; cancelled jobs count as never asked for.
 */
function productImagePlan(
  slots: readonly ProductImageSlot[],
  snapshot: Pick<VisualGenerationSnapshot, "jobs" | "outputs">,
  requests: Readonly<Record<string, ProductImageRequestState>>,
  skipped: ReadonlySet<string> = new Set(),
): ProductImageSlot[] {
  const satisfied = satisfiedProductImageSlots(snapshot);
  // 不做这张: the owner chose not to make it, so it is never planned.
  return slots.filter((slot) => !satisfied.has(slot.key) && !skipped.has(slot.key) && requests[slot.key] !== "waiting");
}

/** The plan by kind: 白底图 3 · 细节图 1 · 静物图 1 · 组合图 1. */
function productImagePlanText(slots: readonly Pick<ProductImageSlot, "jobType">[]): string {
  return productImageSlotDefinitions
    .map(({ jobType, label }) => ({ label, count: slots.filter((slot) => slot.jobType === jobType).length }))
    .filter(({ count }) => count > 0)
    .map(({ label, count }) => `${label} ${count}`)
    .join(" · ");
}

// ---------------------------------------------------------------------------
// v16 · A product image that did not work out: the real reason, then what the
// owner can do — 重做, 上传照片 (a clear photo of that colour, used first as
// its look, then the image is made again) or 不做这张 (stored on the Mac, so
// every device sees it; 恢复 undoes it).
// ---------------------------------------------------------------------------

type SlotFailureKind = "source_missing" | "identity_drift" | "other";

const slotFailureTexts: Record<SlotFailureKind, string> = {
  source_missing: "没有这款的清楚单独照片",
  identity_drift: "做出来和实物有出入，已拦下",
  other: "没做成",
};

/** What helps, in a few words: a photo when none is clear; another try when it drifted. */
const slotFailureHints: Record<SlotFailureKind, string> = {
  source_missing: "重做多半还是不行，上传一张这款的清楚照片",
  identity_drift: "可以再重做一次试试",
  other: "",
};

/** Under an image the generator made by enlarging the real photo (原图放大). */
const SOURCE_UPSCALE_NOTE = "原图放大：没有重画，原图上的文字和标注也会在";

/**
 * 找同款: the product's name (shortened) and colour, to search 淘宝 / 1688 /
 * 亚马逊 in a new tab. A clear photo found there goes back in with 上传照片.
 */
function sameStyleSearchText(productLabel: string | null | undefined, variantLabel: string | null): string {
  const [title = "", variant = ""] = (productLabel ?? "").split(" · ");
  return [title.trim().slice(0, 24), (variantLabel ?? variant).trim()].filter(Boolean).join(" ");
}

function sameStyleSearchLinks(text: string): Array<{ site: string; url: string }> {
  const query = encodeURIComponent(text);
  return [
    { site: "淘宝", url: `https://s.taobao.com/search?q=${query}` },
    { site: "1688", url: `https://s.1688.com/selloffer/offer_search.htm?charset=utf8&keywords=${query}` },
    { site: "亚马逊", url: `https://www.amazon.com/s?k=${query}` },
  ];
}

function SameStyleLinks({ text, testId }: { text: string; testId: string }) {
  if (!text) return null;
  return <span className="same-style" data-testid={testId} title="找到一模一样的款，存下清楚的图，再点上传照片">
    <small>找同款</small>
    {sameStyleSearchLinks(text).map((link) => <a key={link.site} href={link.url} target="_blank" rel="noopener noreferrer">{link.site}</a>)}
  </span>;
}

/** The generator's reason for a failed job; plain 没做成 when it does not say. */
function slotFailureKind(job: Pick<VisualGenerationJob, "failureKind"> | null | undefined): SlotFailureKind {
  const kind = job?.failureKind;
  return kind === "source_missing" || kind === "identity_drift" ? kind : "other";
}

/** The job type stored with a 不做这张 (older spellings read too). */
const slotSkipKinds: Readonly<Record<string, VisualProductImageJobType>> = {
  truth_white: "truth_white",
  white: "truth_white",
  truth_detail: "truth_detail",
  detail: "truth_detail",
  truth_neutral: "truth_neutral",
  neutral: "truth_neutral",
  still: "truth_neutral",
  truth_combo: "truth_combo",
  combo: "truth_combo",
};

/** Exactly the keys POST /api/slot-skips takes for one slot. */
function slotSkipBody(slot: Pick<ProductImageSlot, "jobType" | "catalogProductId" | "sourceSkuId">, projectId: string, skipped: boolean): SlotSkipRequest {
  return {
    projectId,
    catalogProductId: slot.catalogProductId,
    sourceSkuId: slot.jobType === "truth_combo" ? null : slot.sourceSkuId,
    kind: slot.jobType,
    skipped,
  };
}

/** A list in an answer: the answer itself, or its `key` (or `items`) list; null when there is none. */
function answerList(value: unknown, key: string): unknown[] | null {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== "object") return null;
  const record = value as Record<string, unknown>;
  const list = record[key] ?? record.items;
  return Array.isArray(list) ? list : null;
}

/**
 * The slots of one product marked 不做 in a slot-skips answer, as slot keys;
 * entries of other themes or products, un-skipped ones and unknown kinds are
 * left out. null when the answer holds no list (then nothing is known).
 */
function skippedSlotKeys(value: unknown, projectId: string, catalogProductId: string): string[] | null {
  const list = answerList(value, "skips");
  if (!list) return null;
  const keys = new Set<string>();
  for (const entry of list) {
    if (!entry || typeof entry !== "object") continue;
    const item = entry as Record<string, unknown>;
    if (typeof item.projectId === "string" && item.projectId !== projectId) continue;
    if (typeof item.catalogProductId === "string" && item.catalogProductId !== catalogProductId) continue;
    if (item.skipped === false) continue;
    const kind = typeof item.kind === "string" && Object.prototype.hasOwnProperty.call(slotSkipKinds, item.kind) ? slotSkipKinds[item.kind] : null;
    if (!kind) continue;
    const sourceSkuId = typeof item.sourceSkuId === "string" && item.sourceSkuId ? item.sourceSkuId : null;
    if (kind !== "truth_combo" && !sourceSkuId) continue;
    keys.add(productImageSlotKey(kind, catalogProductId, sourceSkuId));
  }
  return [...keys];
}

/** 不做这张 / 恢复 that did not go through, in plain words. */
function slotSkipFailureText(error: unknown): string {
  if (isSlowNetworkError(error)) return "网络有点慢，请稍后刷新看看";
  if (error instanceof ApiError && (error.status === 401 || error.status === 403)) return "请刷新页面后重试";
  return "没能保存，请稍后再试";
}

/** The largest photo the Mac takes (after base64 is read back). */
const OWNER_PHOTO_MAX_BYTES = 12 * 1024 * 1024;
/** A photo over the slow link can take a while; the site gives up after 120 s and says so. */
const OWNER_PHOTO_TIMEOUT_MS = 150_000;

type OwnerPhotoFormat = "jpeg" | "png" | "webp" | "heic";

/** The picture format from the file's first bytes (its name and type can be wrong). */
function ownerPhotoFormat(bytes: Uint8Array): OwnerPhotoFormat | null {
  const text = (start: number, end: number) => String.fromCharCode(...bytes.subarray(start, end));
  if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return "jpeg";
  if (bytes.length >= 8 && [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a].every((byte, index) => bytes[index] === byte)) return "png";
  if (bytes.length >= 12 && text(0, 4) === "RIFF" && text(8, 12) === "WEBP") return "webp";
  if (bytes.length >= 12 && text(4, 8) === "ftyp" && /^(?:heic|heix|hevc|hevx|heim|heis|mif1|msf1)$/.test(text(8, 12))) return "heic";
  return null;
}

/** Why a picked photo cannot be sent, before anything is read; "" when its size is fine. */
function ownerPhotoSizeProblem(size: number): string {
  if (!(size > 0)) return "这张照片是空的，请换一张";
  if (size > OWNER_PHOTO_MAX_BYTES) return "照片太大了，请换一张 12 MB 以内的";
  return "";
}

/** Why a read photo cannot be sent; "" for a JPG, PNG or WebP. */
function ownerPhotoFormatProblem(format: OwnerPhotoFormat | null): string {
  if (format === "heic") return "这张是 HEIC 格式，请先存成 JPG 再上传";
  if (!format) return "这张照片打不开，请换一张 JPG 或 PNG 照片";
  return "";
}

/** The file's bytes as base64, nothing converted; read in pieces of a multiple of 3 bytes so they join cleanly. */
function bytesToBase64(bytes: Uint8Array): string {
  const piece = 3 * 8192;
  let text = "";
  for (let start = 0; start < bytes.length; start += piece) text += btoa(String.fromCharCode(...bytes.subarray(start, start + piece)));
  return text;
}

/** The file's own name without folders or control characters (the Mac checks it again). */
function ownerPhotoFilename(name: string): string {
  const base = name.split(/[\\/]/).pop() ?? "";
  const clean = base.normalize("NFC").replace(/[\u0000-\u001f\u007f-\u009f]/g, "").trim();
  return clean.slice(-120) || "photo.jpg";
}

/** The colour a slot's photo shows: its own colour, or the whole product for a 组合图. */
function ownerPhotoSkuId(slot: Pick<ProductImageSlot, "jobType" | "sourceSkuId">): string | null {
  return slot.jobType === "truth_combo" ? null : slot.sourceSkuId;
}

/** Exactly the keys POST /api/owner-photos takes. */
function ownerPhotoUploadBody(slot: Pick<ProductImageSlot, "jobType" | "catalogProductId" | "sourceSkuId">, projectId: string, filename: string, dataBase64: string): OwnerPhotoUploadRequest {
  return { projectId, catalogProductId: slot.catalogProductId, sourceSkuId: ownerPhotoSkuId(slot), filename: ownerPhotoFilename(filename), dataBase64 };
}

/** An owner photo's own address on the site; nothing else is ever shown. */
function ownerPhotoUrl(value: unknown): string | null {
  if (typeof value !== "string" || !value.startsWith("/api/owner-photo-files/")) return null;
  if (/\.\.|\/\/|[\s"'<>\\]/.test(value.slice(1))) return null;
  return value;
}

/**
 * 删除 of one photo: only this product's copy for this colour (an empty
 * sourceSkuId names the whole-product one); the same photo given for another
 * product or colour stays.
 */
function ownerPhotoDeletePath(photo: Pick<OwnerPhoto, "id" | "catalogProductId" | "sourceSkuId">): string {
  return `/api/owner-photos/${encodeURIComponent(photo.id)}?catalogProductId=${encodeURIComponent(photo.catalogProductId)}&sourceSkuId=${encodeURIComponent(photo.sourceSkuId ?? "")}`;
}

/** One owner photo of this product from an answer, or null. */
function ownerPhotoEntry(value: unknown, catalogProductId: string): OwnerPhoto | null {
  if (!value || typeof value !== "object") return null;
  const item = value as Record<string, unknown>;
  const id = typeof item.id === "string" ? item.id : typeof item.sha256 === "string" ? item.sha256 : "";
  const url = ownerPhotoUrl(item.url);
  if (!/^[A-Za-z0-9_-]{8,128}$/.test(id) || !url) return null;
  if (typeof item.catalogProductId === "string" && item.catalogProductId !== catalogProductId) return null;
  return {
    id,
    catalogProductId,
    sourceSkuId: typeof item.sourceSkuId === "string" && item.sourceSkuId ? item.sourceSkuId : null,
    createdAt: typeof item.createdAt === "string" ? item.createdAt : null,
    note: typeof item.note === "string" && item.note ? item.note : null,
    url,
    previewUrl: ownerPhotoUrl(item.previewUrl),
  };
}

/** A product's owner photos from an answer, newest first, each once; null when the answer holds no list. */
function ownerPhotoList(value: unknown, catalogProductId: string): OwnerPhoto[] | null {
  const list = answerList(value, "photos");
  if (!list) return null;
  const byId = new Map<string, OwnerPhoto>();
  for (const entry of list) {
    const photo = ownerPhotoEntry(entry, catalogProductId);
    if (photo && !byId.has(photo.id)) byId.set(photo.id, photo);
  }
  return [...byId.values()].sort((left, right) => (right.createdAt ?? "").localeCompare(left.createdAt ?? ""));
}

/** The photo list after an upload: the answer's list when it has one, else the stored photo put first. */
function photosAfterUpload(current: readonly OwnerPhoto[], answer: unknown, catalogProductId: string): OwnerPhoto[] | null {
  const listed = ownerPhotoList(answer, catalogProductId);
  if (listed) return listed;
  const record = answer && typeof answer === "object" ? answer as Record<string, unknown> : null;
  const photo = ownerPhotoEntry(record?.photo ?? answer, catalogProductId);
  return photo ? [photo, ...current.filter((item) => item.id !== photo.id)] : null;
}

/** An upload that did not go through, in plain words (the site's own plain message when it has one). */
function ownerPhotoFailureText(error: unknown): string {
  if (isSlowNetworkError(error)) return "网络有点慢，照片可能还在路上，稍后刷新看看";
  if (error instanceof ApiError) {
    if (error.status === 413) return "照片太大了，请换一张 12 MB 以内的";
    if (error.status === 401 || error.status === 403) return "请刷新页面后重试";
    if (error.status >= 400 && error.status < 500) return ownerPhotoServerText(error.message) || "这张照片没能收下，请换一张试试";
  }
  return "照片没传上，请稍后再试";
}

/** The site's own plain message about a photo (format names like JPG or HEIC are plain words here), else "". */
function ownerPhotoServerText(message: string | null | undefined): string {
  const text = (message ?? "").trim();
  return plainServerText(text.replace(/\b(?:jpe?g|png|heic|heif|webp|mb)\b/gi, "图")) ? text : "";
}

/** A photo on its way: being read, going out (how much), or sent and being saved. */
interface OwnerPhotoUploadState {
  stage: "reading" | "sending" | "saving";
  percent: number;
}

function ownerPhotoUploadState(sent: number, total: number): OwnerPhotoUploadState {
  if (!(total > 0) || sent >= total) return { stage: "saving", percent: 100 };
  return { stage: "sending", percent: Math.max(0, Math.min(99, Math.floor((sent / total) * 100))) };
}

function ownerPhotoUploadText(state: OwnerPhotoUploadState): string {
  if (state.stage === "reading") return "读取照片…";
  if (state.stage === "saving") return "传好了，正在保存…";
  return `上传中 ${state.percent}%`;
}

/**
 * A product-image request can still be crossing the wire, or the generator can
 * refuse it before a job exists. Keep the site's safe error code and wording:
 * a refused submission is not an image that "没做成".
 */
interface ProductImageRequestRejection {
  state: "rejected";
  code: string;
  message: string;
}

type ProductImageRequestState = "waiting" | ProductImageRequestRejection;

function productImageRequestRejected(request: ProductImageRequestState | undefined): request is ProductImageRequestRejection {
  return typeof request === "object" && request?.state === "rejected";
}

function productImageRequestRejection(error: ApiError): ProductImageRequestRejection {
  return {
    state: "rejected",
    code: error.code,
    message: error.message.trim() || generationErrorMessage(error),
  };
}

/** A slot marked 不做这张 says 不做 while it has no image and nothing on the way. */
function slotShowsSkipped(view: { state: string }, skipped: boolean): boolean {
  return skipped && (view.state === "failed" || view.state === "idle");
}

type ProductImageSlotView =
  | { state: "loading" | "waiting" | "idle" }
  /** The generator refused the submission before a job existed. */
  | { state: "rejected"; error: ProductImageRequestRejection }
  /** Its newest recorded try failed. */
  | { state: "failed"; job: VisualGenerationJob }
  | { state: "queued" | "running"; job: VisualGenerationJob }
  | {
    state: "done";
    /** The version shown first: the newest one not marked 不用. */
    output: VisualGenerationOutput;
    /** Every finished image of the slot, oldest first, so older versions stay reachable. */
    versions: VisualGenerationOutput[];
    /** A newer version still being made, or one that failed. */
    next: VisualGenerationJob | null;
  };

/**
 * What a slot shows. While a version not marked 不用 exists it stays on show,
 * with a newer job (a 修改 redo) as a small note; otherwise the newest job
 * decides. A pending or refused request wins. `jobs` is null until the
 * generator's records have arrived (just after a refresh): the slot then
 * waits instead of offering 生成 for an image that may already exist.
 */
function productImageSlotView(
  key: string,
  jobs: readonly VisualGenerationJob[] | null,
  outputByJobId: ReadonlyMap<string, VisualGenerationOutput>,
  request: ProductImageRequestState | undefined,
): ProductImageSlotView {
  if (request === "waiting") return { state: "waiting" };
  if (productImageRequestRejected(request)) return { state: "rejected", error: request };
  if (!jobs) return { state: "loading" };
  const slotJobs = jobs
    .filter((job) => productImageJobSlotKey(job) === key)
    .sort((left, right) => left.createdAt.localeCompare(right.createdAt));
  const latest = slotJobs[slotJobs.length - 1];
  if (!latest) return { state: "idle" };
  const versions = slotJobs.flatMap((job) => {
    const output = job.status === "completed" ? outputByJobId.get(job.jobId) : undefined;
    return output ? [output] : [];
  });
  const kept = versions.filter((output) => output.reviewDecision !== "reject");
  const shown = kept[kept.length - 1];
  if (!shown) {
    if (latest.status === "queued" || latest.status === "running") return { state: latest.status, job: latest };
    if (latest.status === "failed") return { state: "failed", job: latest };
    const output = outputByJobId.get(latest.jobId);
    return output ? { state: "done", output, versions, next: null } : { state: "idle" };
  }
  const newer = slotJobs.slice(slotJobs.findIndex((job) => job.jobId === shown.jobId) + 1);
  return { state: "done", output: shown, versions, next: newer.length > 0 && latest.status !== "completed" ? latest : null };
}

/** Small note on an image whose newer version is on the way (with its 第 N 位), or did not work out. */
function revisionStatusLabel(job: Pick<VisualGenerationJob, "status">, position?: number | null): string {
  if (job.status === "queued") return position ? `新版排队中 · 第 ${position} 位` : "新版排队中";
  if (job.status === "running") return "新版生成中";
  if (job.status === "failed") return "新版没做成";
  return "";
}

/**
 * The newest 修改 redo of an image while it is on the way, or when it failed;
 * null once the newest one is done, so an older failed try never sticks.
 */
function pendingRevisionJob(
  jobs: readonly VisualGenerationJob[],
  outputId: string,
  sentRevisions: Readonly<Record<string, string>>,
): VisualGenerationJob | null {
  const newest = jobs
    .filter((job) => job.revisionOf === outputId || sentRevisions[job.queueItemId] === outputId)
    .reduce<VisualGenerationJob | null>((latest, job) => !latest || job.createdAt > latest.createdAt ? job : latest, null);
  return newest && newest.status !== "completed" ? newest : null;
}

/**
 * The owner's 修改 note as plain one-line text. The site is open to anyone, so
 * the note is treated as untrusted: control and invisible characters go, the
 * corner quotes the generator puts around it are swapped for plain quotes, and
 * it is cut to the generator's limit.
 */
function cleanRevisionNote(value: string): string {
  const text = value
    .normalize("NFC")
    .replace(/[\u0000-\u001f\u007f-\u009f\u00ad\u061c\u180e\u200b-\u200f\u2028-\u202e\u2060-\u206f\ufeff\ufff9-\ufffb]/g, " ")
    .replace(/[「『]/g, "“")
    .replace(/[」』]/g, "”")
    .replace(/\s+/g, " ")
    .trim();
  let capped = text.slice(0, MAX_REVISION_NOTE_LENGTH);
  // Never end on half of an emoji.
  if (/[\ud800-\udbff]$/.test(capped)) capped = capped.slice(0, -1);
  return capped.trimEnd();
}

/**
 * Exactly the keys the generator accepts to redo an image with the owner's
 * note: a new queue item with the parent job's identity, plus revisionOf and
 * revisionNote. null when the image cannot be redone (its identity is gone).
 */
function revisionJobBody(
  parent: Pick<VisualGenerationOutput, "outputId" | "projectId" | "jobType" | "referenceId" | "productIds" | "catalogProductId" | "sourceSkuId">,
  job: Pick<VisualGenerationJob, "projectId" | "jobType" | "referenceId" | "productIds" | "catalogProductId" | "sourceSkuId"> | undefined,
  queueItemId: string,
  note: string,
) {
  const projectId = parent.projectId || job?.projectId || "";
  const jobType = parent.jobType ?? job?.jobType ?? "scene";
  const productIds = [...(parent.productIds?.length ? parent.productIds : job?.productIds ?? [])];
  if (!parent.outputId || !projectId || productIds.length === 0) return null;
  if (jobType === "scene") {
    const referenceId = parent.referenceId ?? job?.referenceId ?? null;
    if (!referenceId) return null;
    return { queueItemId, projectId, referenceId, productIds, revisionOf: parent.outputId, revisionNote: note };
  }
  const catalogProductId = parent.catalogProductId ?? job?.catalogProductId ?? null;
  if (!catalogProductId) return null;
  if (jobType === "truth_combo") {
    return { queueItemId, projectId, jobType, catalogProductId, productIds, revisionOf: parent.outputId, revisionNote: note };
  }
  const sourceSkuId = parent.sourceSkuId ?? job?.sourceSkuId ?? null;
  if (!sourceSkuId) return null;
  return { queueItemId, projectId, jobType, catalogProductId, sourceSkuId, productIds, revisionOf: parent.outputId, revisionNote: note };
}

/**
 * 成图 newest first, each image directly followed by the versions redone from
 * it (oldest redo first, each with its own redos after it), so a new version
 * sits right after its parent and the parent stays.
 */
function outputsWithVersions(outputs: readonly VisualGenerationOutput[]): VisualGenerationOutput[] {
  const byId = new Map(outputs.map((output) => [output.outputId, output]));
  const children = new Map<string, VisualGenerationOutput[]>();
  const roots: VisualGenerationOutput[] = [];
  for (const output of outputs) {
    const parentId = output.parentOutputId;
    if (parentId && parentId !== output.outputId && byId.has(parentId)) children.set(parentId, [...(children.get(parentId) ?? []), output]);
    else roots.push(output);
  }
  const ordered: VisualGenerationOutput[] = [];
  const seen = new Set<string>();
  const visit = (output: VisualGenerationOutput) => {
    if (seen.has(output.outputId)) return;
    seen.add(output.outputId);
    ordered.push(output);
    const redos = [...(children.get(output.outputId) ?? [])].sort((left, right) => left.createdAt.localeCompare(right.createdAt));
    for (const redo of redos) visit(redo);
  };
  for (const root of [...roots].sort((left, right) => right.createdAt.localeCompare(left.createdAt))) visit(root);
  // Images caught in a broken parent loop still show.
  for (const output of outputs) visit(output);
  return ordered;
}

/**
 * 成图 as one card per image: the first version and every version redone from
 * it, oldest first (第 1 版 …). Cards keep outputsWithVersions' order (newest
 * image first).
 */
function outputVersionGroups(outputs: readonly VisualGenerationOutput[]): Array<{ rootId: string; versions: VisualGenerationOutput[] }> {
  const byId = new Map(outputs.map((output) => [output.outputId, output]));
  const rootOf = (output: VisualGenerationOutput): string => {
    let current = output;
    const seen = new Set<string>();
    while (current.parentOutputId && byId.has(current.parentOutputId) && !seen.has(current.outputId)) {
      seen.add(current.outputId);
      current = byId.get(current.parentOutputId)!;
    }
    return current.outputId;
  };
  const groups = new Map<string, VisualGenerationOutput[]>();
  for (const output of outputsWithVersions(outputs)) {
    const rootId = rootOf(output);
    groups.set(rootId, [...(groups.get(rootId) ?? []), output]);
  }
  return [...groups].map(([rootId, versions]) => ({
    rootId,
    versions: [...versions].sort((left, right) => left.createdAt.localeCompare(right.createdAt)),
  }));
}

/** The version a card shows: the one picked with 第 N 版, else the newest not marked 不用, else the newest. */
function shownVersion(versions: readonly VisualGenerationOutput[], picked: string | undefined): VisualGenerationOutput {
  return versions.find((version) => version.outputId === picked)
    ?? [...versions].reverse().find((version) => version.reviewDecision !== "reject")
    ?? versions[versions.length - 1];
}

function readVisualReviews(): Record<string, VisualReviewStatus> {
  try {
    const value = JSON.parse(localStorage.getItem(VISUAL_REVIEW_KEY) ?? "{}") as Record<string, unknown>;
    const valid = new Set<VisualReviewStatus>(["undecided", "keep", "revise", "reject"]);
    return Object.fromEntries(Object.entries(value).filter((entry): entry is [string, VisualReviewStatus] => valid.has(entry[1] as VisualReviewStatus)));
  } catch {
    return {};
  }
}

function readVisualFeedback(): Record<string, VisualGeneratedFeedback> {
  try {
    const value = JSON.parse(localStorage.getItem(VISUAL_FEEDBACK_KEY) ?? "{}") as unknown;
    if (!value || typeof value !== "object" || Array.isArray(value)) return {};
    const valid = new Set<VisualReviewStatus>(["undecided", "keep", "revise", "reject"]);
    return Object.fromEntries(Object.entries(value).flatMap(([id, entry]) => {
      if (!entry || typeof entry !== "object" || Array.isArray(entry)) return [];
      const candidate = entry as Partial<VisualGeneratedFeedback>;
      if (!valid.has(candidate.status as VisualReviewStatus) || typeof candidate.note !== "string") return [];
      return [[id, { status: candidate.status as VisualReviewStatus, note: candidate.note }]];
    }));
  } catch {
    return {};
  }
}

function readVisualQueue(): VisualQueueItem[] {
  try {
    const value = JSON.parse(localStorage.getItem(VISUAL_QUEUE_KEY) ?? "[]") as unknown;
    if (!Array.isArray(value)) return [];
    return value.flatMap((entry): VisualQueueItem[] => {
      if (!entry || typeof entry !== "object") return [];
      const item = entry as Partial<Record<keyof VisualQueueItem, unknown>>;
      if (typeof item.id !== "string" || typeof item.referenceId !== "string") return [];
      if (!Array.isArray(item.productIds) || !item.productIds.every((productId) => typeof productId === "string")) return [];
      return [{
        id: item.id,
        projectId: typeof item.projectId === "string" ? item.projectId : undefined,
        referenceId: item.referenceId,
        pinId: typeof item.pinId === "string" ? item.pinId : undefined,
        referenceImageUrl: typeof item.referenceImageUrl === "string" ? item.referenceImageUrl : undefined,
        productIds: item.productIds as string[],
        createdAt: typeof item.createdAt === "string" ? item.createdAt : "",
      }];
    });
  } catch {
    return [];
  }
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === "AbortError";
}

function projectCollectionId(project: VisualProject): string {
  return project.collectionId ?? collectionIdForLegacyProject(project.id) ?? project.id;
}

function mergeVisualProject(dynamicProject: VisualProject | null, manifest: VisualWorkbenchManifest | null, collectionId: string): VisualProject | null {
  const legacyId = legacyProjectIdForCollection(collectionId);
  const legacy = manifest?.projects.find((candidate) => candidate.id === legacyId || candidate.collectionId === collectionId) ?? null;
  if (!dynamicProject) return legacy ? { ...legacy, collectionId } : null;
  if (!legacy) return dynamicProject;
  // The collection id stays the runtime key. A legacy manifest project only
  // contributes its earlier finished images (and its products as a fallback).
  return {
    ...dynamicProject,
    collectionId,
    products: dynamicProject.products.length > 0 ? dynamicProject.products : legacy.products,
    creativeCandidates: legacy.creativeCandidates.length > 0 ? legacy.creativeCandidates : dynamicProject.creativeCandidates,
  };
}

function visualProductReady(product: VisualProduct): boolean {
  return Boolean(product.sourceSkuId)
    && product.status !== "source_missing"
    && product.status !== "source_sku_missing";
}

function visualCatalogGroups(project: VisualProject): VisualCatalogProduct[] {
  if (project.catalogProducts?.length) return project.catalogProducts;
  const groups = new Map<string, VisualCatalogProduct>();
  for (const product of project.products) {
    const group = groups.get(product.productId) ?? {
      id: product.productId,
      productId: product.productId,
      label: product.label,
      role: product.role,
      sourceImage: product.sourceImage,
      status: product.status,
      skus: [],
    };
    group.skus.push({ id: product.id, sourceSkuId: product.sourceSkuId });
    groups.set(product.productId, group);
  }
  return [...groups.values()];
}

function visualSkuLabel(group: VisualCatalogProduct, skuId: string): string {
  const sku = group.skus.find((candidate) => candidate.id === skuId);
  if (!sku) return "缺规格";
  const variant = [sku.style, sku.color, sku.size, sku.material].filter(Boolean).join(" · ");
  if (!sku.sourceSkuId) return [variant, "缺规格"].filter(Boolean).join(" · ");
  const stock = typeof sku.stock === "number" ? `库存 ${sku.stock}` : "";
  return [variant || "默认规格", stock].filter(Boolean).join(" · ");
}

/** A colour's short name (紫色, 大号 · 粉色 …), without its stock. */
function visualVariantName(group: Pick<VisualCatalogProduct, "skus">, skuId: string): string {
  const sku = group.skus.find((candidate) => candidate.id === skuId);
  if (!sku) return "缺规格";
  return [sku.style, sku.color, sku.size, sku.material].filter(Boolean).join(" · ") || "默认规格";
}

/**
 * 全部颜色: the usable colours in stock (every usable one not known to be sold
 * out when no stock is known), in catalog order, at most 8.
 */
function allColourVariantIds(group: VisualCatalogProduct, productById: ReadonlyMap<string, VisualProduct>): string[] {
  const usable = new Set(eligibleVisualSkuIds(group, productById));
  const skus = group.skus.filter((sku) => usable.has(sku.id));
  const inStock = skus.filter((sku) => typeof sku.stock === "number" && sku.stock > 0);
  const pool = inStock.length > 0 ? inStock : skus.filter((sku) => sku.stock !== 0);
  return pool.slice(0, MAX_VARIANTS_PER_PRODUCT).map((sku) => sku.id);
}

/**
 * 全部颜色 applied to a product's chosen colours (主色 first): every colour in
 * stock is added after them, at most 8 in all. When that is too many, other
 * chosen colours that are not in stock give way first; the 主色 never does.
 */
function withAllColours(chosen: readonly string[], inStock: readonly string[]): string[] {
  const merged = [...new Set([...chosen, ...inStock])];
  const overflow = merged.length - MAX_VARIANTS_PER_PRODUCT;
  if (overflow <= 0) return merged;
  const giveWay = new Set(merged.filter((id, index) => index > 0 && !inStock.includes(id)).slice(-overflow));
  return merged.filter((id) => !giveWay.has(id)).slice(0, MAX_VARIANTS_PER_PRODUCT);
}

/**
 * One scene per chosen product × chosen reference. Colours take turns: for
 * each product, reference i (in the order chosen) goes with its colour
 * i mod n, so several references show several colours. Reference by
 * reference, products in the order chosen.
 */
function sceneVariantPlan<R>(
  products: readonly { catalogProductId: string; skuIds: readonly string[] }[],
  references: readonly R[],
): Array<{ reference: R; referenceIndex: number; catalogProductId: string; productId: string }> {
  return references.flatMap((reference, referenceIndex) => products.flatMap((product) => product.skuIds.length > 0
    ? [{ reference, referenceIndex, catalogProductId: product.catalogProductId, productId: product.skuIds[referenceIndex % product.skuIds.length] }]
    : []));
}

/** 参考 1 → 紫色, 参考 2 → 蓝色 …; with one colour, a single line for all references. */
function referencePairingLines(variantNames: readonly string[], referenceCount: number): string[] {
  if (variantNames.length === 0 || referenceCount <= 0) return [];
  if (variantNames.length === 1) return [referenceCount === 1 ? `参考 1 → ${variantNames[0]}` : `参考 1–${referenceCount} → ${variantNames[0]}`];
  return Array.from({ length: referenceCount }, (_, index) => `参考 ${index + 1} → ${variantNames[index % variantNames.length]}`);
}

/** Variants that have a product photo and a supplier variant id. */
function eligibleVisualSkuIds(group: VisualCatalogProduct, productById: ReadonlyMap<string, VisualProduct>): string[] {
  return group.skus.filter((sku) => {
    const product = productById.get(sku.id);
    return Boolean(sku.sourceSkuId) && Boolean(product && visualProductReady(product));
  }).map((sku) => sku.id);
}

/** The variant picked before anyone chooses: the first in stock, else the first usable. */
function defaultVisualSkuId(group: VisualCatalogProduct, productById: ReadonlyMap<string, VisualProduct>): string | null {
  const usable = new Set(eligibleVisualSkuIds(group, productById));
  const executable = group.skus.filter((sku) => usable.has(sku.id));
  return (executable.find((sku) => typeof sku.stock === "number" && sku.stock > 0) ?? executable[0])?.id ?? null;
}

function fallbackVisualProject(collection: MarketingCollectionSummary, products: ProductSummary[] = []): VisualProject {
  return {
    id: collection.id,
    collectionId: collection.id,
    title: collection.title,
    subtitle: collection.subtitle,
    products: products.map((product) => ({
      id: product.id,
      productId: product.id,
      sourceSkuId: product.sourceCatalogId ?? product.offerId,
      label: product.name,
      role: collection.members.find((member) => member.productId === product.id)?.role ?? "choice",
      imageUrl: product.thumbnailUrl,
      status: product.thumbnailUrl ? "source_audit_required" : "source_missing",
      detailCount: 0,
    })),
    creativeCandidates: [],
  };
}

/**
 * A request given up after its time limit, here or between the site and the
 * generator: the generator may well have taken the work and keeps going.
 */
function isSlowNetworkError(error: unknown): boolean {
  // Any gateway timeout counts, also one whose page is not the site's own answer.
  if (error instanceof ApiError) return error.code === "GENERATION_TIMEOUT" || error.status === 504;
  return typeof error === "object" && error !== null && (error as { name?: unknown }).name === "TimeoutError";
}

function generationErrorMessage(error: unknown): string {
  if (isSlowNetworkError(error)) return GENERATION_SLOW_MESSAGE;
  if (error instanceof ApiError) {
    if (error.code === "REFERENCE_NOT_ALLOWLISTED") return "这张参考图已更新，请换一张";
    if (error.code === "IDEMPOTENCY_CONFLICT") return "这组正在生成";
    if (error.code === "PRODUCT_NOT_ALLOWLISTED" || error.code === "PROJECT_NOT_ALLOWLISTED" || error.code === "CATALOG_SKU_NOT_ALLOWLISTED") return "有商品暂时不能生成，请重新选择";
    if (error.code === "OUTPUT_NOT_FOUND" || error.code === "REVISION_PARENT_NOT_FOUND") return "这张图不存在了，请刷新";
    if (error.code === "REVISION_PARENT_INVALID" || error.code === "INVALID_REVISION_NOTE") return "修改意见没能提交，请刷新后重试";
    if (error.status === 401 || error.status === 403) return "请刷新页面后重试";
    if (error.status >= 400 && error.status < 500) return "提交内容有误，请刷新后重试";
  }
  return "生成服务暂时不可用，请稍后再试";
}

function isActiveJob(job: VisualGenerationJob | undefined): boolean {
  return job?.status === "queued" || job?.status === "running";
}

// One request to the generator for this theme's jobs and images; a request
// stuck on the slow link is dropped so the next one can start.
function fetchGenerationSnapshot(projectId: string): Promise<VisualGenerationSnapshot> {
  return api<VisualGenerationSnapshot>(
    `/api/generation-snapshot?projectId=${encodeURIComponent(projectId)}`,
    { signal: AbortSignal.timeout(GENERATION_POLL_TIMEOUT_MS) },
  );
}

function fetchGenerationQueue(): Promise<GenerationQueueData> {
  return api<GenerationQueueData>("/api/generation-queue", { timeoutMs: GENERATION_POLL_TIMEOUT_MS });
}

/**
 * Runs `load` at most once at a time: a call while it is loading shares that
 * load. With `again`, one more load follows it, so news asked for during a
 * load is not missed; polls never stack up on the slow link.
 */
function singleFlight(load: () => Promise<void>): (options?: { again?: boolean }) => Promise<void> {
  let running: Promise<void> | null = null;
  let again = false;
  const run = (options: { again?: boolean } = {}): Promise<void> => {
    if (running) {
      if (options.again) again = true;
      return running;
    }
    running = (async () => {
      try {
        await load();
      } finally {
        running = null;
        if (again) {
          again = false;
          void run();
        }
      }
    })();
    return running;
  };
  return run;
}

/** The queue as the page uses it: lists always present, in global order, 1–4 channels (2 when unsaid). */
function normalizeGenerationQueue(value: Partial<GenerationQueueData> | null | undefined): GenerationQueueData {
  const entries = (list: unknown): QueueEntry[] => Array.isArray(list)
    ? list.filter((entry): entry is QueueEntry => Boolean(entry) && typeof entry === "object" && typeof (entry as QueueEntry).jobId === "string")
    : [];
  const byPosition = (left: QueueEntry, right: QueueEntry) => (left.position || 0) - (right.position || 0);
  const concurrency = Math.round(Number(value?.concurrency));
  const averageSeconds = Number(value?.averageSeconds);
  return {
    concurrency: Number.isFinite(concurrency) ? Math.min(4, Math.max(1, concurrency)) : 2,
    running: entries(value?.running).sort(byPosition),
    queued: entries(value?.queued).sort(byPosition),
    recent: entries(value?.recent),
    averageSeconds: Number.isFinite(averageSeconds) && averageSeconds > 0 ? averageSeconds : null,
    updatedAt: typeof value?.updatedAt === "string" ? value.updatedAt : "",
  };
}

/**
 * Seconds until each queued job should start. Each channel frees up when its
 * running job reaches the usual time (at least a minute away), and every job
 * then holds the first free channel for the usual time. Empty while the
 * generator has no usual time yet.
 */
function queueStartEstimates(
  data: Pick<GenerationQueueData, "concurrency" | "running" | "queued" | "averageSeconds">,
  now: number,
): Map<string, number> {
  const estimates = new Map<string, number>();
  const average = data.averageSeconds;
  if (typeof average !== "number" || !Number.isFinite(average) || average <= 0) return estimates;
  const channels = Math.min(4, Math.max(1, Math.round(data.concurrency) || 1));
  const freeAt = data.running.slice(0, channels).map((entry) => {
    const started = Date.parse(entry.startedAt ?? entry.createdAt ?? "");
    const elapsed = Number.isFinite(started) ? Math.max(0, (now - started) / 1000) : 0;
    return Math.max(60, average - elapsed);
  });
  while (freeAt.length < channels) freeAt.push(0);
  for (const entry of data.queued) {
    freeAt.sort((left, right) => left - right);
    estimates.set(entry.jobId, freeAt[0]);
    freeAt[0] += average;
  }
  return estimates;
}

/** 生成中 · 已用 x 分钟 | 第 N 位 · 约 y 分钟后开始 | 已完成 | 没做成 */
function queueEntryStateText(
  entry: Pick<QueueEntry, "jobId" | "position" | "status" | "startedAt" | "createdAt">,
  estimates: ReadonlyMap<string, number>,
  now: number,
): string {
  if (entry.status === "running") {
    const started = Date.parse(entry.startedAt ?? entry.createdAt ?? "");
    const minutes = Number.isFinite(started) ? Math.max(1, Math.floor((now - started) / 60_000)) : 1;
    return `生成中 · 已用 ${minutes} 分钟`;
  }
  if (entry.status === "queued") {
    const wait = estimates.get(entry.jobId);
    if (wait === undefined) return `第 ${entry.position} 位`;
    return wait < 60 ? `第 ${entry.position} 位 · 马上开始` : `第 ${entry.position} 位 · 约 ${Math.ceil(wait / 60)} 分钟后开始`;
  }
  return entry.status === "completed" ? "已完成" : "没做成";
}

const queueKindNames: Record<string, string> = {
  scene: "场景图",
  truth_white: "白底图",
  truth_detail: "细节图",
  truth_neutral: "模特图",
  truth_combo: "组合图",
  truth_cutout: "透明图",
};

/** 白底图 / 场景图 …, plus 第 N 版 for an image redone with a note. */
function queueEntryKindText(entry: Pick<QueueEntry, "jobType" | "kindLabel" | "revision">): string {
  const kind = entry.kindLabel || queueKindNames[entry.jobType || "scene"] || "场景图";
  return typeof entry.revision === "number" && entry.revision > 1 ? `${kind} · 第 ${entry.revision} 版` : kind;
}

/** Product images live on step 2, scenes on step 4. */
function queueEntryJump(entry: Pick<QueueEntry, "projectId" | "jobType" | "catalogProductId" | "sourceSkuId">): VisualJump {
  const productImage = Boolean(entry.jobType) && entry.jobType !== "scene";
  return {
    collectionId: collectionIdForLegacyProject(entry.projectId) ?? entry.projectId,
    phase: productImage ? "product-images" : "creative",
    catalogProductId: productImage ? entry.catalogProductId ?? null : null,
    sourceSkuId: productImage ? entry.sourceSkuId ?? null : null,
  };
}

/**
 * A job the owner cancelled: the generator marks it failed with stage
 * "cancelled"; one cancelled from this page counts at once, before the
 * generator's records say so. Its image counts as never asked for (缺).
 */
function isCancelledJob(job: Pick<VisualGenerationJob, "jobId" | "stage" | "cancelledAt" | "failureKind">, cancelledJobIds?: ReadonlySet<string>): boolean {
  return job.stage === "cancelled" || Boolean(job.cancelledAt) || job.failureKind === "cancelled" || Boolean(cancelledJobIds?.has(job.jobId));
}

/** The jobs that still count; cancelled ones are left out, as if never asked for. */
function withoutCancelledJobs<T extends Pick<VisualGenerationJob, "jobId" | "stage" | "cancelledAt" | "failureKind">>(jobs: readonly T[], cancelledJobIds?: ReadonlySet<string>): T[] {
  return jobs.filter((job) => !isCancelledJob(job, cancelledJobIds));
}

/**
 * 全部任务 without cancelled jobs: one cancelled here leaves the lists at once
 * (a poll already on its way may still list it), and cancelled ones never
 * show under 最近.
 */
function hideCancelledQueueEntries(data: GenerationQueueData | null, cancelledJobIds: ReadonlySet<string>): GenerationQueueData | null {
  if (!data) return null;
  const shown = (entry: QueueEntry) => !cancelledJobIds.has(entry.jobId) && entry.cancelled !== true && entry.stage !== "cancelled";
  const running = data.running.filter(shown);
  const queued = data.queued.filter(shown);
  const recent = data.recent.filter(shown);
  if (running.length === data.running.length && queued.length === data.queued.length && recent.length === data.recent.length) return data;
  return { ...data, running, queued, recent };
}

/**
 * A queue entry's square preview: a finished image's own preview (a new
 * address, so the picture shown while it waited is never reused), else the
 * queue preview (thumbUrl: the reference or product photo while it waits);
 * null = the number alone.
 */
function queueEntryThumb(entry: Pick<QueueEntry, "status" | "thumbUrl" | "outputImageUrl">): string | null {
  if (entry.status === "completed" && entry.outputImageUrl) return outputPreviewUrl(entry.outputImageUrl);
  return typeof entry.thumbUrl === "string" && /^\/api\/\S+$/.test(entry.thumbUrl) ? entry.thumbUrl : null;
}

const CANCEL_JOB_TEXT = "排队中的会直接移出；正在生成的会停下来，已经花的时间不会退回。";

/** 取消「怪兽绳结 · 紫色 · 白底图」？ */
function cancelJobTitle(target: Pick<CancelJobTarget, "productLabel" | "kindLabel">): string {
  const name = [target.productLabel, target.kindLabel].map((part) => (part ?? "").trim()).filter(Boolean).join(" · ");
  return `取消「${name || "这个任务"}」？`;
}

interface CancelJobOutcome {
  /** The job no longer runs: hide it everywhere now. */
  cancelled: boolean;
  /** Close the dialog: also when there was nothing left to cancel. */
  close: boolean;
  message: string;
}

/**
 * What a failed 取消任务 means, in plain words. Already cancelled counts as
 * done; already finished or gone closes the dialog; a slow answer may still
 * have arrived (the queue is read again); anything else keeps the dialog for
 * another try.
 */
function cancelJobFailure(error: unknown): CancelJobOutcome {
  if (error instanceof ApiError) {
    if (error.code === "JOB_CANCELLED") return { cancelled: true, close: true, message: "已取消" };
    if (error.code === "JOB_NOT_ACTIVE") return { cancelled: false, close: true, message: "这个任务已经结束了" };
    if (error.code === "JOB_NOT_FOUND") return { cancelled: false, close: true, message: "任务不存在了，请刷新" };
  }
  if (isSlowNetworkError(error)) return { cancelled: false, close: true, message: "网络有点慢，请稍后刷新看看" };
  return { cancelled: false, close: false, message: generationErrorMessage(error) };
}

// A job cancelled here stays hidden at least this long, until every list on
// the page (the queue, the theme's records) has been read again; and at most
// this long while the queue still lists it (an answer older than the cancel
// can be at most a poll's time limit old).
const CANCELLED_HOLD_MS = 10_000;
const CANCELLED_MAX_HOLD_MS = 45_000;

/**
 * Jobs cancelled from this page (job id → when) still to hide: those
 * cancelled just now, and those the queue still lists as waiting or running
 * (its answer was older than the cancel). The rest are dropped: the
 * generator's own records say cancelled from then on, and a job asked for
 * again shows again.
 */
function pruneCancelledJobs(
  cancelled: ReadonlyMap<string, number>,
  data: Pick<GenerationQueueData, "running" | "queued">,
  now: number,
): ReadonlyMap<string, number> {
  if (cancelled.size === 0) return cancelled;
  const listed = new Set([...data.running, ...data.queued].map((entry) => entry.jobId));
  const kept = new Map([...cancelled].filter(([jobId, at]) => now - at < CANCELLED_HOLD_MS || (listed.has(jobId) && now - at < CANCELLED_MAX_HOLD_MS)));
  return kept.size === cancelled.size ? cancelled : kept;
}

/** 取消任务: one request with the usual one-minute limit. */
async function cancelGenerationJob(jobId: string): Promise<CancelJobOutcome> {
  try {
    await api<GenerationJobCancelData>("/api/generation-jobs/cancel", {
      method: "POST",
      ...jsonBody({ jobId }),
      timeoutMs: GENERATION_POST_TIMEOUT_MS,
    });
    return { cancelled: true, close: true, message: "已取消" };
  } catch (caught) {
    return cancelJobFailure(caught);
  }
}

/** The theme a queue entry belongs to (older themes keep their own id). */
function queueEntryCollectionId(entry: Pick<QueueEntry, "projectId">): string {
  return collectionIdForLegacyProject(entry.projectId) ?? entry.projectId;
}

/** Jobs queued or running per theme, from the one queue. */
function themeQueueCounts(data: Pick<GenerationQueueData, "running" | "queued"> | null): Map<string, number> {
  const counts = new Map<string, number>();
  for (const entry of data ? [...data.running, ...data.queued] : []) {
    const collectionId = queueEntryCollectionId(entry);
    counts.set(collectionId, (counts.get(collectionId) ?? 0) + 1);
  }
  return counts;
}

/** A theme in the 主题 list: 狗狗绳结拉扯场 · 3 张生成中 while it has work queued or running. */
function themeOptionLabel(title: string, activeCount: number): string {
  return activeCount > 0 ? `${title} · ${activeCount} 张生成中` : title;
}

/** 我入选的商品: the products marked 入选 on the 选品 page, listed above the themes. */
const PICKED_COLLECTION_ID = "my-picks";

function isPickedProject(project: VisualProject): boolean {
  return project.kind === "picks" || projectCollectionId(project) === PICKED_COLLECTION_ID;
}

/** 我入选的商品 · 14 款 (· 2 张生成中); 还没有 when nothing is marked 入选 yet. */
function pickedOptionLabel(project: Pick<VisualProject, "title" | "catalogProducts">, activeCount: number): string {
  const count = project.catalogProducts?.length ?? 0;
  return themeOptionLabel(count > 0 ? `${project.title} · ${count} 款` : `${project.title} · 还没有，去选品页标入选`, activeCount);
}

/**
 * The line under 主题: this theme's products with work queued or running,
 * in queue order — 生成中：怪兽绳结 2 · 绳结饼干 1. "" when none.
 */
function themeActivityText(data: Pick<GenerationQueueData, "running" | "queued"> | null, collectionId: string, maxNames = 4): string {
  const counts = new Map<string, number>();
  for (const entry of data ? [...data.running, ...data.queued] : []) {
    if (queueEntryCollectionId(entry) !== collectionId) continue;
    // 怪兽绳结 · 紫色 → 怪兽绳结; a scene of several products names each.
    const names = (entry.productLabel ?? "").split(" · ")[0].split("、").map((name) => name.trim()).filter(Boolean);
    for (const name of names.length > 0 ? names : ["商品"]) counts.set(name, (counts.get(name) ?? 0) + 1);
  }
  if (counts.size === 0) return "";
  const parts = [...counts].map(([name, count]) => `${name} ${count}`);
  const shown = parts.slice(0, maxNames);
  if (parts.length > maxNames) shown.push(`另 ${parts.length - maxNames} 款`);
  return `生成中：${shown.join(" · ")}`;
}

/**
 * The queue item id to send again: the one of an earlier request for exactly
 * the same thing whose answer never came, so a retry cannot make a second
 * job. null (= use a new id) once that job is finished or failed.
 */
function reusableQueueItemId(
  pending: PendingRequest | undefined,
  requestKey: string,
  jobByQueueId: ReadonlyMap<string, Pick<VisualGenerationJob, "status">>,
): string | null {
  if (!pending || pending.requestKey !== requestKey) return null;
  const status = jobByQueueId.get(pending.queueItemId)?.status;
  return status === undefined || status === "queued" || status === "running" ? pending.queueItemId : null;
}

/**
 * The choice that shows a product-image job of 全部任务 on step 2: its product
 * with the job's colour. A chosen product gets that colour added after its
 * others (its 主色 stays, so no 细节图 / 静物图 is asked for another colour);
 * a product not chosen is added with that colour as its 主色 (a 组合图 keeps
 * the chosen colours, or takes the usual one). null when the product is not in
 * the theme, or no more products fit.
 */
function selectionShowingJob(
  selectedProductIds: readonly string[],
  groups: readonly VisualSelectionGroup[],
  sourceSkuIdById: ReadonlyMap<string, string | null>,
  target: Pick<VisualJump, "catalogProductId" | "sourceSkuId">,
): string[] | null {
  const group = groups.find((candidate) => candidate.productId === target.catalogProductId);
  if (!group || group.skuIds.length === 0) return null;
  const chosen = selectedProductIds.filter((id) => group.skuIds.includes(id));
  const exact = target.sourceSkuId ? group.skuIds.find((id) => sourceSkuIdById.get(id) === target.sourceSkuId) : undefined;
  if (chosen.length > 0) {
    if (!exact || chosen.includes(exact) || chosen.length >= MAX_VARIANTS_PER_PRODUCT) return [...selectedProductIds];
    return withProductVariants(selectedProductIds, group.skuIds, [...chosen, exact]);
  }
  const productOf = new Map(groups.flatMap((candidate) => candidate.skuIds.map((skuId) => [skuId, candidate.productId] as const)));
  if (chosenVariantsByProduct(selectedProductIds, productOf).length >= MAX_SCENE_PRODUCTS) return null;
  return [...selectedProductIds, exact ?? group.defaultSkuId ?? group.skuIds[0]];
}

/** The same image again without the browser's copy of a failed load (`&r=<n>`). */
function retryImageUrl(src: string, attempt: number): string {
  if (attempt <= 0 || /^(?:data|blob):/.test(src)) return src;
  return `${src}${src.includes("?") ? "&" : "?"}r=${attempt}`;
}

interface ImageLoadState {
  /** Cache-busting counter of the current try; 0 = the plain address. */
  attempt: number;
  /** Failed tries since the last tap. */
  failures: number;
  status: "loading" | "loaded" | "waiting" | "failed";
}

/** After a failed load: wait and try again, up to IMAGE_MAX_RETRIES times; then wait for a tap. */
function imageLoadAfterError(state: ImageLoadState): ImageLoadState {
  const failures = state.failures + 1;
  return { ...state, failures, status: failures > IMAGE_MAX_RETRIES ? "failed" : "waiting" };
}

function queueStatusLabel(job: VisualGenerationJob | undefined, starting: boolean): string {
  if (starting) return "提交中";
  if (!job) return "未开始";
  if (job.status === "queued") return "排队中";
  if (job.status === "running") return (job.stage && generationStageLabels[job.stage]) || "生成中";
  if (job.status === "failed") return "生成失败，请重试";
  return "已完成";
}

function pinIdFromReferenceId(referenceId: string | null | undefined): string | null {
  return /^pin:pb-[0-9]{8}T[0-9]{6}Z-[0-9a-f]{8}:([0-9]{6,24})$/.exec(referenceId ?? "")?.[1] ?? null;
}

/**
 * 队列: every queued or running scene job of the theme on the generator (also
 * ones started before a refresh or on another device), plus local items that
 * have not started yet or failed. Oldest first. `jobs` is null until the
 * generator's records have arrived: finished local items cannot be told apart
 * yet, so only items being sent right now show. A cancelled job leaves the
 * queue, with its local item.
 */
function visualPendingQueue(
  jobs: readonly VisualGenerationJob[] | null,
  localItems: readonly VisualQueueItem[],
  startingIds: ReadonlySet<string>,
  cancelledJobIds: ReadonlySet<string> = new Set(),
): VisualQueueItem[] {
  if (!jobs) {
    return localItems.filter((item) => startingIds.has(item.id)).sort((left, right) => left.createdAt.localeCompare(right.createdAt));
  }
  const localById = new Map(localItems.map((item) => [item.id, item]));
  const jobByQueueId = new Map(jobs.map((job) => [job.queueItemId, job]));
  const cancelledQueueIds = new Set(jobs.filter((job) => isCancelledJob(job, cancelledJobIds)).map((job) => job.queueItemId));
  const active = jobs
    .filter((job) => (!job.jobType || job.jobType === "scene") && isActiveJob(job) && !cancelledQueueIds.has(job.queueItemId))
    .map((job): VisualQueueItem => localById.get(job.queueItemId) ?? {
      id: job.queueItemId,
      projectId: job.projectId,
      referenceId: job.referenceId ?? "",
      pinId: pinIdFromReferenceId(job.referenceId) ?? undefined,
      productIds: job.productIds,
      createdAt: job.createdAt,
    });
  const activeIds = new Set(active.map((item) => item.id));
  const unstarted = localItems.filter((item) => {
    if (activeIds.has(item.id) || (cancelledQueueIds.has(item.id) && !startingIds.has(item.id))) return false;
    const status = jobByQueueId.get(item.id)?.status;
    return status === undefined || status === "failed" || startingIds.has(item.id);
  });
  return [...active, ...unstarted].sort((left, right) => left.createdAt.localeCompare(right.createdAt));
}

// Generated outputs reach the public site through a slow link: grids load the
// small JPEG preview and only the enlarged view loads the full file.
function outputPreviewUrl(imageUrl: string): string {
  if (!imageUrl.startsWith("/api/generation-outputs/")) return imageUrl;
  return `${imageUrl}${imageUrl.includes("?") ? "&" : "?"}variant=preview`;
}

// Reserves each board thumbnail's box before it loads. Board previews are the
// 474 px wide Pinterest size, so the original ratio gives their height.
function referencePreviewSize(reference: Pick<PinterestBoardReference, "width" | "height" | "previewWidth" | "previewHeight">): { width: number; height: number } | null {
  const positive = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value) && value > 0;
  if (positive(reference.previewWidth) && positive(reference.previewHeight)) {
    return { width: Math.round(reference.previewWidth), height: Math.round(reference.previewHeight) };
  }
  if (!positive(reference.width) || !positive(reference.height)) return null;
  const width = Math.min(474, Math.round(reference.width));
  return { width, height: Math.max(1, Math.floor((width * reference.height) / reference.width)) };
}

// Board pins come over the slow link: the grid loads the small (≤320 px)
// thumbnail when the server has one, else the 474 px preview. A thumbnail that
// failed to load falls back to the preview.
function referenceGridSrc(reference: Pick<PinterestBoardReference, "pinId" | "thumbUrl" | "imageUrl">, failedThumbs?: ReadonlySet<string>): string {
  const thumb = reference.thumbUrl;
  if (typeof thumb !== "string" || !/^\/api\/\S+$/.test(thumb) || failedThumbs?.has(reference.pinId)) return reference.imageUrl;
  return thumb;
}

// 刷新 without new pins: pins whose thumbnail the site already keeps come
// first (they show at once), the ones still on their way after them, each
// group in a fresh order. Unknown marks count as still on their way.
function cachedFirstPinOrder(references: readonly Pick<PinterestBoardReference, "pinId" | "cached">[], random: (bound: number) => number = randomBelow): string[] {
  const cached = references.filter((reference) => reference.cached === true).map((reference) => reference.pinId);
  const arriving = references.filter((reference) => reference.cached !== true).map((reference) => reference.pinId);
  return [...shuffledPinIds(cached, random), ...shuffledPinIds(arriving, random)];
}

// 还有 N 张缩略图在路上: thumbnails the site is still fetching. One this page
// already showed has arrived; a server without marks shows no hint.
function referenceThumbsOnTheWay(references: readonly Pick<PinterestBoardReference, "pinId" | "cached">[], arrived: ReadonlySet<string>): number {
  return references.filter((reference) => reference.cached === false && !arrived.has(reference.pinId)).length;
}

// A later read of the same board only adds 缩略图 marks (they never go away
// while the board stays); another board, or no news, keeps the one shown.
function withCachedThumbs(current: PinterestBoardSnapshot | null, fresh: PinterestBoardSnapshot | null): PinterestBoardSnapshot | null {
  if (!current || !fresh || current.snapshotId !== fresh.snapshotId) return current;
  const cached = new Set(fresh.references.filter((reference) => reference.cached === true).map((reference) => reference.pinId));
  let changed = false;
  const references = current.references.map((reference) => {
    if (reference.cached === true || !cached.has(reference.pinId)) return reference;
    changed = true;
    return { ...reference, cached: true };
  });
  return changed ? { ...current, references } : current;
}

// Shows the already-loaded thumbnail behind the enlarged image until the full file arrives.
function previewPlaceholderStyle(placeholderUrl: string | undefined): CSSProperties | undefined {
  if (!placeholderUrl) return undefined;
  return {
    backgroundImage: `url(${JSON.stringify(placeholderUrl)})`,
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    backgroundSize: "contain",
  };
}

function productIdsKey(productIds: readonly string[]): string {
  return [...productIds].sort().join("|");
}

// crypto.randomUUID only exists in secure contexts; the shop is also opened over plain http.
function randomUuid(): string {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

// A whole number in [0, bound) from the secure random source. Draws past the
// last full multiple of bound are thrown away, so no number comes up more often.
function randomBelow(bound: number): number {
  const limit = Math.floor(2 ** 32 / bound) * bound;
  const draw = new Uint32Array(1);
  do {
    crypto.getRandomValues(draw);
  } while (draw[0] >= limit);
  return draw[0] % bound;
}

// Fisher–Yates: every order equally likely. `random(n)` gives a whole number
// in [0, n); tests pass their own.
function shuffledPinIds(pinIds: readonly string[], random: (bound: number) => number = randomBelow): string[] {
  const order = [...pinIds];
  for (let index = order.length - 1; index > 0; index -= 1) {
    const other = random(index + 1);
    [order[index], order[other]] = [order[other], order[index]];
  }
  return order;
}

// The board's references in its own order (rank 1 = newest pin), each pin once.
function boardReferenceList(snapshot: PinterestBoardSnapshot | null): PinterestBoardReference[] {
  const seen = new Set<string>();
  return [...(snapshot?.references ?? [])]
    .sort((left, right) => left.rank - right.rank)
    .filter((reference) => {
      if (seen.has(reference.pinId)) return false;
      seen.add(reference.pinId);
      return true;
    });
}

// Pins on the board now that the board shown before 刷新 did not have, newest first.
function newBoardPinIds(before: PinterestBoardSnapshot | null, after: PinterestBoardSnapshot | null): string[] {
  const shown = new Set((before?.references ?? []).map((reference) => reference.pinId));
  return boardReferenceList(after).map((reference) => reference.pinId).filter((pinId) => !shown.has(pinId));
}

// References in the order 刷新 shuffled them into. Pins the order does not
// know (they came later) go first, in board order; no order = board order.
function referencesInOrder<T extends { pinId: string }>(references: readonly T[], order: readonly string[] | null): T[] {
  if (!order) return [...references];
  const position = new Map(order.map((pinId, index) => [pinId, index]));
  const later = references.filter((reference) => !position.has(reference.pinId));
  const known = references
    .filter((reference) => position.has(reference.pinId))
    .sort((left, right) => (position.get(left.pinId) ?? 0) - (position.get(right.pinId) ?? 0));
  return [...later, ...known];
}

// A sync answer: a stale board still shows, but the sync counts as failed.
function referenceSyncResult(data: PinterestReferencesData): ReferenceSyncResult {
  const snapshot = data.snapshot ?? null;
  return { ok: !data.stale && snapshot !== null, snapshot };
}

// A dropped request or a stale server answer is retried here as one user
// action. The server independently retries each complete Board traversal.
async function retryReferenceSync(
  request: () => Promise<PinterestReferencesData>,
  options: {
    pause?: (milliseconds: number) => Promise<void>;
    onRetry?: (attempt: number) => void;
  } = {},
): Promise<ReferenceSyncResult> {
  const pause = options.pause ?? ((milliseconds: number) => new Promise<void>((resolve) => window.setTimeout(resolve, milliseconds)));
  let latest: ReferenceSyncResult = { ok: false, snapshot: null };
  for (let attempt = 0; attempt < REFERENCE_SYNC_RETRY_DELAYS_MS.length; attempt += 1) {
    const wait = REFERENCE_SYNC_RETRY_DELAYS_MS[attempt];
    if (wait > 0) {
      options.onRetry?.(attempt);
      await pause(wait);
    }
    try {
      const result = referenceSyncResult(await request());
      if (result.snapshot) latest = result;
      if (result.ok) return result;
      // The server already retried a complete traversal before explicitly
      // returning stale data. Do not hammer Pinterest again in this gesture.
      return latest;
    } catch {
      // A later attempt can rejoin the server's still-running single flight.
    }
  }
  return latest;
}

// 刷新: new pins keep the board's order (newest first) and get a 新 badge;
// no new pins shuffle the references already here, with cached thumbnails
// first. A failed sync leaves the screen untouched while recovery continues.
function referenceRefreshOutcome(before: PinterestBoardSnapshot | null, result: ReferenceSyncResult, random: (bound: number) => number = randomBelow): ReferenceRefreshOutcome {
  if (!result.ok) return { order: undefined, newPinIds: [], message: "网络波动，已保留当前结果" };
  const fresh = result.ok ? newBoardPinIds(before, result.snapshot) : [];
  if (fresh.length > 0) return { order: null, newPinIds: fresh, message: `有 ${fresh.length} 张新图` };
  return {
    order: cachedFirstPinOrder(boardReferenceList(result.snapshot ?? before), random),
    newPinIds: [],
    message: "Board 没有新图，已换一批顺序",
  };
}

function formatClock(value: Date): string {
  return `${String(value.getHours()).padStart(2, "0")}:${String(value.getMinutes()).padStart(2, "0")}`;
}

/**
 * An image coming over the slow link: a soft placeholder until it arrives. A
 * failed load is tried again after a pause with a fresh address, a few times;
 * then a tap tries again.
 */
function RetryImage({ src, alt = "" }: { src: string; alt?: string }) {
  const [state, setState] = useState<ImageLoadState>({ attempt: 0, failures: 0, status: "loading" });
  const [shownSrc, setShownSrc] = useState(src);
  if (shownSrc !== src) {
    setShownSrc(src);
    setState({ attempt: 0, failures: 0, status: "loading" });
  }
  useEffect(() => {
    if (state.status !== "waiting") return;
    const timer = window.setTimeout(() => setState((current) => current.status === "waiting"
      ? { ...current, attempt: current.attempt + 1, status: "loading" }
      : current), IMAGE_RETRY_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [state]);
  // Inside a button that opens the large view: this tap only tries again.
  function retryNow(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    setState((current) => ({ attempt: current.attempt + 1, failures: 0, status: "loading" }));
  }
  const url = retryImageUrl(src, state.attempt);
  return <span className={`retry-image ${state.status}`}>
    {(state.status === "loading" || state.status === "loaded") && <img
      key={url}
      src={url}
      alt={alt}
      loading="lazy"
      decoding="async"
      onLoad={() => setState((current) => ({ ...current, status: "loaded" }))}
      onError={() => setState((current) => imageLoadAfterError(current))}
    />}
    {state.status === "failed"
      ? <span className="image-wait failed" title="点一下重试" onClick={retryNow}>图片还在路上<small>点一下重试</small></span>
      : state.status !== "loaded" && <span className="image-wait" aria-hidden="true">加载中…</span>}
  </span>;
}

/**
 * 全部任务: product images and scenes of every theme in the generator's one
 * queue, numbered in the order they run, each with its small square preview
 * (the number as a badge on it) and what it is doing now. A tap opens the
 * job's theme at its step; a waiting or running job has 取消 (asks first).
 */
function GenerationQueueDrawer({ data, unavailable, onClose, onOpenEntry, onCancelEntry }: {
  data: GenerationQueueData | null;
  unavailable: boolean;
  onClose: () => void;
  onOpenEntry: (entry: QueueEntry) => void;
  /** 取消 on a queued or running job: opens the confirm dialog. */
  onCancelEntry: (entry: QueueEntry) => void;
}) {
  const now = Date.now();
  const active = data ? [...data.running, ...data.queued] : [];
  const estimates = data ? queueStartEstimates(data, now) : new Map<string, number>();
  const row = (entry: QueueEntry) => {
    const running = entry.status === "running";
    const numbered = running || entry.status === "queued";
    const stateText = queueEntryStateText(entry, estimates, now);
    const kindText = queueEntryKindText(entry);
    const thumb = queueEntryThumb(entry);
    return <li key={entry.jobId} className={`queue-entry-item${numbered ? " cancellable" : ""}`}>
      <button type="button" className={`queue-entry ${entry.status}`} data-testid={`queue-entry-${entry.jobId}`} onClick={() => onOpenEntry(entry)}>
        <span className={`queue-entry-tile${thumb ? " has-thumb" : ""}`}>
          {thumb
            ? <RetryImage src={thumb} />
            : !numbered && <span aria-hidden="true">🐾</span>}
          {numbered && <b>#{entry.position}</b>}
        </span>
        <span className="queue-entry-copy">
          <strong>{entry.productLabel || kindText}</strong>
          <small>{[entry.projectTitle, kindText].filter(Boolean).join(" · ")}</small>
          {running
            ? <span className="generation-activity"><i aria-hidden="true" />{stateText}</span>
            : <span className="queue-entry-state">{stateText}</span>}
        </span>
        {!numbered && <span className="queue-entry-go" aria-hidden="true">›</span>}
      </button>
      {numbered && <button type="button" className="queue-entry-cancel" data-testid={`queue-cancel-${entry.jobId}`} aria-label={`取消${entry.productLabel ? `「${entry.productLabel}」` : ""}${kindText}`} onClick={() => onCancelEntry(entry)}>取消</button>}
    </li>;
  };
  return <div className="drawer-backdrop queue-drawer-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <aside className="queue-drawer" role="dialog" aria-modal="true" aria-labelledby="queue-drawer-title" data-testid="queue-drawer">
      <header className="queue-drawer-head">
        <div>
          <h2 id="queue-drawer-title">全部任务</h2>
          {data && <small data-testid="queue-concurrency">{`同时 ${data.concurrency} 张`} · 生成中 {data.running.length} · 排队 {data.queued.length}</small>}
        </div>
        <button type="button" className="drawer-close" aria-label="关闭" autoFocus onClick={onClose}>×</button>
      </header>
      <div className="queue-drawer-body">
        {unavailable && <small className="queue-drawer-note" role="status">{data ? "网络波动，任务列表稍后自动更新" : "生成服务暂时不可用，请稍后再试"}</small>}
        {!data
          ? !unavailable && <div className="queue-drawer-empty">加载中…</div>
          : <>
            {active.length > 0
              ? <ol className="queue-entry-list" data-testid="queue-active">{active.map(row)}</ol>
              : <div className="queue-drawer-empty">没有排队的任务</div>}
            {data.recent.length > 0 && <section className="queue-recent" aria-labelledby="queue-recent-title">
              <h3 id="queue-recent-title">最近</h3>
              <ol className="queue-entry-list">{data.recent.map(row)}</ol>
            </section>}
          </>}
      </div>
    </aside>
  </div>;
}

/**
 * 取消 asks first: 取消「<商品> · <图>」？ with what happens, 再想想 (the safe
 * choice: focused, Escape, a tap outside) and a red 取消任务.
 */
function CancelJobDialog({ target, busy, onConfirm, onClose }: {
  target: CancelJobTarget;
  /** 取消任务 is on its way: both buttons wait. */
  busy: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  // Escape closes only this dialog, not 全部任务 under it.
  useEffect(() => {
    function closeOnEscape(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      if (!busy) onClose();
    }
    window.addEventListener("keydown", closeOnEscape, true);
    return () => window.removeEventListener("keydown", closeOnEscape, true);
  }, [busy, onClose]);
  return <div className="confirm-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget && !busy) onClose(); }}>
    <div className="confirm-dialog" role="alertdialog" aria-modal="true" aria-labelledby="cancel-job-title" aria-describedby="cancel-job-text" data-testid="cancel-job-dialog">
      <h2 id="cancel-job-title">{cancelJobTitle(target)}</h2>
      <div id="cancel-job-text" className="confirm-dialog-text">{CANCEL_JOB_TEXT}</div>
      <div className="confirm-dialog-actions">
        <button type="button" className="button soft" data-testid="cancel-job-keep" autoFocus disabled={busy} onClick={onClose}>再想想</button>
        <button type="button" className="button danger" data-testid="cancel-job-confirm" disabled={busy} aria-busy={busy} onClick={onConfirm}>{busy ? "取消中…" : "取消任务"}</button>
      </div>
    </div>
  </div>;
}

function VisualWorkbench({ requestedCollectionId, marketing, onCollectionChange, notify, generationQueue, onQueueChanged, jump, onJumpHandled, canExportListing, cancelledJobIds, onCancelJob }: {
  requestedCollectionId: string;
  marketing: MarketingCollectionsData | null;
  onCollectionChange: (collectionId: string) => void;
  notify: (message: string) => void;
  /** The one queue of every theme; gives queued jobs their 第 N 位. */
  generationQueue: GenerationQueueData | null;
  /** Asks for a fresh queue after new work was sent. */
  onQueueChanged: () => void;
  /** A tap in 全部任务 to show here. */
  jump: VisualJump | null;
  onJumpHandled: () => void;
  /** 5 宝贝: only the owner (管理员) may export a product's images; the site refuses others. */
  canExportListing: boolean;
  /** Jobs just cancelled from this page: gone from 队列, their step-2 images 缺 again. */
  cancelledJobIds: ReadonlySet<string>;
  /** 取消 on a queued or running job: asks first (the dialog lives with 全部任务). */
  onCancelJob: (target: CancelJobTarget) => void;
}) {
  const [manifest, setManifest] = useState<VisualWorkbenchManifest | null>(null);
  const [directory, setDirectory] = useState<VisualProjectsData | null>(null);
  const [selectedProjectDetail, setSelectedProjectDetail] = useState<VisualProject | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  // The address this page opened with (?theme=…&step=…&product=…): its step
  // and 宝贝 win over the theme's saved step.
  const [openedLink] = useState<VisualLink | null>(() => {
    const link = readVisualLink();
    return visualLinkActive(link) ? link : null;
  });
  const [selectedCollectionId, setSelectedCollectionId] = useState(() => requestedCollectionId || readStoredString(VISUAL_CURRENT_THEME_KEY));
  // A refresh reopens the theme's last step (or the link's step) at once; its selections follow when the theme's products load.
  const [phase, setPhase] = useState<VisualPhase>(() => openingVisualPhase(openedLink, requestedCollectionId || readStoredString(VISUAL_CURRENT_THEME_KEY)));
  // 5 宝贝 showing only this product ("" = every 宝贝 of the theme).
  const [listingProductId, setListingProductId] = useState(() => visualLinkFor(openedLink, requestedCollectionId || readStoredString(VISUAL_CURRENT_THEME_KEY))?.product ?? "");
  // Theme whose product list has loaded, and theme whose saved state has been applied.
  const [detailLoadedFor, setDetailLoadedFor] = useState("");
  const [hydratedCollectionId, setHydratedCollectionId] = useState("");
  const [boardSnapshot, setBoardSnapshot] = useState<PinterestBoardSnapshot | null>(null);
  const [referenceSync, setReferenceSync] = useState<{ status: ReferenceSyncStatus; syncedAt: Date | null }>({ status: "idle", syncedAt: null });
  const [referenceShotType, setReferenceShotType] = useState<VisualShotTypeFilter>("all");
  const [referenceQuery, setReferenceQuery] = useState("");
  const [selectedPinIds, setSelectedPinIds] = useState<string[]>([]);
  // 刷新 without new pins: the references' shuffled order (pin ids; null = the board's own).
  const [referenceShuffle, setReferenceShuffle] = useState<string[] | null>(null);
  // 刷新 with new pins: they wear a 新 badge while this board shows, until the next 刷新 or theme switch.
  const [newReferencePins, setNewReferencePins] = useState<{ snapshotId: string; pinIds: string[] } | null>(null);
  // Pins whose small thumbnail showed on this page (so it is on the site now),
  // and pins whose thumbnail would not load (the grid shows the preview instead).
  const [arrivedThumbPins, setArrivedThumbPins] = useState<ReadonlySet<string>>(() => new Set());
  const [failedThumbPins, setFailedThumbPins] = useState<ReadonlySet<string>>(() => new Set());
  const [reviews] = useState<Record<string, VisualReviewStatus>>(readVisualReviews);
  const [generatedFeedback, setGeneratedFeedback] = useState<Record<string, VisualGeneratedFeedback>>(readVisualFeedback);
  const [queue, setQueue] = useState<VisualQueueItem[]>(readVisualQueue);
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
  // Per product, its colours last chosen (主色 first), also while it is not ticked.
  const [selectedSkuByCatalogProduct, setSelectedSkuByCatalogProduct] = useState<Record<string, string[]>>({});
  const [preview, setPreview] = useState<{ imageUrl: string; placeholderUrl?: string; label: string } | null>(null);
  const [generationSnapshot, setGenerationSnapshot] = useState<VisualGenerationSnapshot | null>(null);
  const [generationUnavailable, setGenerationUnavailable] = useState(false);
  const [startingQueueIds, setStartingQueueIds] = useState<Set<string>>(() => new Set());
  // Product-image slots waiting to be sent, or refused by the generator.
  const [productImageRequests, setProductImageRequests] = useState<Record<string, ProductImageRequestState>>({});
  // Images with a 保留 / 不用 ("review") or 修改 ("revise") request on the way.
  const [busyOutputs, setBusyOutputs] = useState<Record<string, "review" | "revise">>({});
  // 修改 redos sent from this page: queue item → the image being redone.
  const [sentRevisions, setSentRevisions] = useState<Record<string, string>>({});
  // Older version picked on a 商品图 slot (slot key → image); none = the newest.
  const [productImageVersionChoice, setProductImageVersionChoice] = useState<Record<string, string>>({});
  // 成图: the version each image's card shows, by its first version's id (newest when not picked).
  const [sceneVersionChoice, setSceneVersionChoice] = useState<Record<string, string>>({});
  // Each entry into 5 宝贝 reads the kept images and earlier exports again.
  const [listingVisit, setListingVisit] = useState(0);
  // 生成这些商品图 is sending its requests (one after another).
  const [sendingProductImages, setSendingProductImages] = useState(false);
  // 不做这张 per product (slot keys), as stored on the Mac, for this theme only.
  const [slotSkips, setSlotSkips] = useState<{ projectId: string; byProduct: Record<string, string[]> }>({ projectId: "", byProduct: {} });
  // Slots whose 不做这张 / 恢复 is on its way.
  const [skipBusy, setSkipBusy] = useState<Record<string, boolean>>({});
  // Slots whose 用原图放大 is on its way.
  const [upscalingSlots, setUpscalingSlots] = useState<Record<string, boolean>>({});
  const upscalingSlotKeys = useRef(new Set<string>());
  // 我上传的照片 per product, for this theme only.
  const [ownerPhotos, setOwnerPhotos] = useState<{ projectId: string; byProduct: Record<string, OwnerPhoto[]> }>({ projectId: "", byProduct: {} });
  // Photos on their way, by slot (read → sent → saved).
  const [photoUploads, setPhotoUploads] = useState<Record<string, OwnerPhotoUploadState>>({});
  // Photos whose 删除 is on its way.
  const [deletingPhotos, setDeletingPhotos] = useState<Record<string, boolean>>({});
  // Photos uploaded from this page show from the picked file, not over the slow link.
  const [photoPreviews, setPhotoPreviews] = useState<Record<string, string>>({});
  const slotSkipsRef = useRef(slotSkips);
  slotSkipsRef.current = slotSkips;
  const ownerPhotosRef = useRef(ownerPhotos);
  ownerPhotosRef.current = ownerPhotos;
  const productImageRequestsRef = useRef(productImageRequests);
  productImageRequestsRef.current = productImageRequests;
  const busySkipKeys = useRef(new Set<string>());
  const uploadingSlotKeys = useRef(new Set<string>());
  const deletingPhotoIds = useRef(new Set<string>());
  // The newest read (or change) of each product's 不做 slots and photos: a late older answer is dropped.
  const slotSkipReads = useRef(new Map<string, number>());
  const ownerPhotoReads = useRef(new Map<string, number>());
  // Products whose 不做 slots were read for step 2 on this page.
  const slotSkipsReadFor = useRef(new Set<string>());
  // The newest generator records, for decisions made after a long wait (an upload).
  const latestGenerationSnapshot = useRef<VisualGenerationSnapshot | null>(null);
  const busyOutputIds = useRef(new Set<string>());
  const referenceSyncInFlight = useRef<Promise<ReferenceSyncResult> | null>(null);
  const latestBoardSnapshot = useRef<PinterestBoardSnapshot | null>(null);
  // Bumped by each 刷新 and theme switch: an older 刷新's answer is dropped.
  const referenceRefreshRun = useRef(0);
  const currentPhase = useRef(phase);
  const productImageRun = useRef<Promise<void> | null>(null);
  const productImageRerun = useRef<string[] | null>(null);
  const latestEnsureProductImages = useRef<(productIds: readonly string[]) => Promise<void>>(() => Promise.resolve());
  const activeProjectId = useRef("");
  // Requests whose answer never came (slow link): a retry sends the same queue
  // item id, so the generator never makes the image twice. Keyed by slot / image.
  const pendingProductImageRequests = useRef(new Map<string, PendingRequest>());
  const pendingRevisionRequests = useRef(new Map<string, PendingRequest>());
  // A 全部任务 tap waiting for its theme's products to load.
  const pendingJump = useRef<VisualJump | null>(null);
  // The opening link, applied once its theme's products load; its theme is
  // checked against the theme list first.
  const pendingLink = useRef<VisualLink | null>(openedLink);
  const linkThemeToCheck = useRef(openedLink?.theme ?? "");
  const followAddress = useRef<() => void>(() => undefined);
  // The address of the history entry 单独打开 last added ("" = none since this page loaded).
  const singleEntryQuery = useRef("");

  const load = useCallback(async () => {
    setLoadFailed(false);
    const [manifestResult, directoryResult] = await Promise.allSettled([
      fetch("/visual-workbench/manifest.json", { cache: "no-store" }).then(async (response) => {
        if (!response.ok) throw new Error("manifest unavailable");
        return response.json() as Promise<VisualWorkbenchManifest>;
      }),
      api<VisualProjectsData>("/api/visual-projects"),
    ]);
    if (manifestResult.status === "fulfilled") setManifest(manifestResult.value);
    if (directoryResult.status === "fulfilled") {
      setDirectory(directoryResult.value);
      const firstTheme = directoryResult.value.items.find((item) => !isPickedProject(item)) ?? directoryResult.value.items[0];
      setSelectedCollectionId((current) => current || directoryResult.value.currentCollectionId || firstTheme?.collectionId || "");
    }
    if (manifestResult.status === "rejected" || directoryResult.status === "rejected") setLoadFailed(true);
  }, []);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    let active = true;
    void api<PinterestReferencesData>("/api/pinterest-references")
      .then((data) => {
        const snapshot = data.snapshot;
        if (active && snapshot) setBoardSnapshot((current) => current ?? snapshot);
      })
      .catch(() => undefined);
    return () => { active = false; };
  }, []);
  useEffect(() => {
    if (!requestedCollectionId || requestedCollectionId === selectedCollectionId) return;
    enterTheme(requestedCollectionId);
  }, [requestedCollectionId, selectedCollectionId]);
  // 全部任务: open the job's theme at its step. A theme whose products are
  // still loading takes the tap once they are back (in the restore below).
  useEffect(() => {
    if (!jump) return;
    onJumpHandled();
    if (jump.collectionId !== selectedCollectionId || !hydrated) {
      if (jump.collectionId !== selectedCollectionId) enterTheme(jump.collectionId);
      pendingJump.current = jump;
      setPhase(jump.phase);
      return;
    }
    const next = jumpState(jump, { selectedProductIds, selectedSkuByCatalogProduct });
    setSelectedProductIds(next.selectedProductIds);
    setSelectedSkuByCatalogProduct(next.selectedSkuByCatalogProduct);
    // Step 2 only shows the job; product images are never asked for by themselves.
    setPhase(next.phase);
  }, [jump]);
  useEffect(() => {
    if (!preview) return;
    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === "Escape") setPreview(null);
    }
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [preview]);

  const fallbackDirectory = useMemo(
    () => marketing?.items.map((collection) => fallbackVisualProject(collection)) ?? [],
    [marketing],
  );
  const selectableProjects = useMemo(() => {
    if (directory?.items.length) return directory.items;
    if (fallbackDirectory.length) return fallbackDirectory;
    return manifest?.projects.map((candidate) => ({
      ...candidate,
      collectionId: projectCollectionId(candidate),
    })) ?? [];
  }, [directory, fallbackDirectory, manifest]);
  const pickedProjects = selectableProjects.filter(isPickedProject);
  const themeProjects = selectableProjects.filter((item) => !isPickedProject(item));

  useEffect(() => {
    if (selectedCollectionId || selectableProjects.length === 0) return;
    // A first visit opens the first theme, as before 我入选的商品 was listed.
    setSelectedCollectionId(projectCollectionId(selectableProjects.find((item) => !isPickedProject(item)) ?? selectableProjects[0]));
  }, [selectableProjects, selectedCollectionId]);

  useEffect(() => {
    if (!selectedCollectionId) return;
    const controller = new AbortController();
    const directoryProject = selectableProjects.find((candidate) => projectCollectionId(candidate) === selectedCollectionId) ?? null;
    // Keep an already loaded copy of this theme while it reloads.
    setSelectedProjectDetail((current) => current && projectCollectionId(current) === selectedCollectionId ? current : directoryProject);
    void api<VisualProjectDetailData>(`/api/visual-projects/${encodeURIComponent(selectedCollectionId)}`, { signal: controller.signal })
      .then((result) => {
        setSelectedProjectDetail(result.project);
        setDetailLoadedFor(selectedCollectionId);
      })
      .catch(async (caught: unknown) => {
        if (isAbortError(caught)) return;
        // The directory copy lists the same products, so the saved state can apply to it.
        if (directoryProject?.products.length) {
          setDetailLoadedFor(selectedCollectionId);
          return;
        }
        const collection = marketing?.items.find((candidate) => candidate.id === selectedCollectionId);
        if (!collection) {
          setLoadFailed(true);
          return;
        }
        try {
          // Display only: these catalog ids are not the theme's product ids, so the
          // saved state is neither applied to them nor overwritten by them.
          const catalogData = await api<CatalogData>(`/api/catalog?collection=${encodeURIComponent(selectedCollectionId)}&page=1&pageSize=100&sort=price_asc&imageQuality=all`, { signal: controller.signal });
          setSelectedProjectDetail(fallbackVisualProject(collection, catalogData.items));
        } catch (fallbackError) {
          if (isAbortError(fallbackError)) return;
          setLoadFailed(true);
        }
      });
    return () => controller.abort();
  }, [marketing, selectableProjects, selectedCollectionId]);

  const dynamicProject = selectedProjectDetail
    ?? selectableProjects.find((candidate) => projectCollectionId(candidate) === selectedCollectionId)
    ?? null;
  const project = useMemo(
    () => mergeVisualProject(dynamicProject, manifest, selectedCollectionId || (dynamicProject ? projectCollectionId(dynamicProject) : "")),
    [dynamicProject, manifest, selectedCollectionId],
  );
  const projectId = project?.id ?? "";
  activeProjectId.current = projectId;
  currentPhase.current = phase;
  latestBoardSnapshot.current = boardSnapshot;
  const hydrated = Boolean(selectedCollectionId) && hydratedCollectionId === selectedCollectionId;

  const productById = useMemo(() => new Map(project?.products.map((product) => [product.id, product]) ?? []), [project]);
  const catalogProductGroups = useMemo(() => project ? visualCatalogGroups(project) : [], [project]);
  // Each product's usable variants, for restoring and for showing a 全部任务 job.
  const selectionGroups = useMemo((): VisualSelectionGroup[] => catalogProductGroups.map((group) => ({
    productId: group.productId,
    skuIds: eligibleVisualSkuIds(group, productById),
    defaultSkuId: defaultVisualSkuId(group, productById),
  })), [catalogProductGroups, productById]);
  // Colours whose images can be made (5 宝贝 says 缺原图 for the others).
  const usableVariantIds = useMemo(() => new Set(selectionGroups.flatMap((group) => group.skuIds)), [selectionGroups]);
  const projectProductSignature = catalogProductGroups
    .map((group) => `${group.productId}:${group.skus.map((sku) => `${sku.id}:${sku.stock ?? "?"}`).join(",")}`)
    .join("|");
  // When the product list changes, keep every colour choice that is still usable.
  useEffect(() => {
    setSelectedSkuByCatalogProduct((current) => {
      const choices: Record<string, string[]> = {};
      for (const group of selectionGroups) {
        const kept = (current[group.productId] ?? []).filter((skuId) => group.skuIds.includes(skuId)).slice(0, MAX_VARIANTS_PER_PRODUCT);
        const choice = kept.length > 0 ? kept : group.defaultSkuId ? [group.defaultSkuId] : [];
        if (choice.length > 0) choices[group.productId] = choice;
      }
      return choices;
    });
    setSelectedProductIds((current) => limitVariantSelection(current, selectionGroups));
  }, [catalogProductGroups, productById, projectId, projectProductSignature, selectionGroups]);
  // Which product each colour belongs to, and the chosen colours per product (主色 first).
  const variantProductOf = useMemo(
    () => new Map(catalogProductGroups.flatMap((group) => group.skus.map((sku) => [sku.id, group.productId] as const))),
    [catalogProductGroups],
  );
  const chosenProducts = useMemo(() => chosenVariantsByProduct(selectedProductIds, variantProductOf), [selectedProductIds, variantProductOf]);
  const selectedProductCount = chosenProducts.length;

  const currentGenerationSnapshot = generationSnapshot?.projectId === projectId ? generationSnapshot : null;
  latestGenerationSnapshot.current = currentGenerationSnapshot;
  // Slots marked 不做这张 in this theme.
  const skippedSlots = useMemo(
    () => new Set(slotSkips.projectId === projectId ? Object.values(slotSkips.byProduct).flat() : []),
    [projectId, slotSkips],
  );
  const generationJobByQueueId = useMemo(
    () => new Map((currentGenerationSnapshot?.jobs ?? []).map((job) => [job.queueItemId, job])),
    [currentGenerationSnapshot],
  );
  // Once the generator lists a request that timed out, it arrived: retries need no saved id.
  useEffect(() => {
    for (const pending of [pendingProductImageRequests.current, pendingRevisionRequests.current]) {
      for (const [key, request] of pending) if (generationJobByQueueId.has(request.queueItemId)) pending.delete(key);
    }
  }, [generationJobByQueueId]);
  // 第 N 位 of each queued job in the one queue of every theme.
  const queuePositionByJobId = useMemo(
    () => new Map((generationQueue?.queued ?? []).map((entry) => [entry.jobId, entry.position])),
    [generationQueue],
  );
  // Every queued or running job of the one queue: its #N badge, preview and labels.
  const queueEntryByJobId = useMemo(
    () => new Map([...(generationQueue?.running ?? []), ...(generationQueue?.queued ?? [])].map((entry) => [entry.jobId, entry])),
    [generationQueue],
  );
  // 主题: how many images each theme has queued or running.
  const themeActiveCounts = useMemo(() => themeQueueCounts(generationQueue), [generationQueue]);
  const projectQueue = useMemo(() => queue
    .filter((item) => item.projectId === projectId)
    .map((item) => ({ ...item, productIds: item.productIds.filter((productId) => productById.has(productId)) }))
    .filter((item) => item.productIds.length > 0), [productById, projectId, queue]);
  // Any queued or running job of this theme on the generator — including work
  // started before a refresh or on another device — keeps polling on.
  const hasActiveGeneration = startingQueueIds.size > 0
    || Object.values(productImageRequests).includes("waiting")
    || (currentGenerationSnapshot?.jobs ?? []).some((job) => isActiveJob(job));
  const shouldPollGeneration = phase === "product-images" || phase === "creative" || hasActiveGeneration;

  // Poll and POST responses can arrive out of order; keep the newest store state
  // (updatedAt is the companion's last write) and ignore other themes.
  const applyGenerationSnapshot = useCallback((snapshot: VisualGenerationSnapshot) => {
    if (snapshot.projectId !== activeProjectId.current) return;
    setGenerationSnapshot((current) => current?.projectId === snapshot.projectId && current.updatedAt > snapshot.updatedAt ? current : snapshot);
  }, []);
  const loadGenerationSnapshot = useCallback(async (id: string) => {
    try {
      applyGenerationSnapshot(await fetchGenerationSnapshot(id));
      setGenerationUnavailable(false);
    } catch {
      setGenerationUnavailable(true);
    }
  }, [applyGenerationSnapshot]);
  // A product's 不做这张 slots from the Mac. Resolves with the slot keys, or
  // null when they could not be read (what the page knew stays).
  const loadSlotSkips = useCallback(async (catalogProductId: string): Promise<string[] | null> => {
    const id = activeProjectId.current;
    if (!id || !catalogProductId) return null;
    const readKey = `${id}:${catalogProductId}`;
    const ticket = (slotSkipReads.current.get(readKey) ?? 0) + 1;
    slotSkipReads.current.set(readKey, ticket);
    try {
      const answer = await api<unknown>(
        `/api/slot-skips?projectId=${encodeURIComponent(id)}&catalogProductId=${encodeURIComponent(catalogProductId)}`,
        { timeoutMs: GENERATION_POLL_TIMEOUT_MS },
      );
      const keys = skippedSlotKeys(answer, id, catalogProductId);
      if (keys && activeProjectId.current === id && slotSkipReads.current.get(readKey) === ticket) {
        setSlotSkips((current) => ({ projectId: id, byProduct: { ...(current.projectId === id ? current.byProduct : {}), [catalogProductId]: keys } }));
      }
      return keys;
    } catch {
      return null;
    }
  }, []);
  // A product's 我上传的照片 from the Mac.
  const loadOwnerPhotos = useCallback(async (catalogProductId: string): Promise<void> => {
    const id = activeProjectId.current;
    if (!id || !catalogProductId) return;
    const readKey = `${id}:${catalogProductId}`;
    const ticket = (ownerPhotoReads.current.get(readKey) ?? 0) + 1;
    ownerPhotoReads.current.set(readKey, ticket);
    try {
      const answer = await api<unknown>(
        `/api/owner-photos?projectId=${encodeURIComponent(id)}&catalogProductId=${encodeURIComponent(catalogProductId)}`,
        { timeoutMs: GENERATION_POLL_TIMEOUT_MS },
      );
      const photos = ownerPhotoList(answer, catalogProductId);
      if (photos && activeProjectId.current === id && ownerPhotoReads.current.get(readKey) === ticket) {
        setOwnerPhotos((current) => ({ projectId: id, byProduct: { ...(current.projectId === id ? current.byProduct : {}), [catalogProductId]: photos } }));
      }
    } catch {
      // The photos read last stay on the page.
    }
  }, []);
  // Step 2 shows 不做 on the slots the owner skipped: each product's are read once on this page.
  const boardProductIds = chosenProducts.map((product) => product.catalogProductId).join("\n");
  useEffect(() => {
    if (phase !== "product-images" || !projectId || !boardProductIds) return;
    for (const catalogProductId of boardProductIds.split("\n")) {
      const readKey = `${projectId}:${catalogProductId}`;
      if (slotSkipsReadFor.current.has(readKey)) continue;
      slotSkipsReadFor.current.add(readKey);
      void loadSlotSkips(catalogProductId).then((keys) => {
        if (!keys) slotSkipsReadFor.current.delete(readKey);
      });
    }
  }, [boardProductIds, loadSlotSkips, phase, projectId]);
  // Previews of photos picked on this page are freed with the page.
  const photoPreviewUrls = useRef(photoPreviews);
  photoPreviewUrls.current = photoPreviews;
  useEffect(() => () => {
    for (const url of Object.values(photoPreviewUrls.current)) URL.revokeObjectURL(url);
  }, []);

  // 5 宝贝 does not poll: one fresh read of the kept images when it opens.
  const refreshListingOutputs = useCallback(async () => {
    const id = activeProjectId.current;
    if (!id) return;
    try {
      applyGenerationSnapshot(await fetchGenerationSnapshot(id));
      setGenerationUnavailable(false);
    } catch {
      setGenerationUnavailable(true);
    }
  }, [applyGenerationSnapshot]);
  useEffect(() => {
    if (!projectId) return;
    // Every poll crosses one slow link to the generator: never start one while
    // the last is still loading, and skip ticks while the tab is hidden.
    let polling = false;
    const poll = () => {
      if (polling) return;
      polling = true;
      void loadGenerationSnapshot(projectId).finally(() => { polling = false; });
    };
    poll();
    if (!shouldPollGeneration) return;
    const timer = window.setInterval(() => { if (!document.hidden) poll(); }, 2_000);
    return () => window.clearInterval(timer);
  }, [loadGenerationSnapshot, projectId, shouldPollGeneration]);

  const boardReferences = useMemo(() => boardReferenceList(boardSnapshot), [boardSnapshot]);
  const referenceByPinId = useMemo(() => new Map(boardReferences.map((reference) => [reference.pinId, reference])), [boardReferences]);
  const selectedReferences = useMemo(
    () => selectedPinIds
      .map((pinId) => referenceByPinId.get(pinId))
      .filter((reference): reference is PinterestBoardReference => Boolean(reference)),
    [referenceByPinId, selectedPinIds],
  );
  // One product per image: every chosen product × every chosen reference, its colours taking turns.
  const plannedImageCount = selectedReferences.length * selectedProductCount;
  // The shuffled order first, then the type filter and search.
  const orderedReferences = useMemo(() => referencesInOrder(boardReferences, referenceShuffle), [boardReferences, referenceShuffle]);
  const visibleReferences = useMemo(() => {
    const query = referenceQuery.trim().toLocaleLowerCase("zh-CN");
    return orderedReferences.filter((reference) => {
      if (referenceShotType !== "all" && reference.shotType !== referenceShotType) return false;
      if (!query) return true;
      return `${reference.title} ${visualShotTypeLabels[reference.shotType] ?? ""}`.toLocaleLowerCase("zh-CN").includes(query);
    });
  }, [orderedReferences, referenceQuery, referenceShotType]);
  // 新 badges belong to the board 刷新 found them on; a changed board drops them.
  const newPinIds = useMemo(
    () => new Set(newReferencePins && newReferencePins.snapshotId === boardSnapshot?.snapshotId ? newReferencePins.pinIds : []),
    [boardSnapshot, newReferencePins],
  );
  // 还有 N 张缩略图在路上 (hidden at 0).
  const thumbsOnTheWay = useMemo(() => referenceThumbsOnTheWay(boardReferences, arrivedThumbPins), [arrivedThumbPins, boardReferences]);
  const thumbsArriving = thumbsOnTheWay > 0;
  // While some are on their way, step 3 reads the board again now and then
  // (the site's own copy, no Pinterest call) so the count goes down; the
  // order and the choices stay as they are.
  useEffect(() => {
    if (phase !== "references" || !thumbsArriving) return;
    let reading = false;
    const timer = window.setInterval(() => {
      if (reading || document.hidden || referenceSyncInFlight.current) return;
      reading = true;
      void api<PinterestReferencesData>("/api/pinterest-references")
        .then((data) => setBoardSnapshot((current) => withCachedThumbs(current, data.snapshot ?? null)))
        .catch(() => undefined)
        .finally(() => { reading = false; });
    }, REFERENCE_THUMB_POLL_MS);
    return () => window.clearInterval(timer);
  }, [phase, thumbsArriving]);
  useEffect(() => {
    if (!boardSnapshot || selectedPinIds.length === 0) return;
    const onBoard = new Set(boardSnapshot.references.map((reference) => reference.pinId));
    if (selectedPinIds.some((pinId) => !onBoard.has(pinId))) setSelectedPinIds(selectedPinIds.filter((pinId) => onBoard.has(pinId)));
  }, [boardSnapshot, selectedPinIds]);

  const syncReferences = useCallback((): Promise<ReferenceSyncResult> => {
    if (referenceSyncInFlight.current) return referenceSyncInFlight.current;
    setReferenceSync((current) => ({ ...current, status: "syncing" }));
    const request = retryReferenceSync(
      () => api<PinterestReferencesData>("/api/pinterest-sync", {
        method: "POST",
        ...jsonBody({}),
        timeoutMs: REFERENCE_SYNC_TIMEOUT_MS,
      }),
      { onRetry: () => setReferenceSync((current) => ({ ...current, status: "retrying" })) },
    )
      .then((result) => {
        if (result.snapshot) setBoardSnapshot(result.snapshot);
        setReferenceSync((current) => result.ok
          ? { status: "synced", syncedAt: new Date() }
          : { ...current, status: result.snapshot || latestBoardSnapshot.current ? "waiting" : "failed" });
        return result;
      })
      .catch((): ReferenceSyncResult => {
        setReferenceSync((current) => ({ ...current, status: latestBoardSnapshot.current ? "waiting" : "failed" }));
        return { ok: false, snapshot: null };
      })
      .finally(() => { referenceSyncInFlight.current = null; });
    referenceSyncInFlight.current = request;
    return request;
  }, []);

  // A transient outage heals by itself. Coming online or returning to the tab
  // retries immediately; otherwise one quiet retry happens after two minutes.
  useEffect(() => {
    if (phase !== "references" || referenceSync.status !== "waiting") return;
    const retry = () => {
      if (!document.hidden) void syncReferences();
    };
    const timer = window.setTimeout(retry, REFERENCE_SYNC_BACKGROUND_RETRY_MS);
    const whenVisible = () => {
      if (!document.hidden) retry();
    };
    window.addEventListener("online", retry);
    document.addEventListener("visibilitychange", whenVisible);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("online", retry);
      document.removeEventListener("visibilitychange", whenVisible);
    };
  }, [phase, referenceSync.status, syncReferences]);

  // Once a theme's products have loaded, put back its saved step and choices
  // (dropping products and pins that are gone). Coming back to step 3 counts
  // as entering it, so the board refreshes; step 2 only shows its plan.
  useEffect(() => {
    if (!project || !selectedCollectionId || detailLoadedFor !== selectedCollectionId || hydratedCollectionId === selectedCollectionId) return;
    // The link this page opened with picks the step (and the 宝贝) over the saved step.
    const linkTarget = visualLinkFor(pendingLink.current, selectedCollectionId);
    pendingLink.current = null;
    const saved = restoreVisualThemeState(
      withLinkedPhase(readVisualThemeStates()[selectedCollectionId], linkTarget?.phase ?? null),
      selectionGroups,
      boardSnapshot ? new Set(boardSnapshot.references.map((reference) => reference.pinId)) : null,
    );
    // A 全部任务 tap for this theme picks the step (and shows the job's product).
    const jumpTarget = pendingJump.current?.collectionId === selectedCollectionId ? pendingJump.current : null;
    pendingJump.current = null;
    const restored = jumpTarget ? { ...saved, ...jumpState(jumpTarget, saved) } : saved;
    setSelectedSkuByCatalogProduct(restored.selectedSkuByCatalogProduct);
    setSelectedProductIds(restored.selectedProductIds);
    setSelectedPinIds(restored.selectedPinIds);
    setPhase(restored.phase);
    if (linkTarget) setListingProductId(restored.phase === "listing" ? linkTarget.product : "");
    setHydratedCollectionId(selectedCollectionId);
    if (restored.phase === "references") void syncReferences();
  }, [boardSnapshot, detailLoadedFor, hydratedCollectionId, project, selectedCollectionId, selectionGroups, syncReferences]);

  // Saved only after the theme's own state was put back, so a half-loaded
  // page never overwrites it.
  useEffect(() => {
    if (!selectedCollectionId || hydratedCollectionId !== selectedCollectionId) return;
    writeVisualThemeState(selectedCollectionId, { phase, selectedProductIds, selectedSkuByCatalogProduct, selectedPinIds });
  }, [hydratedCollectionId, phase, selectedCollectionId, selectedPinIds, selectedProductIds, selectedSkuByCatalogProduct]);

  // One 宝贝 is shown on 5 宝贝 only.
  useEffect(() => {
    if (phase !== "listing") setListingProductId("");
  }, [phase]);

  // The address bar follows the theme, step and single 宝贝 on screen (after
  // the theme's own state is back), so a reload or a copied address opens
  // exactly this view. replaceState: no history entry for every tap.
  useEffect(() => {
    if (!selectedCollectionId || hydratedCollectionId !== selectedCollectionId) return;
    writeAddressQuery(visualLinkQuery({ theme: selectedCollectionId, phase, product: listingProductId }, window.location.search));
  }, [hydratedCollectionId, listingProductId, phase, selectedCollectionId]);

  // Back / Forward between one 宝贝 and the list (the only entries this page adds).
  useEffect(() => {
    const follow = () => followAddress.current();
    window.addEventListener("popstate", follow);
    return () => window.removeEventListener("popstate", follow);
  }, []);

  // A link's theme that the theme list does not have opens the usual theme
  // instead of a broken page; a known one becomes the current theme, like 去做图.
  useEffect(() => {
    const theme = linkThemeToCheck.current;
    if (!theme || !directory?.items.length) return;
    linkThemeToCheck.current = "";
    const known = (collectionId: string | null | undefined) => Boolean(collectionId) && directory.items.some((item) => projectCollectionId(item) === collectionId);
    if (known(theme)) {
      if (readStoredString(VISUAL_CURRENT_THEME_KEY) !== theme) onCollectionChange(theme);
      return;
    }
    const fallback = [readStoredString(VISUAL_CURRENT_THEME_KEY), directory.currentCollectionId, projectCollectionId(directory.items[0])].find(known);
    if (!fallback || fallback === selectedCollectionId) return;
    chooseProject(fallback);
    notify("链接里的主题找不到了，已打开上次的主题");
  }, [directory]);

  // A scene cancelled here leaves this browser's own list too, so it never
  // comes back as 未开始.
  useEffect(() => {
    if (cancelledJobIds.size === 0 || !currentGenerationSnapshot) return;
    const cancelled = new Set(currentGenerationSnapshot.jobs.filter((job) => cancelledJobIds.has(job.jobId)).map((job) => job.queueItemId));
    if (queue.some((item) => cancelled.has(item.id))) updateQueue((current) => current.filter((item) => !cancelled.has(item.id)));
  }, [cancelledJobIds]);

  function updateQueue(change: (current: VisualQueueItem[]) => VisualQueueItem[]) {
    setQueue((current) => {
      const next = change(current);
      writeVisualStorage(VISUAL_QUEUE_KEY, next);
      return next;
    });
  }

  function generatedFeedbackValue(id: string): VisualGeneratedFeedback {
    return generatedFeedback[id] ?? { status: reviews[`${projectId}:creative:${id}`] ?? "undecided", note: "" };
  }

  // 成图: the generator's 保留 / 修改 / 不用 and note come first; this
  // browser's copy is the fallback (older choices, or the service was down).
  function sceneOutputFeedback(output: VisualGenerationOutput): VisualGeneratedFeedback {
    const cached = generatedFeedbackValue(output.outputId);
    if (!output.reviewDecision) return cached;
    return { status: output.reviewDecision, note: output.reviewNote ?? (output.reviewDecision === "revise" ? cached.note : "") };
  }

  // Fallback copy in this browser only; the generator keeps the real choice.
  function cacheGeneratedFeedback(id: string, status: VisualReviewStatus, note?: string) {
    setGeneratedFeedback((current) => {
      const next = { ...current, [id]: { status, note: note ?? current[id]?.note ?? "" } };
      writeVisualStorage(VISUAL_FEEDBACK_KEY, next);
      return next;
    });
  }

  // Earlier finished images from the theme files are not generator images and cannot be redone.
  function saveLegacyRevision(id: string, note: string): boolean {
    cacheGeneratedFeedback(id, "revise", cleanRevisionNote(note));
    notify("这张是旧图，不能重新生成");
    return true;
  }

  // One request per image at a time; the ref also stops a quick double tap.
  function claimOutput(outputId: string, kind: "review" | "revise"): boolean {
    if (busyOutputIds.current.has(outputId)) return false;
    busyOutputIds.current.add(outputId);
    setBusyOutputs((current) => ({ ...current, [outputId]: kind }));
    return true;
  }

  function releaseOutput(outputId: string) {
    busyOutputIds.current.delete(outputId);
    setBusyOutputs((current) => {
      const next = { ...current };
      delete next[outputId];
      return next;
    });
  }

  function mergeReviewedOutput(outputId: string, result: VisualOutputReviewData, reviewed: Partial<VisualGenerationOutput>) {
    if (result.snapshot) applyGenerationSnapshot(result.snapshot);
    const merged: Partial<VisualGenerationOutput> = { ...reviewed, ...result.output };
    setGenerationSnapshot((current) => current && current.outputs.some((item) => item.outputId === outputId)
      ? { ...current, outputs: current.outputs.map((item) => item.outputId === outputId ? { ...item, ...merged } : item) }
      : current);
  }

  function eligibleSkuIds(group: VisualCatalogProduct): string[] {
    return eligibleVisualSkuIds(group, productById);
  }

  // A product's chosen colours, 主色 first; none = the product is not ticked.
  function chosenVariantsOf(group: VisualCatalogProduct): string[] {
    return chosenProducts.find((product) => product.catalogProductId === group.productId)?.skuIds ?? [];
  }

  // The colours a product had last (主色 first), else its usual one.
  function rememberedVariantsOf(group: VisualCatalogProduct): string[] {
    const usable = eligibleSkuIds(group);
    const remembered = (selectedSkuByCatalogProduct[group.productId] ?? []).filter((skuId) => usable.includes(skuId));
    if (remembered.length > 0) return remembered.slice(0, MAX_VARIANTS_PER_PRODUCT);
    const fallback = defaultVisualSkuId(group, productById) ?? usable[0];
    return fallback ? [fallback] : [];
  }

  // Sets a product's chosen colours (主色 first) and remembers them; none unticks it.
  function setProductVariants(group: VisualCatalogProduct, variants: readonly string[]) {
    const next = [...new Set(variants)].slice(0, MAX_VARIANTS_PER_PRODUCT);
    setSelectedProductIds(withProductVariants(selectedProductIds, group.skus.map((sku) => sku.id), next));
    if (next.length > 0) setSelectedSkuByCatalogProduct((current) => ({ ...current, [group.productId]: next }));
  }

  // A product not chosen yet needs room among the 15.
  function roomForProduct(group: VisualCatalogProduct): boolean {
    if (chosenVariantsOf(group).length > 0 || selectedProductCount < MAX_SCENE_PRODUCTS) return true;
    notify(`一次最多选 ${MAX_SCENE_PRODUCTS} 个商品`);
    return false;
  }

  function toggleCatalogProduct(group: VisualCatalogProduct) {
    if (chosenVariantsOf(group).length > 0) {
      setProductVariants(group, []);
      return;
    }
    const variants = rememberedVariantsOf(group);
    if (variants.length === 0) {
      notify("这个商品缺原图或缺规格");
      return;
    }
    if (!roomForProduct(group)) return;
    setProductVariants(group, variants);
  }

  // A colour chip: on or off; the first colour on is the 主色. Tapping a
  // colour of a product not ticked ticks it with that colour.
  function toggleCatalogVariant(group: VisualCatalogProduct, skuId: string) {
    if (!eligibleSkuIds(group).includes(skuId)) {
      notify("这个规格缺原图或缺规格");
      return;
    }
    const chosen = chosenVariantsOf(group);
    if (chosen.includes(skuId)) {
      setProductVariants(group, chosen.filter((id) => id !== skuId));
      return;
    }
    if (chosen.length === 0) {
      if (roomForProduct(group)) setProductVariants(group, [skuId]);
      return;
    }
    if (chosen.length >= MAX_VARIANTS_PER_PRODUCT) {
      notify(`每个商品最多选 ${MAX_VARIANTS_PER_PRODUCT} 个颜色`);
      return;
    }
    setProductVariants(group, [...chosen, skuId]);
  }

  // 全部颜色 is on once tapping it would add nothing more.
  function allColoursChosen(group: VisualCatalogProduct, chosen: readonly string[]): boolean {
    const all = allColourVariantIds(group, productById);
    return all.length > 0 && chosen.length > 0 && withAllColours(chosen, all).every((id) => chosen.includes(id));
  }

  // 全部颜色: every colour in stock (the 主色 stays first); again = only the 主色.
  function toggleAllColours(group: VisualCatalogProduct) {
    const all = allColourVariantIds(group, productById);
    const chosen = chosenVariantsOf(group);
    if (allColoursChosen(group, chosen)) {
      setProductVariants(group, chosen.slice(0, 1));
      return;
    }
    if (!roomForProduct(group)) return;
    const base = chosen.length > 0 ? chosen : rememberedVariantsOf(group).slice(0, 1);
    setProductVariants(group, withAllColours(base, all));
  }

  function selectAllCatalogProducts() {
    const picks = catalogProductGroups.flatMap((group) => {
      const chosen = chosenVariantsOf(group);
      const variants = chosen.length > 0 ? chosen : rememberedVariantsOf(group);
      return variants.length > 0 ? [variants] : [];
    });
    setSelectedProductIds(picks.slice(0, MAX_SCENE_PRODUCTS).flat());
    if (picks.length > MAX_SCENE_PRODUCTS) notify(`已选前 ${MAX_SCENE_PRODUCTS} 个商品`);
  }

  // Switching theme clears what belongs to the old theme and reopens the new
  // theme's last step; its choices come back once its products load.
  function enterTheme(collectionId: string) {
    setSelectedCollectionId(collectionId);
    // Nothing is saved for the new theme until its own state is back.
    setDetailLoadedFor("");
    setHydratedCollectionId("");
    setGenerationSnapshot(null);
    setGenerationUnavailable(false);
    setPhase(savedVisualPhase(collectionId));
    setReferenceShotType("all");
    setReferenceQuery("");
    setNewReferencePins(null);
    referenceRefreshRun.current += 1;
    setSelectedPinIds([]);
    setSelectedProductIds([]);
    setSelectedSkuByCatalogProduct({});
    setProductImageRequests({});
    setProductImageVersionChoice({});
    setSceneVersionChoice({});
    setPreview(null);
    setListingProductId("");
    pendingJump.current = null;
    pendingLink.current = null;
  }

  // The step, products and colours that show a 全部任务 job of this theme:
  // a product image needs its product chosen, with the job's colour.
  function jumpState(target: VisualJump, base: Pick<VisualThemeState, "selectedProductIds" | "selectedSkuByCatalogProduct">): Pick<VisualThemeState, "phase" | "selectedProductIds" | "selectedSkuByCatalogProduct"> {
    if (target.phase !== "product-images") return { ...base, phase: target.phase };
    const sourceSkuIdById = new Map([...productById].map(([id, product]) => [id, product.sourceSkuId]));
    const shown = selectionShowingJob(base.selectedProductIds, selectionGroups, sourceSkuIdById, target);
    if (!shown) return { ...base, phase: base.selectedProductIds.length > 0 ? "product-images" : "truth" };
    const choices = { ...base.selectedSkuByCatalogProduct };
    for (const group of selectionGroups) {
      const chosen = shown.filter((id) => group.skuIds.includes(id));
      if (chosen.length > 0) choices[group.productId] = chosen;
    }
    return { phase: "product-images", selectedProductIds: shown, selectedSkuByCatalogProduct: choices };
  }

  function chooseProject(collectionId: string) {
    enterTheme(collectionId);
    onCollectionChange(collectionId);
  }

  // `chosen` is the choice just made in the same tap (state updates later).
  // Every step opens whole: the 5 宝贝 tab lists every 宝贝 again. Step 2
  // only shows what would be made; nothing is asked for until 生成这些商品图.
  function goToPhase(next: VisualPhase, chosen: readonly string[] = selectedProductIds) {
    setListingProductId("");
    if ((next === "product-images" || next === "references") && chosen.length === 0) {
      notify("先选商品");
      setPhase("truth");
      return;
    }
    setPhase(next);
    if (next === "references") void syncReferences();
    if (next === "listing") setListingVisit((visit) => visit + 1);
  }

  // Back / Forward: show what the address now says (theme, step, one 宝贝).
  followAddress.current = () => {
    const link = readVisualLink();
    if (!visualLinkActive(link)) return;
    if (link.theme && link.theme !== selectedCollectionId) {
      chooseProject(link.theme);
      pendingLink.current = link;
      if (link.phase) setPhase(link.phase);
      setListingProductId(link.product);
      return;
    }
    if (link.phase && link.phase !== phase) goToPhase(link.phase);
    setListingProductId(link.product);
  };

  // 5 宝贝 · 单独打开: only this 宝贝, as a new history entry so Back returns to the list.
  function openListingProduct(catalogProductId: string) {
    const query = visualLinkQuery({ theme: selectedCollectionId, phase: "listing", product: catalogProductId }, window.location.search);
    if (writeAddressQuery(query, "push")) singleEntryQuery.current = `?${query}`;
    setListingProductId(catalogProductId);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  // 查看全部: on an entry 单独打开 added, go Back to the list it came from, so
  // the history never holds the list twice; otherwise (a link opened as is,
  // or after a reload) the address is replaced.
  function showAllListing() {
    if (singleEntryQuery.current && window.location.search === singleEntryQuery.current) {
      try {
        // The list shows when the address changes back (popstate).
        window.history.back();
        return;
      } catch {
        // Falls back to replacing the address.
      }
    }
    setListingProductId("");
  }

  // The address of this theme's 宝贝 list, or of one 宝贝, to copy and send.
  function listingLink(catalogProductId: string): string {
    return visualLinkHref(window.location.origin, { theme: selectedCollectionId, phase: "listing", product: catalogProductId });
  }

  // 生成这些商品图: asks for every product image the chosen products still
  // lack — only on that tap, never on entering step 2. Runs one at a time; a
  // call while a run is sending is remembered and checked after it.
  function ensureProductImages(productIds: readonly string[] = selectedProductIds): Promise<void> {
    if (productImageRun.current) {
      productImageRerun.current = [...productIds];
      return productImageRun.current;
    }
    setSendingProductImages(true);
    const run = requestMissingProductImages(productIds).finally(() => {
      productImageRun.current = null;
      const again = productImageRerun.current;
      productImageRerun.current = null;
      if (again) void latestEnsureProductImages.current(again);
      else setSendingProductImages(false);
    });
    productImageRun.current = run;
    return run;
  }
  latestEnsureProductImages.current = ensureProductImages;

  async function requestMissingProductImages(productIds: readonly string[]): Promise<void> {
    if (!project) return;
    const targetProjectId = project.id;
    // 白底图 for every chosen colour; 细节图 / 静物图 for the 主色; 组合图 per product.
    const slots = productImageSlotsFor(productImageCardsFor(productIds, productById, catalogProductGroups));
    if (slots.length === 0) return;
    // Decide from the newest server state so nothing made or running is asked for twice.
    let snapshot = currentGenerationSnapshot;
    // 不做这张 slots are read again at the same time: they are never asked for.
    const skipsRead = currentSkippedSlots(slots.map((slot) => slot.catalogProductId));
    try {
      snapshot = await fetchGenerationSnapshot(targetProjectId);
      applyGenerationSnapshot(snapshot);
      setGenerationUnavailable(false);
    } catch {
      if (!snapshot) {
        setGenerationUnavailable(true);
        notify("生成服务暂时不可用，请稍后再试");
        return;
      }
    }
    const skipped = await skipsRead;
    if (activeProjectId.current !== targetProjectId) return;
    // A job just cancelled here counts as never asked for, like on the board.
    const satisfied = satisfiedProductImageSlots({ ...snapshot, jobs: withoutCancelledJobs(snapshot.jobs, cancelledJobIds) });
    const missing = slots.filter((slot) => !satisfied.has(slot.key) && !skipped.has(slot.key));
    setProductImageRequests((current) => {
      const next = { ...current };
      for (const slot of slots) delete next[slot.key];
      for (const slot of missing) next[slot.key] = "waiting";
      return next;
    });
    if (missing.length === 0) {
      notify("这些商品图都有了");
      return;
    }
    let sent = 0;
    let refused = 0;
    // Sent in order, each after the last, so the generator works first in, first out.
    for (let index = 0; index < missing.length; index += 1) {
      const outcome = activeProjectId.current === targetProjectId
        ? await submitProductImageSlot(missing[index], targetProjectId)
        : "unavailable";
      if (outcome === "sent" || outcome === "slow") sent += 1;
      if (outcome === "refused") refused += 1;
      if (outcome !== "unavailable") continue;
      const unsent = new Set(missing.slice(index + 1).map((slot) => slot.key));
      setProductImageRequests((current) => Object.fromEntries(Object.entries(current).filter(([key]) => !unsent.has(key))));
      // The service went away: its own message stays unless some were sent.
      if (sent > 0) notify(`已排上 ${sent} 张，其余没排上，请稍后再试`);
      return;
    }
    // Refused submissions keep their safe API reason on the slot; the toast also makes the refusal visible.
    if (sent > 0) notify(refused > 0 ? `已排上 ${sent} 张，${refused} 张没排上` : `已排上 ${sent} 张`);
    else if (refused > 0) notify(`${refused} 张没排上，请刷新后重试`);
  }

  // "slow": no answer in time. The generator may have taken it, so the run goes
  // on, and a retry of this slot sends the same queue item id.
  async function submitProductImageSlot(slot: ProductImageSlot, targetProjectId: string): Promise<"sent" | "refused" | "unavailable" | "slow"> {
    setProductImageRequests((current) => ({ ...current, [slot.key]: "waiting" }));
    const requestKey = `${targetProjectId}|${slot.key}|${slot.productIds.join(",")}`;
    const queueItemId = reusableQueueItemId(pendingProductImageRequests.current.get(slot.key), requestKey, generationJobByQueueId) ?? `local-${randomUuid()}`;
    pendingProductImageRequests.current.set(slot.key, { queueItemId, requestKey });
    let outcome: "sent" | "refused" | "unavailable" | "slow" = "sent";
    let rejection: ProductImageRequestRejection | null = null;
    try {
      const result = await api<{ snapshot: VisualGenerationSnapshot }>("/api/generation-jobs", {
        method: "POST",
        ...jsonBody(productImageJobBody(slot, targetProjectId, queueItemId)),
        timeoutMs: GENERATION_POST_TIMEOUT_MS,
      });
      pendingProductImageRequests.current.delete(slot.key);
      applyGenerationSnapshot(result.snapshot);
      setGenerationUnavailable(false);
      onQueueChanged();
    } catch (caught) {
      // A 4xx means the submission did not create a job. Keep the site's safe
      // code and wording instead of presenting it as an image-generation failure.
      const refused = caught instanceof ApiError && caught.status >= 400 && caught.status < 500;
      if (refused && caught instanceof ApiError) {
        pendingProductImageRequests.current.delete(slot.key);
        rejection = productImageRequestRejection(caught);
      }
      outcome = refused ? "refused" : isSlowNetworkError(caught) ? "slow" : "unavailable";
      if (!refused) notify(generationErrorMessage(caught));
    }
    if (activeProjectId.current === targetProjectId) {
      setProductImageRequests((current) => {
        const next = { ...current };
        if (outcome === "refused" && rejection) next[slot.key] = rejection;
        else delete next[slot.key];
        return next;
      });
    }
    return outcome;
  }

  function redoProductImage(slot: ProductImageSlot) {
    if (!project || productImageRequests[slot.key] === "waiting") return;
    void submitProductImageSlot(slot, project.id);
  }

  // 找同款 searches the product's own name and the slot's colour.
  function slotSearchText(slot: ProductImageSlot): string {
    return sameStyleSearchText(productById.get(slot.productIds[0])?.label, slot.jobType === "truth_combo" ? null : slot.variantLabel);
  }

  // 用原图放大: the slot's newest failed run becomes the real photo, cropped
  // and enlarged on the Mac (nothing redrawn).
  async function upscaleSlot(slot: ProductImageSlot) {
    if (upscalingSlotKeys.current.has(slot.key)) return;
    const failed = (currentGenerationSnapshot?.jobs ?? [])
      .filter((job) => productImageJobSlotKey(job) === slot.key && job.status === "failed" && !isCancelledJob(job))
      .sort((left, right) => right.createdAt.localeCompare(left.createdAt))[0];
    if (!failed) {
      notify("这张图的记录还没刷新出来，请稍后再试");
      return;
    }
    upscalingSlotKeys.current.add(slot.key);
    setUpscalingSlots((current) => ({ ...current, [slot.key]: true }));
    try {
      const result = await api<VisualSourceUpscaleData>("/api/source-upscale", {
        method: "POST",
        ...jsonBody({ jobId: failed.jobId }),
        timeoutMs: GENERATION_POST_TIMEOUT_MS,
      });
      if (result.snapshot) applyGenerationSnapshot(result.snapshot);
      notify("已用原图放大，可以在格子里看");
    } catch (caught) {
      notify(generationErrorMessage(caught));
    } finally {
      upscalingSlotKeys.current.delete(slot.key);
      setUpscalingSlots((current) => {
        const next = { ...current };
        delete next[slot.key];
        return next;
      });
    }
  }

  // The 不做这张 slots of these products, read again just before a run sends
  // anything, so a slot skipped on another device is never asked for; a
  // product that cannot be read keeps what this page knew.
  async function currentSkippedSlots(catalogProductIds: readonly string[]): Promise<Set<string>> {
    const products = [...new Set(catalogProductIds)];
    const fresh = await Promise.all(products.map((catalogProductId) => loadSlotSkips(catalogProductId)));
    const known = slotSkipsRef.current.projectId === activeProjectId.current ? slotSkipsRef.current.byProduct : {};
    return new Set(products.flatMap((catalogProductId, index) => fresh[index] ?? known[catalogProductId] ?? []));
  }

  function storeSlotSkips(targetProjectId: string, catalogProductId: string, keys: readonly string[]) {
    setSlotSkips((current) => ({
      projectId: targetProjectId,
      byProduct: { ...(current.projectId === targetProjectId ? current.byProduct : {}), [catalogProductId]: [...keys] },
    }));
  }

  // 不做这张 (skipped: true) / 恢复 (false), stored on the Mac so every device sees it.
  async function setSlotSkipped(slot: ProductImageSlot, skipped: boolean) {
    if (!project || busySkipKeys.current.has(slot.key)) return;
    const targetProjectId = project.id;
    const readKey = `${targetProjectId}:${slot.catalogProductId}`;
    busySkipKeys.current.add(slot.key);
    setSkipBusy((current) => ({ ...current, [slot.key]: true }));
    try {
      const answer = await api<unknown>("/api/slot-skips", {
        method: "POST",
        ...jsonBody(slotSkipBody(slot, targetProjectId, skipped)),
        timeoutMs: GENERATION_POST_TIMEOUT_MS,
      });
      // A read that started before this change would bring the old state back.
      slotSkipReads.current.set(readKey, (slotSkipReads.current.get(readKey) ?? 0) + 1);
      if (activeProjectId.current === targetProjectId) {
        // An answer listing the product's 不做 slots replaces them; any other answer means this one change was stored.
        const known = slotSkipsRef.current.projectId === targetProjectId ? slotSkipsRef.current.byProduct[slot.catalogProductId] ?? [] : [];
        const listed = skippedSlotKeys(answer, targetProjectId, slot.catalogProductId);
        storeSlotSkips(targetProjectId, slot.catalogProductId, listed ?? (skipped ? [...new Set([...known, slot.key])] : known.filter((key) => key !== slot.key)));
      }
      notify(skipped ? "这张不做了，可以随时恢复" : "已恢复");
    } catch (caught) {
      notify(slotSkipFailureText(caught));
    } finally {
      busySkipKeys.current.delete(slot.key);
      setSkipBusy((current) => {
        const next = { ...current };
        delete next[slot.key];
        return next;
      });
    }
  }

  function setPhotoUpload(slotKey: string, state: OwnerPhotoUploadState | null) {
    setPhotoUploads((current) => {
      const next = { ...current };
      if (state) next[slotKey] = state;
      else delete next[slotKey];
      return next;
    });
  }

  // 上传照片 on an image that did not work out: the owner's photo of that
  // colour goes to the Mac as it is (base64, nothing converted), with the
  // progress on the slot; then the image is made again with it, like 重做.
  // The photo only gives the generator a truer look; nothing is invented.
  async function uploadSlotPhoto(slot: ProductImageSlot, file: File) {
    if (!project || uploadingSlotKeys.current.has(slot.key)) return;
    const sizeProblem = ownerPhotoSizeProblem(file.size);
    if (sizeProblem) {
      notify(sizeProblem);
      return;
    }
    const targetProjectId = project.id;
    uploadingSlotKeys.current.add(slot.key);
    setPhotoUpload(slot.key, { stage: "reading", percent: 0 });
    try {
      let bytes: Uint8Array;
      try {
        bytes = new Uint8Array(await file.arrayBuffer());
      } catch {
        notify("这张照片读不出来，请换一张");
        return;
      }
      const formatProblem = ownerPhotoFormatProblem(ownerPhotoFormat(bytes));
      if (formatProblem) {
        notify(formatProblem);
        return;
      }
      setPhotoUpload(slot.key, { stage: "sending", percent: 0 });
      let answer: unknown;
      try {
        answer = await apiUpload<unknown>("/api/owner-photos", ownerPhotoUploadBody(slot, targetProjectId, file.name, bytesToBase64(bytes)), {
          timeoutMs: OWNER_PHOTO_TIMEOUT_MS,
          onProgress: (sent, total) => setPhotoUpload(slot.key, ownerPhotoUploadState(sent, total)),
        });
      } catch (caught) {
        notify(ownerPhotoFailureText(caught));
        return;
      }
      rememberUploadedPhoto(targetProjectId, slot.catalogProductId, answer, file);
      if (activeProjectId.current !== targetProjectId) {
        notify("照片已上传");
        return;
      }
      // Made again with the photo — unless that image is already on its way.
      const onItsWay = productImageRequestsRef.current[slot.key] === "waiting"
        || (latestGenerationSnapshot.current?.jobs ?? []).some((job) => productImageJobSlotKey(job) === slot.key && isActiveJob(job) && !isCancelledJob(job, cancelledJobIds));
      if (onItsWay) {
        notify("照片已上传，这张正在做");
        return;
      }
      setPhotoUpload(slot.key, null);
      const outcome = await submitProductImageSlot(slot, targetProjectId);
      notify(outcome === "sent" || outcome === "slow" ? "照片已上传，正在按照片重做" : "照片已上传，重做没排上，请稍后点重做");
    } finally {
      uploadingSlotKeys.current.delete(slot.key);
      setPhotoUpload(slot.key, null);
    }
  }

  // The uploaded photo shows at once (from the picked file) in 我上传的照片.
  function rememberUploadedPhoto(targetProjectId: string, catalogProductId: string, answer: unknown, file: File) {
    const readKey = `${targetProjectId}:${catalogProductId}`;
    ownerPhotoReads.current.set(readKey, (ownerPhotoReads.current.get(readKey) ?? 0) + 1);
    const stored = ownerPhotosRef.current;
    const known = stored.projectId === targetProjectId ? stored.byProduct[catalogProductId] ?? [] : [];
    const photos = photosAfterUpload(known, answer, catalogProductId);
    if (!photos) {
      // The answer does not list it: read the list again (only while still on that theme).
      if (activeProjectId.current === targetProjectId) void loadOwnerPhotos(catalogProductId);
      return;
    }
    const added = photos.find((photo) => !known.some((item) => item.id === photo.id));
    if (added && typeof URL.createObjectURL === "function") {
      const previewUrl = URL.createObjectURL(file);
      setPhotoPreviews((current) => ({ ...current, [added.id]: previewUrl }));
    }
    if (activeProjectId.current !== targetProjectId) return;
    setOwnerPhotos((current) => ({
      projectId: targetProjectId,
      byProduct: { ...(current.projectId === targetProjectId ? current.byProduct : {}), [catalogProductId]: photos },
    }));
  }

  // 删除 (after 确认删除) on one of 我上传的照片: only that photo goes.
  async function deleteOwnerPhoto(photo: OwnerPhoto) {
    if (!project || deletingPhotoIds.current.has(photo.id)) return;
    const targetProjectId = project.id;
    const readKey = `${targetProjectId}:${photo.catalogProductId}`;
    deletingPhotoIds.current.add(photo.id);
    setDeletingPhotos((current) => ({ ...current, [photo.id]: true }));
    const forget = () => {
      ownerPhotoReads.current.set(readKey, (ownerPhotoReads.current.get(readKey) ?? 0) + 1);
      setOwnerPhotos((current) => current.projectId === targetProjectId
        ? { ...current, byProduct: { ...current.byProduct, [photo.catalogProductId]: (current.byProduct[photo.catalogProductId] ?? []).filter((item) => item.id !== photo.id) } }
        : current);
      setPhotoPreviews((current) => {
        if (!current[photo.id]) return current;
        URL.revokeObjectURL(current[photo.id]);
        const next = { ...current };
        delete next[photo.id];
        return next;
      });
    };
    try {
      await api<unknown>(ownerPhotoDeletePath(photo), { method: "DELETE", timeoutMs: GENERATION_POST_TIMEOUT_MS });
      forget();
      notify("照片已删除");
    } catch (caught) {
      // Already gone is what was asked for.
      if (caught instanceof ApiError && caught.status === 404) {
        forget();
        notify("照片已删除");
      } else {
        notify(isSlowNetworkError(caught) ? "网络有点慢，请稍后刷新看看" : "没删掉，请稍后再试");
      }
    } finally {
      deletingPhotoIds.current.delete(photo.id);
      setDeletingPhotos((current) => {
        const next = { ...current };
        delete next[photo.id];
        return next;
      });
    }
  }

  // 5 宝贝 reads a product's 不做 slots and photos with its exports, once per visit and 刷新.
  function loadListingProduct(catalogProductId: string) {
    if (projectId) slotSkipsReadFor.current.add(`${projectId}:${catalogProductId}`);
    void loadSlotSkips(catalogProductId);
    void loadOwnerPhotos(catalogProductId);
  }

  // 5 宝贝 · 一键补齐. Shares step 2's one-run-at-a-time rule: it waits for
  // nothing else to be sending, and step 2 entered meanwhile checks after it.
  function fillListingImages(input: ListingFillInput): Promise<number> {
    if (productImageRun.current) {
      notify("正在提交，请稍等");
      return Promise.resolve(0);
    }
    let sent = 0;
    const run = requestListingImages(input)
      .then((count) => { sent = count; })
      .finally(() => {
        productImageRun.current = null;
        const again = productImageRerun.current;
        productImageRerun.current = null;
        if (again) void latestEnsureProductImages.current(again);
      });
    productImageRun.current = run;
    return run.then(() => sent);
  }

  // Decides again from the newest server state (work may have started
  // elsewhere), then sends each missing or failed image in order, each after
  // the last, through the same path as step 2 (same ids on retry, 60 s limit).
  // 5 宝贝 does not poll while nothing runs, so the copy on the page can be
  // minutes old: without the newest records nothing is sent, so nothing made
  // or on the way elsewhere is asked for twice.
  async function requestListingImages(input: ListingFillInput): Promise<number> {
    if (!project) return 0;
    const targetProjectId = project.id;
    let snapshot: VisualGenerationSnapshot;
    // 不做这张 slots are read again at the same time: 一键补齐 never plans them.
    const skipsRead = currentSkippedSlots([input.group.productId]);
    try {
      snapshot = await fetchGenerationSnapshot(targetProjectId);
      applyGenerationSnapshot(snapshot);
      setGenerationUnavailable(false);
    } catch (caught) {
      notify(isSlowNetworkError(caught) ? "网络有点慢，请稍后再试" : "生成服务暂时不可用，请稍后再试");
      return 0;
    }
    const skipped = await skipsRead;
    if (activeProjectId.current !== targetProjectId) return 0;
    const completeness = listingCompleteness(withoutCancelledJobs(snapshot.jobs, cancelledJobIds), snapshot.outputs, input.group, input.onSale, { mainSkuId: input.mainSkuId, requests: productImageRequests, skipped });
    const slots = listingFillSlots(completeness, input.group.productId);
    if (slots.length === 0) {
      notify("没有要补的图");
      return 0;
    }
    setProductImageRequests((current) => {
      const next = { ...current };
      for (const slot of slots) next[slot.key] = "waiting";
      return next;
    });
    let sent = 0;
    let refused = 0;
    for (let index = 0; index < slots.length; index += 1) {
      const outcome = activeProjectId.current === targetProjectId
        ? await submitProductImageSlot(slots[index], targetProjectId)
        : "unavailable";
      if (outcome === "sent" || outcome === "slow") sent += 1;
      if (outcome === "refused") refused += 1;
      if (outcome !== "unavailable") continue;
      const unsent = new Set(slots.slice(index + 1).map((slot) => slot.key));
      setProductImageRequests((current) => Object.fromEntries(Object.entries(current).filter(([key]) => !unsent.has(key))));
      break;
    }
    // Refused submissions show 提交未成功 on their chips; the toast also prevents a silent tap.
    if (sent > 0) notify(refused > 0 ? `已排上 ${sent} 张，${refused} 张没排上` : `已排上 ${sent} 张`);
    else if (refused > 0) notify(`${refused} 张没排上，请刷新后重试`);
    return sent;
  }

  // 5 宝贝 · 去做场景图: step 3 with only this product, its 主色 first, through
  // the usual way in (the board syncs); the owner then picks references, none
  // are picked for them.
  function openSceneStep(catalogProductId: string, forSale: readonly string[]) {
    const group = selectionGroups.find((candidate) => candidate.productId === catalogProductId);
    const next = sceneStepSelection(selectedProductIds, group?.skuIds ?? [], forSale);
    if (next.length === 0) {
      notify("这个商品缺原图或缺规格");
      return;
    }
    setSelectedProductIds(next);
    setSelectedSkuByCatalogProduct((current) => ({ ...current, [catalogProductId]: next }));
    setSelectedPinIds([]);
    goToPhase("references", next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  // 保留 / 不用 on a generator image ("undecided" = tapping the chosen one again clears it).
  // Resolves true once the generator has stored the choice.
  async function reviewOutput(output: VisualGenerationOutput, choice: VisualReviewStatus): Promise<boolean> {
    const decision: Exclude<VisualOutputReviewDecision, "revise"> | "clear" = choice === "keep" || choice === "reject" ? choice : "clear";
    if (!claimOutput(output.outputId, "review")) return false;
    try {
      const result = await api<VisualOutputReviewData>("/api/generation-output-review", {
        method: "POST",
        ...jsonBody({ outputId: output.outputId, decision }),
        timeoutMs: GENERATION_POST_TIMEOUT_MS,
      });
      mergeReviewedOutput(output.outputId, result, { reviewDecision: decision === "clear" ? null : decision });
      return true;
    } catch (caught) {
      notify(generationErrorMessage(caught));
      return false;
    } finally {
      releaseOutput(output.outputId);
    }
  }

  // 成图 keep a copy of the choice in this browser too, written only once the
  // generator stored it, so a failed tap never looks saved.
  async function reviewSceneOutput(output: VisualGenerationOutput, choice: VisualReviewStatus) {
    if (await reviewOutput(output, choice)) cacheGeneratedFeedback(output.outputId, choice);
  }

  // 修改 → 保存: the generator stores the note, then redoes the image with it
  // as a new version next to the old one. An empty note is a plain redo.
  async function reviseOutput(output: VisualGenerationOutput, rawNote: string, { cache = false } = {}): Promise<boolean> {
    if (!project) return false;
    const note = cleanRevisionNote(rawNote);
    const parentJob = currentGenerationSnapshot?.jobs.find((job) => job.jobId === output.jobId);
    // A redo whose answer never came goes out again under its first id, so it is made once.
    const queueItemId = reusableQueueItemId(pendingRevisionRequests.current.get(output.outputId), note, generationJobByQueueId) ?? `local-${randomUuid()}`;
    const body = revisionJobBody(output, parentJob, queueItemId, note);
    if (!body) {
      // Like the earlier finished images: a 成图 keeps the note in this browser.
      if (cache) cacheGeneratedFeedback(output.outputId, "revise", note);
      notify("这张是旧图，不能重新生成");
      return cache;
    }
    if (!claimOutput(output.outputId, "revise")) return false;
    try {
      try {
        const review = await api<VisualOutputReviewData>("/api/generation-output-review", {
          method: "POST",
          ...jsonBody({ outputId: output.outputId, decision: "revise", note }),
          timeoutMs: GENERATION_POST_TIMEOUT_MS,
        });
        mergeReviewedOutput(output.outputId, review, { reviewDecision: "revise", reviewNote: note || null });
        // The browser copy follows the generator, never ahead of it.
        if (cache) cacheGeneratedFeedback(output.outputId, "revise", note);
      } catch (caught) {
        // No answer in time: the note most likely arrived, and the redo carries it too.
        if (!isSlowNetworkError(caught)) throw caught;
      }
      pendingRevisionRequests.current.set(output.outputId, { queueItemId, requestKey: note });
      // Linked before the answer, so a redo whose answer is slow still shows on this image.
      setSentRevisions((current) => ({ ...current, [queueItemId]: output.outputId }));
      const result = await api<{ snapshot: VisualGenerationSnapshot }>("/api/generation-jobs", {
        method: "POST",
        ...jsonBody(body),
        timeoutMs: GENERATION_POST_TIMEOUT_MS,
      });
      pendingRevisionRequests.current.delete(output.outputId);
      applyGenerationSnapshot(result.snapshot);
      setGenerationUnavailable(false);
      onQueueChanged();
      notify("已提交，正在按意见重做");
      return true;
    } catch (caught) {
      notify(generationErrorMessage(caught));
      // No answer in time: the redo most likely arrived and goes on, so the field
      // closes as after a sent redo instead of inviting a second one; 修改 again
      // sends the same queue item id until the generator lists the redo.
      if (isSlowNetworkError(caught)) return true;
      // Refused: nothing was made, so a new try may use a new id.
      if (caught instanceof ApiError && caught.status >= 400 && caught.status < 500) pendingRevisionRequests.current.delete(output.outputId);
      return false;
    } finally {
      releaseOutput(output.outputId);
    }
  }

  // 刷新 on step 3: the same single sync as entering the step. New pins show
  // first with a 新 badge; otherwise the references come in a new order.
  // Chosen references stay chosen.
  function refreshReferences() {
    if (referenceSyncInFlight.current) return;
    const before = boardSnapshot;
    const run = ++referenceRefreshRun.current;
    void syncReferences().then((result) => {
      if (referenceRefreshRun.current !== run) return;
      const outcome = referenceRefreshOutcome(before, result);
      if (outcome.order !== undefined) setReferenceShuffle(outcome.order);
      if (result.ok) setNewReferencePins(outcome.newPinIds.length > 0 && result.snapshot ? { snapshotId: result.snapshot.snapshotId, pinIds: outcome.newPinIds } : null);
      notify(outcome.message);
      if (result.ok && currentPhase.current === "references") window.scrollTo({ top: 0, behavior: "smooth" });
    });
  }

  // A board thumbnail that showed is on the site now: one fewer on its way.
  function referenceThumbLoaded(reference: PinterestBoardReference) {
    if (reference.cached !== false) return;
    setArrivedThumbPins((current) => current.has(reference.pinId) ? current : new Set(current).add(reference.pinId));
  }

  // A thumbnail that would not load: the grid shows the bigger preview instead.
  function referenceThumbFailed(pinId: string) {
    setFailedThumbPins((current) => current.has(pinId) ? current : new Set(current).add(pinId));
  }

  function toggleReference(pinId: string) {
    // Functional update so quick successive taps never read a stale selection.
    setSelectedPinIds((current) => {
      if (current.includes(pinId)) return current.filter((id) => id !== pinId);
      if (current.length >= MAX_REFERENCES_PER_RUN) return current;
      return [...current, pinId];
    });
    if (!selectedPinIds.includes(pinId) && selectedPinIds.length >= MAX_REFERENCES_PER_RUN) {
      notify(`一次最多选 ${MAX_REFERENCES_PER_RUN} 张参考图`);
    }
  }

  // Each selected reference × each selected product becomes its own queue
  // item: one product per image, never several products in one picture. A
  // product's colours take turns over the references (shown before 生成).
  function generateSelectedReference() {
    if (selectedReferences.length === 0) {
      notify("先选参考图");
      return;
    }
    const products = chosenProducts
      .map((product) => ({ ...product, skuIds: product.skuIds.filter((productId) => productById.has(productId)) }))
      .filter((product) => product.skuIds.length > 0);
    if (!project || products.length === 0) {
      notify("先选商品");
      return;
    }
    const plan = sceneVariantPlan(products, selectedReferences);
    if (plan.length > MAX_JOBS_PER_RUN) {
      notify(`一次最多生成 ${MAX_JOBS_PER_RUN} 张，请少选几张`);
      return;
    }
    const toStart: VisualQueueItem[] = [];
    const added: VisualQueueItem[] = [];
    const superseded = new Set<string>();
    // Pin × product pairs already queued or running on the generator, also ones
    // started before a refresh, in another browser or on another device.
    const runningPairs = new Set((currentGenerationSnapshot?.jobs ?? [])
      .filter((job) => (!job.jobType || job.jobType === "scene") && isActiveJob(job))
      .map((job) => `${pinIdFromReferenceId(job.referenceId) ?? ""}|${productIdsKey(job.productIds)}`));
    for (const { reference, productId } of plan) {
      if (runningPairs.has(`${reference.pinId}|${productId}`)) continue;
      const sameGroup = projectQueue.filter((item) => item.pinId === reference.pinId && productIdsKey(item.productIds) === productId);
      if (sameGroup.some((item) => startingQueueIds.has(item.id) || isActiveJob(generationJobByQueueId.get(item.id)))) continue;
      // A failed or never-started item is retried only while it still points at the
      // same board snapshot; after a sync the pin gets a new referenceId, and the
      // server ties a queueItemId to its first referenceId. Without a loaded
      // snapshot a "completed" item cannot be told apart, so start a new one.
      const unfinished = currentGenerationSnapshot
        ? sameGroup.filter((item) => generationJobByQueueId.get(item.id)?.status !== "completed")
        : [];
      const retryable = unfinished.find((item) => item.referenceId === reference.referenceId);
      if (retryable) {
        toStart.push(retryable);
        continue;
      }
      for (const item of unfinished) superseded.add(item.id);
      const queueItem: VisualQueueItem = {
        id: `local-${randomUuid()}`,
        projectId: project.id,
        referenceId: reference.referenceId,
        pinId: reference.pinId,
        // What the grid showed (already in the browser): the queue card needs no new download.
        referenceImageUrl: referenceGridSrc(reference, failedThumbPins),
        productIds: [productId],
        createdAt: new Date().toISOString(),
      };
      added.push(queueItem);
      toStart.push(queueItem);
    }
    if (added.length > 0) updateQueue((current) => [...current.filter((item) => !superseded.has(item.id)), ...added]);
    setPhase("creative");
    setSelectedPinIds([]);
    if (toStart.length === 0) {
      notify("这组正在生成");
      return;
    }
    // One request at a time keeps the slow link clear and the queue in the order shown.
    void (async () => {
      for (const queueItem of toStart) await startGeneration(queueItem);
    })();
  }

  async function startGeneration(item: VisualQueueItem) {
    if (!project) return;
    setStartingQueueIds((current) => new Set(current).add(item.id));
    try {
      const result = await api<{ snapshot: VisualGenerationSnapshot }>("/api/generation-jobs", {
        method: "POST",
        ...jsonBody({
          queueItemId: item.id,
          projectId: project.id,
          referenceId: item.referenceId,
          productIds: item.productIds,
          retry: generationJobByQueueId.get(item.id)?.status === "failed",
        }),
        timeoutMs: GENERATION_POST_TIMEOUT_MS,
      });
      applyGenerationSnapshot(result.snapshot);
      setGenerationUnavailable(false);
      onQueueChanged();
      notify("开始生成");
    } catch (caught) {
      notify(generationErrorMessage(caught));
    } finally {
      setStartingQueueIds((current) => {
        const next = new Set(current);
        next.delete(item.id);
        return next;
      });
    }
  }

  function removeQueueItem(id: string) {
    updateQueue((current) => current.filter((item) => item.id !== id));
  }

  if (!project && loadFailed) return <div className="state-panel" data-testid="visual-workbench"><strong>做图暂时打不开</strong><button className="button soft" onClick={() => void load()}>重试</button></div>;
  if (!project) return <div className="state-panel" data-testid="visual-workbench"><strong>加载中…</strong></div>;

  const phaseTabs: Array<{ id: VisualPhase; label: string }> = [
    { id: "truth", label: "1 选商品" },
    { id: "product-images", label: "2 商品图" },
    { id: "references", label: "3 选参考" },
    { id: "creative", label: "4 生成" },
    { id: "listing", label: "5 宝贝" },
  ];
  const eligibleCatalogProducts = catalogProductGroups.filter((group) => eligibleSkuIds(group).length > 0);
  const referenceSyncLabel = referenceSync.status === "syncing"
    ? "正在同步…"
    : referenceSync.status === "retrying"
      ? "网络波动，正在恢复…"
      : referenceSync.status === "waiting"
        ? "网络波动，稍后自动更新"
        : referenceSync.status === "failed"
          ? "参考图暂时打不开"
          : referenceSync.status === "synced" && referenceSync.syncedAt ? `已同步 ${formatClock(referenceSync.syncedAt)}` : "";
  const referenceSyncing = referenceSync.status === "syncing" || referenceSync.status === "retrying";

  // Step 2: the product-image board comes from the generator's own records,
  // so finished, running and failed images all show again after a refresh.
  const productImageCards = productImageCardsFor(selectedProductIds, productById, catalogProductGroups);
  // null until the generator's records for this theme have arrived.
  const knownGenerationJobs = currentGenerationSnapshot?.jobs ?? null;
  // Cancelled jobs count as never asked for: their images show 缺 again.
  const countedGenerationJobs = knownGenerationJobs ? withoutCancelledJobs(knownGenerationJobs, cancelledJobIds) : null;
  const generationJobs = countedGenerationJobs ?? [];
  const outputByJobId = new Map((currentGenerationSnapshot?.outputs ?? []).map((output) => [output.jobId, output]));
  const productImageSlotList = productImageSlotsFor(productImageCards);
  const productImageViews = new Map(productImageSlotList
    .map((slot) => [slot.key, productImageSlotView(slot.key, countedGenerationJobs, outputByJobId, productImageRequests[slot.key])] as const));
  // 不做这张 slots leave 已完成 x/y.
  const productImageTotal = [...productImageViews].filter(([key, view]) => !slotShowsSkipped(view, skippedSlots.has(key))).length;
  const productImageDone = [...productImageViews.values()]
    .filter((view) => view.state === "done" && view.output.reviewDecision !== "reject").length;
  // The plan: what 生成这些商品图 would ask for (null until the records arrive); never a 不做这张 slot.
  const productImagePlanSlots = currentGenerationSnapshot && countedGenerationJobs
    ? productImagePlan(productImageSlotList, { jobs: countedGenerationJobs, outputs: currentGenerationSnapshot.outputs }, productImageRequests, skippedSlots)
    : null;
  const productImagePlanCount = productImagePlanSlots?.length ?? 0;
  const showProductImagePlan = hydrated && productImageCards.length > 0 && productImagePlanCount > 0;
  // 主题: this theme's products with images queued or running.
  const themeActivity = themeActivityText(generationQueue, selectedCollectionId || projectCollectionId(project));

  // Step 3: which colour each chosen reference gets, per product, shown before 生成.
  const referenceOrder = new Map(selectedReferences.map((reference, index) => [reference.pinId, index + 1]));
  const referencePairing = selectedReferences.length === 0 ? [] : chosenProducts.flatMap((product) => {
    const group = catalogProductGroups.find((candidate) => candidate.productId === product.catalogProductId);
    const lines = group ? referencePairingLines(product.skuIds.map((skuId) => visualVariantName(group, skuId)), selectedReferences.length) : [];
    return group && lines.length > 0 ? [{ catalogProductId: product.catalogProductId, label: group.label, lines }] : [];
  });

  // Step 4: 队列 comes from the generator's records first, so running work
  // still shows after a refresh, in another browser or on another device.
  const localQueueIds = new Set(projectQueue.map((item) => item.id));
  const queueById = new Map(queue.map((item) => [item.id, item]));
  const pendingQueue = visualPendingQueue(knownGenerationJobs, projectQueue, startingQueueIds, cancelledJobIds);
  const generationJobById = new Map(generationJobs.map((job) => [job.jobId, job]));
  // Newest first; a version redone with a note sits right after the image it came from.
  // One card per image; its versions (修改 makes a new one) are picked with 第 N 版.
  const generatedGroups = outputVersionGroups((currentGenerationSnapshot?.outputs ?? [])
    .filter((output) => !output.jobType || output.jobType === "scene"));
  const resultCount = generatedGroups.length + project.creativeCandidates.length;

  function productLabel(productIds: readonly string[]): string {
    return productIds.map((productId) => productById.get(productId)?.label).filter(Boolean).join(" + ") || "场景图";
  }

  function referenceImages(queueItemId: string, referenceId: string | null): { thumb: string; full: string } | null {
    const pinId = pinIdFromReferenceId(referenceId);
    const reference = pinId ? referenceByPinId.get(pinId) : undefined;
    const thumb = queueById.get(queueItemId)?.referenceImageUrl ?? (reference ? referenceGridSrc(reference, failedThumbPins) : undefined);
    if (!thumb) return null;
    return { thumb, full: reference?.fullImageUrl ?? thumb };
  }

  // One 商品图 slot: its image with 保留 / 修改 / 不用 (older versions one tap
  // away), or where its job is; 重做 / 生成 only when there is no image.
  function renderProductImageSlot(slot: ProductImageSlot, testId: string, label: string) {
    const view = productImageViews.get(slot.key) ?? { state: "idle" as const };
    const versions = view.state === "done" ? view.versions : [];
    // An older version picked with 上一版, else the newest one not marked 不用.
    const output = view.state === "done"
      ? versions.find((version) => version.outputId === productImageVersionChoice[slot.key]) ?? view.output
      : null;
    const next = view.state === "done" ? view.next : null;
    const job = view.state === "queued" || view.state === "running" ? view.job : null;
    // 第 N 位 in the one queue of every theme.
    const position = job?.status === "queued" ? queuePositionByJobId.get(job.jobId) : undefined;
    // 不做这张: 不做 with 恢复. Otherwise a failed image says why, with 重做 / 上传照片 / 不做这张.
    const skipped = slotShowsSkipped(view, skippedSlots.has(slot.key));
    const failure = view.state === "failed" && !skipped ? slotFailureKind(view.job) : null;
    const submissionError = view.state === "rejected" ? view.error : null;
    const upload = photoUploads[slot.key];
    return <div className={`product-image-slot ${skipped ? "skipped" : view.state}`} key={slot.key} data-testid={testId}>
      <div className="product-image-slot-head">
        <span className="product-image-slot-label">{label}</span>
      </div>
      {output ? <>
        <div className="product-image-frame">
          <button type="button" className={`product-image-thumb ${output.reviewDecision === "reject" ? "rejected" : ""}`} aria-label={`看${label}`} onClick={() => setPreview({ imageUrl: output.imageUrl, placeholderUrl: outputPreviewUrl(output.imageUrl), label })}><RetryImage src={outputPreviewUrl(output.imageUrl)} />
            {next && <span className={`product-image-next ${next.status}`}>{revisionStatusLabel(next, queuePositionByJobId.get(next.jobId))}</span>}
            {output.fallback === "source_upscale" && <span className="product-image-fallback" data-testid={`${testId}-fallback`}>原图放大</span>}
          </button>
          <VersionPicker
            versions={versions}
            currentId={output.outputId}
            testId={`${testId}-version`}
            onPick={(outputId) => setProductImageVersionChoice((current) => ({ ...current, [slot.key]: outputId }))}
          />
        </div>
        {output.fallback === "source_upscale" && <div className="slot-fallback-note">
          <small>{SOURCE_UPSCALE_NOTE}</small>
          <SameStyleLinks text={slotSearchText(slot)} testId={`${testId}-same-style`} />
        </div>}
        <VisualReviewActions
          key={output.outputId}
          testId={`${testId}-review`}
          label={`${label}怎么处理`}
          compact
          value={output.reviewDecision ?? "undecided"}
          note={output.reviewNote ?? ""}
          busy={Boolean(busyOutputs[output.outputId])}
          saving={busyOutputs[output.outputId] === "revise"}
          onChange={(value) => void reviewOutput(output, value)}
          onSave={async (note) => {
            const sent = await reviseOutput(output, note);
            // Show the new version when it arrives, not the one picked before.
            if (sent) setProductImageVersionChoice((current) => Object.fromEntries(Object.entries(current).filter(([key]) => key !== slot.key)));
            return sent;
          }}
        />
      </> : <div className="product-image-placeholder">
        {job?.status === "running"
          ? <span className="generation-activity"><i aria-hidden="true" />{(job.stage && generationStageLabels[job.stage]) || "生成中"}</span>
          : upload
            ? <SlotUploadProgress state={upload} />
            : skipped
              ? <small>不做这张</small>
              : submissionError
                ? <><small data-testid={`${testId}-submission`}>提交未成功</small><span className="slot-fix-hint">{submissionError.message}</span></>
              : failure
                ? <><small data-testid={`${testId}-reason`}>{slotFailureTexts[failure]}</small>{slotFailureHints[failure] && <span className="slot-fix-hint">{slotFailureHints[failure]}</span>}</>
                : <small>{view.state === "idle" ? "缺" : view.state === "loading" ? "加载中…" : position ? `排队中 · 第 ${position} 位` : "排队中"}</small>}
        {skipped
          ? <button type="button" className="button soft" data-testid={`${testId}-restore`} disabled={Boolean(skipBusy[slot.key])} aria-busy={Boolean(skipBusy[slot.key])} onClick={() => void setSlotSkipped(slot, false)}>恢复</button>
          : view.state === "idle" && <button type="button" className="button soft" data-testid={`${testId}-redo`} onClick={() => redoProductImage(slot)}>生成</button>}
      </div>}
      {failure && !upload && <SlotFixActions
        testId={`${testId}-fix`}
        kind={failure}
        busy={Boolean(skipBusy[slot.key])}
        upscaling={Boolean(upscalingSlots[slot.key])}
        searchText={slotSearchText(slot)}
        onRedo={() => redoProductImage(slot)}
        onUpload={(file) => void uploadSlotPhoto(slot, file)}
        onUpscale={() => void upscaleSlot(slot)}
        onSkip={() => void setSlotSkipped(slot, true)}
      />}
      {submissionError && <SlotSubmissionActions
        testId={`${testId}-submission-actions`}
        error={submissionError}
        onRetry={() => redoProductImage(slot)}
      />}
    </div>;
  }

  return (
    <section className="visual-workbench" data-testid="visual-workbench">
      <div className="visual-theme-row">
        <label htmlFor="visual-theme-select">主题</label>
        {/* 我入选的 (from the 选品 page) first, then the themes: either can be picked. */}
        <select id="visual-theme-select" data-testid="visual-theme-select" value={selectedCollectionId || projectCollectionId(project)} onChange={(event) => chooseProject(event.target.value)}>
          {pickedProjects.length > 0 && <optgroup label="我选的">{pickedProjects.map((item) => { const collectionId = projectCollectionId(item); return <option key={collectionId} value={collectionId} disabled={(item.catalogProducts?.length ?? 0) === 0 && collectionId !== selectedCollectionId} data-testid="visual-theme-option-picks">{pickedOptionLabel(item, themeActiveCounts.get(collectionId) ?? 0)}</option>; })}</optgroup>}
          <optgroup label="主题">{themeProjects.map((item) => { const collectionId = projectCollectionId(item); return <option key={collectionId} value={collectionId}>{themeOptionLabel(item.title, themeActiveCounts.get(collectionId) ?? 0)}</option>; })}</optgroup>
        </select>
        {themeActivity && <small className="visual-theme-activity" data-testid="theme-activity" title={themeActivity}><i aria-hidden="true" />{themeActivity}</small>}
      </div>

      <div className="visual-phase-tabs" role="tablist" aria-label="步骤">
        {phaseTabs.map((tab) => <button key={tab.id} id={`visual-tab-${tab.id}`} type="button" role="tab" aria-controls="visual-step-panel" data-testid={`visual-phase-${tab.id}`} aria-selected={phase === tab.id} className={phase === tab.id ? "active" : ""} onClick={() => goToPhase(tab.id)}>{tab.label}</button>)}
      </div>

      <div className="visual-step" id="visual-step-panel" role="tabpanel" aria-labelledby={`visual-tab-${phase}`}>
        {phase === "truth" && <>
          <div className="visual-step-toolbar">
            <span>已选 {selectedProductCount}/{eligibleCatalogProducts.length}</span>
            <div className="composer-selection-actions"><button type="button" onClick={selectAllCatalogProducts}>全选</button><button type="button" onClick={() => setSelectedProductIds([])}>清空</button></div>
            <button type="button" data-testid="skip-product-images-first" className="button ghost composer-skip" disabled={selectedProductIds.length === 0} onClick={() => goToPhase("references")}>跳过商品图</button>
            <button type="button" data-testid="start-product-images" className="button primary composer-next" disabled={selectedProductIds.length === 0} onClick={() => goToPhase("product-images")}>下一步</button>
          </div>
          {catalogProductGroups.length > 0 ? <div className="composer-products">{catalogProductGroups.map((group) => {
            const validSkuIds = eligibleSkuIds(group);
            // The first colour chosen is the 主色.
            const chosen = chosenVariantsOf(group);
            const selected = chosen.length > 0;
            const mainSkuId = chosen[0] ?? rememberedVariantsOf(group)[0] ?? "";
            const imageUrl = group.sourceImage?.url ?? productById.get(mainSkuId)?.imageUrl ?? null;
            const allColours = allColourVariantIds(group, productById);
            const allChosen = allColours.length > 1 && allColoursChosen(group, chosen);
            return <article key={group.productId} data-testid={`truth-product-${group.productId}`} className={`${selected ? "selected" : ""} ${validSkuIds.length === 0 ? "blocked" : ""}`}>
              <label className="composer-product-toggle">
                <input type="checkbox" data-testid={`truth-product-check-${group.productId}`} checked={selected} disabled={validSkuIds.length === 0} onChange={() => toggleCatalogProduct(group)} />
                <ProductImage src={imageUrl} alt="" />
                <span><strong>{group.label}</strong>{validSkuIds.length === 0 && <small>{group.status === "source_missing" ? "缺原图" : "缺规格"}</small>}</span>
              </label>
              {group.skus.length > 0 && <div className="composer-variants" role="group" aria-label={`${group.label}的颜色`} data-testid={`truth-variants-${group.productId}`}>
                {group.skus.map((sku) => {
                  const usable = validSkuIds.includes(sku.id);
                  const order = chosen.indexOf(sku.id);
                  // Stock 0 in the 货盘: still the owner's pick, only tagged.
                  const soldOut = palletSoldOut(sku.stock);
                  // A colour that cannot be picked says why.
                  const missing = usable ? "" : !sku.sourceSkuId || productById.get(sku.id)?.status === "source_sku_missing" ? "缺规格" : "缺原图";
                  return <button type="button" key={sku.id} className={`composer-variant${order !== -1 ? " on" : ""}${order === 0 ? " main" : ""}${soldOut ? " sold-out" : ""}`} aria-pressed={order !== -1} disabled={!usable} title={visualSkuLabel(group, sku.id)} data-testid={`truth-variant-${sku.id}`} onClick={() => toggleCatalogVariant(group, sku.id)}>
                    <span>{visualVariantName(group, sku.id)}</span>
                    {order === 0 && <b>主色</b>}
                    {missing ? <small>{missing}</small> : soldOut && <small className="composer-variant-stock" title={PALLET_STOCK_NOTE} data-testid={`truth-variant-soldout-${sku.id}`}>{PALLET_SOLD_OUT_TEXT}</small>}
                  </button>;
                })}
              </div>}
              {allColours.length > 1 && <button type="button" className={`composer-all-colours${allChosen ? " on" : ""}`} aria-pressed={allChosen} data-testid={`truth-all-colours-${group.productId}`} onClick={() => toggleAllColours(group)}>全部颜色</button>}
            </article>;
          })}</div> : <div className="reference-empty"><strong>暂无商品</strong></div>}
        </>}

        {phase === "product-images" && <>
          <div className="visual-step-toolbar" data-testid="product-image-toolbar">
            <span aria-live="polite">已完成 {productImageDone}/{productImageTotal}</span>
            {/* While images are missing, the plan below offers 生成 or 跳过 instead. */}
            {!showProductImagePlan && <button type="button" data-testid="start-pinterest-selection" className="button primary composer-next" disabled={selectedProductIds.length === 0} onClick={() => goToPhase("references")}>下一步</button>}
          </div>
          {generationUnavailable && <small className="generation-service-error" role="alert">生成服务暂时不可用，请稍后再试</small>}
          {showProductImagePlan && productImagePlanSlots && <section className="product-image-plan" data-testid="product-image-plan" aria-label="要做的商品图">
            <div className="product-image-plan-copy">
              <strong>还差 {productImagePlanCount} 张商品图</strong>
              <small>{productImagePlanText(productImagePlanSlots)}</small>
            </div>
            <div className="product-image-plan-actions">
              <button type="button" data-testid="generate-product-images" className="button primary" disabled={sendingProductImages} aria-busy={sendingProductImages} onClick={() => void ensureProductImages(selectedProductIds)}>{sendingProductImages ? "提交中…" : `生成这些商品图（${productImagePlanCount} 张）`}</button>
              <button type="button" data-testid="skip-product-images" className="button soft" onClick={() => goToPhase("references")}>跳过，去选参考</button>
            </div>
          </section>}
          {!hydrated
            ? <div className="reference-empty"><strong>加载中…</strong></div>
            : productImageCards.length === 0
              ? <div className="reference-empty"><strong>先选商品</strong><button type="button" className="button soft" onClick={() => goToPhase("truth")}>选商品</button></div>
              : <div className="product-image-board" data-testid="product-image-board">{productImageCards.map((card) => <article className="product-image-card" key={card.productId} data-testid={`product-image-card-${card.catalogProductId}`}>
                <header className="product-image-card-head">
                  <ProductImage src={card.imageUrl} alt="" />
                  <div><strong>{card.label}</strong><small>{card.variantSlots.length > 0 ? `主色 ${card.variantLabel} · ${card.variantSlots.length + 1} 个颜色` : card.variantLabel}</small></div>
                </header>
                <div className="product-image-slots">{productImageSlotDefinitions.map((definition, index) => {
                  const slot = card.slots[index];
                  const testId = `product-image-slot-${card.catalogProductId}-${definition.jobType}`;
                  if (!slot) {
                    return <div className="product-image-slot na" key={definition.jobType} data-testid={testId}>
                      <span className="product-image-slot-label">{definition.label}</span>
                      <div className="product-image-placeholder"><small>只有一个规格</small></div>
                    </div>;
                  }
                  // With several colours, the 主色's 白底图 says which colour it is.
                  const label = slot.variantLabel && card.variantSlots.length > 0 ? `${definition.label} · ${slot.variantLabel}` : definition.label;
                  return renderProductImageSlot(slot, testId, label);
                })}</div>
                {card.variantSlots.length > 0 && <section className="product-image-variants" aria-label="其他颜色的白底图" data-testid={`product-image-variants-${card.catalogProductId}`}>
                  <span className="product-image-variants-title">其他颜色 · 白底图</span>
                  <div className="product-image-slots">{card.variantSlots.map((slot) => renderProductImageSlot(slot, `product-image-slot-${card.catalogProductId}-truth_white-${slot.sourceSkuId}`, slot.variantLabel ?? "白底图"))}</div>
                </section>}
              </article>)}</div>}
        </>}

        {phase === "references" && <>
          <div className="reference-toolbar" data-testid="reference-generation-bar">
            <div className="visual-filter-tabs" role="group" aria-label="类型">
              {referenceFilters.map(([value, label]) => <button type="button" key={value} className={referenceShotType === value ? "active" : ""} aria-pressed={referenceShotType === value} onClick={() => setReferenceShotType(value)}>{label}</button>)}
            </div>
            <div className={`reference-sync ${referenceSync.status}`}>
              <span aria-live="polite">{referenceSyncLabel}</span>
            </div>
            <label className="reference-search"><span aria-hidden="true">⌕</span><input type="search" value={referenceQuery} onChange={(event) => setReferenceQuery(event.target.value)} placeholder="搜索" aria-label="搜索参考图" /></label>
            <button type="button" className="button soft reference-refresh" data-testid="refresh-references" disabled={referenceSyncing} aria-busy={referenceSyncing} onClick={refreshReferences}>{referenceSync.status === "retrying" ? "恢复中…" : referenceSyncing ? "同步中…" : <><span aria-hidden="true">↻</span>刷新</>}</button>
            {selectedReferences.length > 0 && <span className="reference-selected-count" aria-live="polite">{selectedProductCount} 款 × {selectedReferences.length} 张参考 · 生成 {plannedImageCount} 张</span>}
            <button type="button" className="button primary reference-generate" data-testid="generate-selected-reference" disabled={selectedReferences.length === 0 || selectedProductIds.length === 0} onClick={generateSelectedReference}>生成</button>
          </div>
          {thumbsOnTheWay > 0 && <small className="reference-thumb-note" data-testid="reference-thumb-note">还有 {thumbsOnTheWay} 张缩略图在路上</small>}
          {referencePairing.length > 0 && <section className="reference-pairing" aria-label="参考图配哪个颜色" data-testid="reference-pairing">
            {referencePairing.map((row) => <div className="reference-pairing-row" key={row.catalogProductId} data-testid={`reference-pairing-${row.catalogProductId}`}>
              <strong>{row.label}</strong>
              <ol>{row.lines.map((line) => <li key={line}>{line}</li>)}</ol>
            </div>)}
          </section>}
          {boardReferences.length === 0
            ? <div className="reference-empty"><strong>{referenceSyncing ? "加载中…" : "暂无参考图"}</strong></div>
            : visibleReferences.length === 0
              ? <div className="reference-empty"><strong>没有结果</strong></div>
              : <div className="reference-card-grid">{visibleReferences.map((reference) => {
                const selected = selectedPinIds.includes(reference.pinId);
                // 参考 N, as the colour list above counts them.
                const order = referenceOrder.get(reference.pinId);
                const previewSize = referencePreviewSize(reference);
                // The small thumbnail when there is one; the preview if it would not load.
                const gridSrc = referenceGridSrc(reference, failedThumbPins);
                const showsThumb = gridSrc !== reference.imageUrl;
                return <article className={`reference-card ${selected ? "selected" : ""}`} key={reference.pinId}>
                  <button type="button" className="reference-image" aria-label="看大图" onClick={() => setPreview({ imageUrl: reference.fullImageUrl, placeholderUrl: gridSrc, label: "参考图" })}><img key={gridSrc} src={gridSrc} alt="" loading="lazy" decoding="async" width={previewSize?.width} height={previewSize?.height} onLoad={showsThumb ? () => referenceThumbLoaded(reference) : undefined} onError={showsThumb ? () => referenceThumbFailed(reference.pinId) : undefined} /><span>{visualShotTypeLabels[reference.shotType]}</span></button>
                  {newPinIds.has(reference.pinId) && <span className="reference-new" data-testid="reference-new-badge">新</span>}
                  <button type="button" className={`button reference-pick ${selected ? "primary" : "soft"}`} aria-pressed={selected} onClick={() => toggleReference(reference.pinId)}>{selected ? order ? `已选 · 参考 ${order}` : "已选" : "选择"}</button>
                </article>;
              })}</div>}
        </>}

        {phase === "creative" && <>
          {generationUnavailable && <small className="generation-service-error" role="alert">生成服务暂时不可用，请稍后再试</small>}
          <section className="local-queue" data-testid="generation-queue">
            <header><h3>队列</h3><span>{pendingQueue.length}</span></header>
            {pendingQueue.length > 0 ? <div className="queue-grid">{pendingQueue.map((item) => {
              const job = generationJobByQueueId.get(item.id);
              const starting = startingQueueIds.has(item.id);
              const busy = starting || isActiveJob(job);
              const position = job?.status === "queued" ? queuePositionByJobId.get(job.jobId) : undefined;
              const statusLabel = position ? `排队中 · 第 ${position} 位` : queueStatusLabel(job, starting);
              // The one queue's entry: its #N badge, its preview when the board has no reference, its labels for 取消.
              const entry = job ? queueEntryByJobId.get(job.jobId) : undefined;
              const thumb = referenceImages(item.id, item.referenceId)?.thumb ?? (entry ? queueEntryThumb(entry) : null);
              const label = productLabel(item.productIds);
              return <article className={`queue-card ${job?.status ?? ""}`} key={item.id} data-testid={`queue-card-${item.id}`}>
                <span className="queue-card-thumb">
                  {thumb ? <RetryImage src={thumb} /> : <span className="queue-thumb" aria-hidden="true">🐾</span>}
                  {entry?.position ? <b>#{entry.position}</b> : null}
                </span>
                <div><strong>{label}</strong>{busy ? <span className="generation-activity"><i aria-hidden="true" />{statusLabel}</span> : <small>{statusLabel}</small>}</div>
                {job && isActiveJob(job) && !starting && <div className="queue-actions"><button type="button" className="button ghost queue-cancel" data-testid={`cancel-generation-${item.id}`} onClick={() => onCancelJob({ jobId: job.jobId, productLabel: entry?.productLabel || label, kindLabel: entry?.kindLabel || "场景图" })}>取消</button></div>}
                {!busy && localQueueIds.has(item.id) && <div className="queue-actions"><button type="button" className="button primary" data-testid={`confirm-generation-${item.id}`} onClick={() => void startGeneration(item)}>{job?.status === "failed" ? "重试" : "生成"}</button><button type="button" className="button ghost" onClick={() => removeQueueItem(item.id)}>移除</button></div>}
              </article>;
            })}</div> : !knownGenerationJobs && !generationUnavailable
              ? <div className="queue-empty"><strong>加载中…</strong></div>
              : <div className="queue-empty"><strong>暂无任务</strong><button type="button" className="button soft" onClick={() => goToPhase(selectedProductIds.length > 0 ? "references" : "truth")}>{selectedProductIds.length > 0 ? "选参考" : "选商品"}</button></div>}
          </section>
          <section className="generated-results" data-testid="generated-results">
            <header><h3>成图</h3><span>{resultCount}</span></header>
            {resultCount > 0 ? <div className="creative-card-grid">
              {generatedGroups.map(({ rootId, versions }) => {
                const output = shownVersion(versions, sceneVersionChoice[rootId]);
                const job = generationJobById.get(output.jobId);
                const reference = referenceImages(output.queueItemId, output.referenceId ?? job?.referenceId ?? null);
                const label = productLabel(output.productIds?.length ? output.productIds : job?.productIds ?? []);
                const feedback = sceneOutputFeedback(output);
                // A version on its way for any version of this image shows on the card.
                const redo = versions.map((version) => pendingRevisionJob(generationJobs, version.outputId, sentRevisions)).find(Boolean) ?? null;
                return <article className="creative-card" key={rootId} data-testid={`generated-card-${output.outputId}`}>
                  <div className={`creative-compare ${reference ? "" : "single"}`}>
                    {reference && <><button type="button" aria-label="看参考图" onClick={() => setPreview({ imageUrl: reference.full, placeholderUrl: reference.thumb, label: "参考图" })}><RetryImage src={reference.thumb} /><span>参考图</span></button><i aria-hidden="true">→</i></>}
                    <button type="button" aria-label="看成图" onClick={() => setPreview({ imageUrl: output.imageUrl, placeholderUrl: outputPreviewUrl(output.imageUrl), label })}><RetryImage src={outputPreviewUrl(output.imageUrl)} />{redo && <span className={`creative-next ${redo.status}`}>{revisionStatusLabel(redo, queuePositionByJobId.get(redo.jobId))}</span>}</button>
                    <VersionPicker
                      versions={versions}
                      currentId={output.outputId}
                      align="right"
                      testId={`generated-version-${rootId}`}
                      onPick={(outputId) => setSceneVersionChoice((current) => ({ ...current, [rootId]: outputId }))}
                    />
                  </div>
                  <div className="creative-title-row">
                    <strong className="creative-title">{label}</strong>
                  </div>
                  <VisualReviewActions
                    testId={`generated-feedback-${output.outputId}`}
                    value={feedback.status}
                    note={feedback.note}
                    busy={Boolean(busyOutputs[output.outputId])}
                    saving={busyOutputs[output.outputId] === "revise"}
                    onChange={(value) => void reviewSceneOutput(output, value)}
                    onSave={async (note) => {
                      const sent = await reviseOutput(output, note, { cache: true });
                      // Show the new version when it arrives, not the one picked before.
                      if (sent) setSceneVersionChoice((current) => Object.fromEntries(Object.entries(current).filter(([key]) => key !== rootId)));
                      return sent;
                    }}
                  />
                </article>;
              })}
              {project.creativeCandidates.map((candidate) => {
                const feedback = generatedFeedbackValue(candidate.id);
                const label = candidate.label.split(" · ")[0] || "场景图";
                return <article className="creative-card" key={candidate.id} data-testid={`generated-card-${candidate.id}`}>
                  <div className="creative-compare">
                    <button type="button" aria-label="看参考图" onClick={() => setPreview({ imageUrl: candidate.referenceImageUrl, label: "参考图" })}><img src={candidate.referenceImageUrl} alt="" loading="lazy" decoding="async" /><span>参考图</span></button>
                    <i aria-hidden="true">→</i>
                    <button type="button" aria-label="看成图" onClick={() => setPreview({ imageUrl: candidate.imageUrl, label })}><img src={candidate.imageUrl} alt="" loading="lazy" decoding="async" /></button>
                  </div>
                  <div className="creative-title-row"><strong className="creative-title">{label}</strong></div>
                  <VisualReviewActions
                    testId={`generated-feedback-${candidate.id}`}
                    value={feedback.status}
                    note={feedback.note}
                    onChange={(value) => cacheGeneratedFeedback(candidate.id, value)}
                    onSave={(note) => saveLegacyRevision(candidate.id, note)}
                  />
                </article>;
              })}
            </div> : <div className="generated-empty"><strong>{!knownGenerationJobs && !generationUnavailable ? "加载中…" : "暂无成图"}</strong></div>}
          </section>
        </>}

        {phase === "listing" && <ListingBoard
          projectId={project.id}
          groups={catalogProductGroups}
          outputs={currentGenerationSnapshot?.outputs ?? null}
          themeTitle={project.title}
          singleProductId={listingProductId}
          linkFor={listingLink}
          onOpenProduct={openListingProduct}
          onShowAll={showAllListing}
          outputsUnavailable={generationUnavailable}
          canExport={canExportListing}
          visit={listingVisit}
          notify={notify}
          onRefreshOutputs={refreshListingOutputs}
          onPreview={(imageUrl, label) => setPreview({ imageUrl, placeholderUrl: outputPreviewUrl(imageUrl), label })}
          onMakeImages={() => goToPhase("product-images")}
          jobs={countedGenerationJobs}
          queuePositions={queuePositionByJobId}
          usableSkuIds={usableVariantIds}
          requests={productImageRequests}
          busyOutputs={busyOutputs}
          mainSkuIdOf={(catalogProductId) => selectedSkuByCatalogProduct[catalogProductId]?.[0] ?? null}
          onReview={(output, choice) => void reviewOutput(output, choice)}
          onFill={fillListingImages}
          onMakeScenes={openSceneStep}
          onLoadProduct={loadListingProduct}
          slotFix={{
            skipped: skippedSlots,
            skipBusy,
            uploads: photoUploads,
            onRedo: redoProductImage,
            onUpload: (slot, file) => void uploadSlotPhoto(slot, file),
            onSkip: (slot, skipped) => void setSlotSkipped(slot, skipped),
            upscaling: upscalingSlots,
            onUpscale: (slot) => void upscaleSlot(slot),
            searchText: slotSearchText,
          }}
          ownerPhotos={{
            byProduct: ownerPhotos.projectId === project.id ? ownerPhotos.byProduct : {},
            previews: photoPreviews,
            deleting: deletingPhotos,
            onDelete: (photo) => void deleteOwnerPhoto(photo),
          }}
        />}
      </div>

      {preview && <div className="visual-preview-backdrop" role="dialog" aria-modal="true" aria-label={preview.label} onMouseDown={(event) => { if (event.target === event.currentTarget) setPreview(null); }}><div className="visual-preview"><button type="button" className="drawer-close" aria-label="关闭" onClick={() => setPreview(null)}>×</button><img key={preview.imageUrl} src={preview.imageUrl} alt={preview.label} style={previewPlaceholderStyle(preview.placeholderUrl)} onLoad={() => setPreview((current) => current?.imageUrl === preview.imageUrl ? { imageUrl: current.imageUrl, label: current.label } : current)} /></div></div>}
    </section>
  );
}

/**
 * 保留 / 修改 / 不用 under a finished image. 保留 and 不用 toggle at once;
 * 修改 opens the note field and 保存 hands the note on (the parent sends it and
 * says whether it went out, so the field closes only then).
 */
function VisualReviewActions({ testId, label = "这张图怎么处理", compact = false, value, note, busy = false, saving = false, onChange, onSave }: {
  testId: string;
  label?: string;
  compact?: boolean;
  value: VisualReviewStatus;
  note: string;
  busy?: boolean;
  saving?: boolean;
  onChange: (value: VisualReviewStatus) => void;
  onSave: (note: string) => boolean | Promise<boolean>;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(note);
  // A newer note from the generator fills the field, but never while typing.
  useEffect(() => { if (!editing) setDraft(note); }, [editing, note]);
  const actions: Array<{ id: Exclude<VisualReviewStatus, "undecided">; label: string }> = [
    { id: "keep", label: "保留" },
    { id: "revise", label: "修改" },
    { id: "reject", label: "不用" },
  ];
  function choose(action: Exclude<VisualReviewStatus, "undecided">) {
    if (action === "revise") {
      setEditing((open) => !open);
      return;
    }
    setEditing(false);
    onChange(value === action ? "undecided" : action);
  }
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (saving) return;
    if (await onSave(draft)) setEditing(false);
  }
  return <div className={`visual-feedback ${compact ? "compact" : ""}`} data-testid={testId}>
    <div className={`visual-review-actions ${compact ? "product-image-actions" : ""}`} role="group" aria-label={label}>
      {actions.map((action) => {
        const active = action.id === "revise" ? editing || value === "revise" : value === action.id;
        return <button type="button" key={action.id} data-testid={`${testId}-${action.id}`} className={active ? `active ${action.id}` : ""} aria-pressed={active} disabled={busy} onClick={() => choose(action.id)}>{action.label}</button>;
      })}
    </div>
    {editing && <form className="visual-revision-form" data-testid={`${testId}-form`} onSubmit={(event) => void submit(event)}>
      <input value={draft} maxLength={MAX_REVISION_NOTE_LENGTH} onChange={(event) => setDraft(event.target.value)} placeholder="要改什么（可不填）" aria-label="要改什么" data-testid={`${testId}-input`} disabled={saving} autoFocus enterKeyHint="send" />
      <button type="submit" data-testid={`${testId}-save`} disabled={saving} aria-busy={saving}>{saving ? "提交中…" : "保存"}</button>
    </form>}
    {!editing && value === "revise" && note && <small className="visual-feedback-note" data-testid={`${testId}-note`}>修改意见：{note}</small>}
  </div>;
}

// ---------------------------------------------------------------------------
// 5 宝贝: every image marked 保留, gathered per catalog product into what one
// Taobao link needs — 主图, a 规格图 per colour and the 详情 images.
// 导出到本地文件夹 copies that set into a new version folder on the Mac for
// 豆包 to upload from; prices, titles and other listing facts are not handled
// on this page, and nothing here ever publishes.
// ---------------------------------------------------------------------------

type ListingImageKind = "white" | "still" | "combo" | "detail" | "scene";

/** One image marked 保留 that may be exported for this product. */
interface ListingImage {
  outputId: string;
  kind: ListingImageKind;
  imageUrl: string;
  /** The variant of a 白底图 / 静物图 / 细节图; null for 组合图 and scenes. */
  sourceSkuId: string | null;
  /** This product's variants (sourceSkuId) the image shows. */
  skuIds: string[];
  /** 合拍: a scene that also shows other products of the theme. */
  together: boolean;
  createdAt: string;
}

const listingKindByJobType: Record<string, ListingImageKind> = {
  truth_white: "white",
  truth_neutral: "still",
  truth_combo: "combo",
  truth_detail: "detail",
  scene: "scene",
};

const listingKindNames: Record<ListingImageKind, string> = {
  white: "白底图",
  still: "模特图",
  combo: "组合图",
  detail: "细节图",
  scene: "场景图",
};

/**
 * Every finished image of one catalog product marked 保留, oldest first: its
 * 白底图 / 静物图 / 组合图 / 细节图, and every scene showing at least one of
 * its variants (those with a supplier variant id, as the exporter checks). A
 * scene that also shows other products is a 合拍: it shows in each of their
 * cards. Images not marked 保留 never show here.
 */
function keptListingImages(
  outputs: readonly Pick<VisualGenerationOutput, "outputId" | "jobType" | "imageUrl" | "productIds" | "catalogProductId" | "sourceSkuId" | "reviewDecision" | "createdAt">[],
  group: Pick<VisualCatalogProduct, "productId" | "skus">,
): ListingImage[] {
  const sourceSkuById = new Map(group.skus.flatMap((sku) => sku.sourceSkuId ? [[sku.id, sku.sourceSkuId] as const] : []));
  return outputs.flatMap((output): ListingImage[] => {
    if (output.reviewDecision !== "keep" || !output.outputId || !output.imageUrl) return [];
    const jobType = output.jobType ?? "scene";
    const kind = Object.prototype.hasOwnProperty.call(listingKindByJobType, jobType) ? listingKindByJobType[jobType] : undefined;
    if (!kind) return [];
    const productIds = output.productIds ?? [];
    if (kind === "scene") {
      if (!productIds.some((id) => sourceSkuById.has(id))) return [];
    } else if (output.catalogProductId !== group.productId) {
      return [];
    }
    const sourceSkuId = output.sourceSkuId ?? null;
    // Scenes and 组合图 show the variants they were made from; the others their own one.
    const skuIds = kind === "scene" || kind === "combo"
      ? [...new Set(productIds.flatMap((id) => sourceSkuById.get(id) ?? []))]
      : sourceSkuId ? [sourceSkuId] : [];
    const together = kind === "scene" && productIds.some((id) => !sourceSkuById.has(id));
    return [{ outputId: output.outputId, kind, imageUrl: output.imageUrl, sourceSkuId, skuIds, together, createdAt: output.createdAt ?? "" }];
  }).sort((left, right) => left.createdAt.localeCompare(right.createdAt));
}

/** A kept image's name on the page: 合拍 for a scene with other products. */
function listingImageName(image: Pick<ListingImage, "kind" | "together">): string {
  return image.together ? "合拍" : listingKindNames[image.kind];
}

/**
 * Shows no colour switched off (上架 off): only such images are picked
 * for 主图 and 详情 by themselves; the others stay on the card to add by hand.
 */
function showsOnlyListed(image: Pick<ListingImage, "skuIds">, listedSkuIds: readonly string[]): boolean {
  return image.skuIds.every((id) => listedSkuIds.includes(id));
}

/** The 组合图 to use: the newest showing only colours for sale; none when every one also shows a colour not for sale. */
function preferredListingCombo(images: readonly ListingImage[], listedSkuIds: readonly string[]): ListingImage | null {
  return images.filter((image) => image.kind === "combo").reverse().find((image) => showsOnlyListed(image, listedSkuIds)) ?? null;
}

/**
 * Every kept image 主图 takes by itself, in the order it takes them. First
 * the preferred picks: 1 场景图 (the 主色's newest scene of it alone, else the
 * newest scene of this product) → 2 白底图 → 3 细节图 (the 主色's newest, else
 * the first colour's that has one) → 4 组合图 → 5 静物图; a missing one leaves
 * no hole. Then, to fill up to 5: the other scenes of this product (newest
 * first) → the 白底图 of each colour not in yet → the other 细节图 → the other
 * 静物图 → 合拍 (newest first). `images` are those for sale, oldest first;
 * `listedSkuIds` the colours for sale in catalog order. An image also showing
 * a colour not for sale, an older 白底图 of a colour already in and other
 * 组合图 are never taken by themselves; the owner may add them by hand.
 */
function listingMainFillOrder(images: readonly ListingImage[], listedSkuIds: readonly string[], mainSourceSkuId: string | null): string[] {
  const forSale = images.filter((image) => showsOnlyListed(image, listedSkuIds)).reverse();
  const newestFirst = forSale.filter((image) => !image.together);
  const colours = mainSourceSkuId && listedSkuIds.includes(mainSourceSkuId)
    ? [mainSourceSkuId, ...listedSkuIds.filter((id) => id !== mainSourceSkuId)]
    : [...listedSkuIds];
  const main = colours[0] ?? null;
  const scenes = newestFirst.filter((image) => image.kind === "scene");
  const ofColour = (kind: ListingImageKind, sourceSkuId: string) => newestFirst.find((image) => image.kind === kind && image.sourceSkuId === sourceSkuId);
  const firstColour = (kind: ListingImageKind) => colours.map((id) => ofColour(kind, id)).find(Boolean) ?? null;
  const picked: ListingImage[] = [];
  const take = (image: ListingImage | null | undefined) => {
    if (image && !picked.includes(image)) picked.push(image);
  };
  // The preferred picks.
  take(scenes.find((image) => image.skuIds.length === 1 && image.skuIds[0] === main) ?? scenes[0]);
  take(firstColour("white"));
  take(firstColour("detail"));
  take(preferredListingCombo(images, listedSkuIds));
  take(firstColour("still") ?? newestFirst.find((image) => image.kind === "still"));
  // Then what fills up the empty places.
  scenes.forEach(take);
  const whiteColours = new Set(picked.flatMap((image) => image.kind === "white" && image.sourceSkuId ? [image.sourceSkuId] : []));
  colours.forEach((id) => {
    if (!whiteColours.has(id)) take(ofColour("white", id));
  });
  newestFirst.filter((image) => image.kind === "detail").forEach(take);
  newestFirst.filter((image) => image.kind === "still").forEach(take);
  forSale.filter((image) => image.together && image.kind === "scene").forEach(take);
  return picked.map((image) => image.outputId);
}

/** The default 主图: the first 5 that 主图 takes by itself, so no place stays empty while kept images are left. */
function defaultListingMainIds(images: readonly ListingImage[], listedSkuIds: readonly string[], mainSourceSkuId: string | null): string[] {
  return listingMainFillOrder(images, listedSkuIds, mainSourceSkuId).slice(0, LISTING_MAX_MAIN_IMAGES);
}

/**
 * 补满 5 张: the 主图 the owner arranged, as they are, then the images 主图
 * would take by itself that are not in yet, up to 5.
 */
function filledListingMainIds(mainIds: readonly string[], images: readonly ListingImage[], listedSkuIds: readonly string[], mainSourceSkuId: string | null): string[] {
  return [...new Set([...mainIds, ...listingMainFillOrder(images, listedSkuIds, mainSourceSkuId)])].slice(0, LISTING_MAX_MAIN_IMAGES);
}

/**
 * What a ＋ place offers: every kept image of this 宝贝 not in 主图 yet —
 * those 主图 would take by itself first (in that order), then the others
 * newest first.
 */
function listingMainChoices(images: readonly ListingImage[], mainIds: readonly string[], listedSkuIds: readonly string[], mainSourceSkuId: string | null): ListingImage[] {
  const used = new Set(mainIds);
  const byId = new Map(images.map((image) => [image.outputId, image]));
  const order = listingMainFillOrder(images, listedSkuIds, mainSourceSkuId);
  const ordered = new Set(order);
  return [
    ...order.flatMap((id) => byId.get(id) ?? []),
    ...[...images].reverse().filter((image) => !ordered.has(image.outputId)),
  ].filter((image) => !used.has(image.outputId));
}

/** A ＋ place's choice: the image goes into that place (after the last one when places before it are empty). */
function placeListingImage(mainIds: readonly string[], outputId: string, index: number): string[] {
  const next = mainIds.filter((id) => id !== outputId);
  next.splice(Math.max(0, Math.min(index, next.length)), 0, outputId);
  return next.slice(0, LISTING_MAX_MAIN_IMAGES);
}

/**
 * The 主图 in use: the arranged order (null = never arranged → the default),
 * kept to images still marked 保留 and for sale (细节图 and 合拍 included),
 * each once, at most 5. When none of the arranged images is left (all no
 * longer kept, or their colours switched off) the default comes back; an
 * order the owner emptied on purpose stays empty.
 */
function listingMainIds(arranged: readonly string[] | null, images: readonly ListingImage[], listedSkuIds: readonly string[], mainSourceSkuId: string | null): string[] {
  if (!arranged) return defaultListingMainIds(images, listedSkuIds, mainSourceSkuId);
  const usable = new Set(images.map((image) => image.outputId));
  const kept = [...new Set(arranged)].filter((id) => usable.has(id)).slice(0, LISTING_MAX_MAIN_IMAGES);
  return kept.length === 0 && arranged.length > 0 ? defaultListingMainIds(images, listedSkuIds, mainSourceSkuId) : kept;
}

/** ↑ / ↓ on a 主图. */
function moveListingImage(ids: readonly string[], id: string, delta: number): string[] {
  const index = ids.indexOf(id);
  const target = index + delta;
  if (index === -1 || target < 0 || target >= ids.length) return [...ids];
  const next = [...ids];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

/** One colour's own kept images, shown in its container (主图 ones included). */
interface ListingVariantImages {
  sourceSkuId: string;
  /** Its newest kept 白底图: the colour's 规格图. */
  skuImage: ListingImage | null;
  /** Its older kept 白底图. */
  whites: ListingImage[];
  /** Its kept 细节图. */
  details: ListingImage[];
  /** Kept scenes of this colour alone (no 合拍). */
  scenes: ListingImage[];
}

interface ListingImageGroups {
  main: ListingImage[];
  /** One entry per variant, in catalog order. */
  variants: ListingVariantImages[];
  /** Shared after the colours: 细节图 of no colour listed here. */
  details: ListingImage[];
  stills: ListingImage[];
  combos: ListingImage[];
  /** Scenes of several colours of this product, then 合拍. */
  scenes: ListingImage[];
  /** 白底图 of no colour listed here. */
  others: ListingImage[];
}

function listingImageGroups(images: readonly ListingImage[], mainIds: readonly string[], skuOrder: readonly string[]): ListingImageGroups {
  const byId = new Map(images.map((image) => [image.outputId, image]));
  const main = mainIds.flatMap((id) => {
    const image = byId.get(id);
    return image ? [image] : [];
  });
  const listed = new Set(skuOrder);
  // A scene of exactly one colour (and no other product) belongs to that colour.
  const colourOf = (image: ListingImage): string | null => {
    if (image.kind === "scene") return !image.together && image.skuIds.length === 1 && listed.has(image.skuIds[0]) ? image.skuIds[0] : null;
    return image.sourceSkuId && listed.has(image.sourceSkuId) ? image.sourceSkuId : null;
  };
  const variants = skuOrder.map((sourceSkuId): ListingVariantImages => {
    const own = (kind: ListingImageKind) => images.filter((image) => image.kind === kind && colourOf(image) === sourceSkuId);
    const whites = own("white");
    const skuImage = whites[whites.length - 1] ?? null;
    return { sourceSkuId, skuImage, whites: whites.filter((image) => image !== skuImage), details: own("detail"), scenes: own("scene") };
  });
  const shared = (kind: ListingImageKind) => images.filter((image) => image.kind === kind && colourOf(image) === null);
  const sharedScenes = shared("scene");
  return {
    main,
    variants,
    details: shared("detail"),
    stills: images.filter((image) => image.kind === "still"),
    combos: images.filter((image) => image.kind === "combo"),
    scenes: [...sharedScenes.filter((image) => !image.together), ...sharedScenes.filter((image) => image.together)],
    others: shared("white"),
  };
}

/**
 * The 详情 images in page order: scenes (each colour's, then several
 * colours', then 合拍) → the 组合图 → per colour for sale its 规格图 and
 * 细节图 → 静物图 (the newest per colour). Scenes also showing a colour not
 * for sale stay out. Each image once, at most 30.
 */
function listingDetailIds(groups: ListingImageGroups, listedSkuIds: readonly string[]): string[] {
  const listed = new Set(listedSkuIds);
  const variants = groups.variants.filter((variant) => listed.has(variant.sourceSkuId));
  const combo = preferredListingCombo(groups.combos, listedSkuIds);
  const stills = new Map<string, ListingImage>();
  for (const image of groups.stills) stills.set(image.sourceSkuId ?? "", image);
  const ordered = [
    ...variants.flatMap((variant) => variant.scenes),
    ...groups.scenes.filter((image) => showsOnlyListed(image, listedSkuIds)),
    ...(combo ? [combo] : []),
    ...variants.flatMap((variant) => [...(variant.skuImage ? [variant.skuImage] : []), ...variant.details]),
    ...groups.details,
    ...stills.values(),
  ];
  return [...new Set(ordered.map((image) => image.outputId))].slice(0, LISTING_MAX_DETAIL_IMAGES);
}

/**
 * Exactly what 导出到本地文件夹 sends for one card: the 主图 in order, each
 * colour for sale with its 规格图 (its newest kept 白底图; colours without one
 * are left out), the 详情 order, the colours for sale and 导出到 (a numbered
 * folder's name, or "new").
 */
function listingExportBody(
  projectId: string,
  catalogProductId: string,
  mainIds: readonly string[],
  groups: ListingImageGroups,
  listedSkuIds: readonly string[],
  targetFolder: string,
): DoubaoExportRequest {
  const listed = new Set(listedSkuIds);
  return {
    projectId,
    catalogProductId,
    mainOutputIds: [...new Set(mainIds)].slice(0, LISTING_MAX_MAIN_IMAGES),
    skuImages: groups.variants.flatMap((variant) => listed.has(variant.sourceSkuId) && variant.skuImage
      ? [{ sourceSkuId: variant.sourceSkuId, outputId: variant.skuImage.outputId }]
      : []),
    detailOutputIds: listingDetailIds(groups, listedSkuIds),
    listedSkuIds: [...listed],
    targetFolder,
  };
}

/** A variant with a supplier variant id: one container in its product's card. */
interface ListingSku {
  skuId: string;
  sourceSkuId: string;
  variant: string;
  stock: number | null;
  /**
   * Stock 0 in the 货盘 (货盘显示没货): 上架 starts off, so its container
   * starts folded, but the owner may switch it on like any other colour.
   */
  soldOut: boolean;
}

function listingSkus(group: Pick<VisualCatalogProduct, "skus">): ListingSku[] {
  const seen = new Set<string>();
  return group.skus.flatMap((sku): ListingSku[] => {
    if (!sku.sourceSkuId || seen.has(sku.sourceSkuId)) return [];
    seen.add(sku.sourceSkuId);
    const stock = typeof sku.stock === "number" && Number.isFinite(sku.stock) ? sku.stock : null;
    return [{
      skuId: sku.id,
      sourceSkuId: sku.sourceSkuId,
      variant: [sku.style, sku.color, sku.size, sku.material].filter(Boolean).join(" · ") || "默认规格",
      stock,
      soldOut: palletSoldOut(stock),
    }];
  });
}

/**
 * The colours for sale on this link, in catalog order: 上架 is on for every
 * colour until the owner switches it off — except one the 货盘 shows sold
 * out, which is off until the owner switches it on; once on it counts like
 * any other colour. A colour switched off folds to its name and leaves
 * 完整度, 一键补齐, 主图 and the export.
 */
function listedListingSkus<T extends Pick<ListingSku, "sourceSkuId" | "soldOut">>(skus: readonly T[], listing: Readonly<Record<string, boolean>>): T[] {
  return skus.filter((sku) => sku.soldOut ? listing[sku.sourceSkuId] === true : listing[sku.sourceSkuId] !== false);
}

/** Images showing only colours not for sale stay out of the link. */
function listingSaleImages(images: readonly ListingImage[], listedSkuIds: readonly string[]): ListingImage[] {
  const listed = new Set(listedSkuIds);
  return images.filter((image) => image.skuIds.length === 0 || image.skuIds.some((id) => listed.has(id)));
}

// ---------------------------------------------------------------------------
// 5 宝贝 · 完整度: what a product link still lacks, worked out from every job
// and image of the product (not only the kept ones) and the colours switched
// on for the link. A complete link has, per colour for sale, a kept 白底图 and
// 细节图; a kept 组合图 with two or more colours; a kept 静物图 and 场景图.
// ---------------------------------------------------------------------------

/** 已保留 · 待你挑 · 提交中 · 生成中 · 排队 · 不做 · 提交未成功 · 没做成 · 缺 · 缺原图. */
type ListingSlotState = "kept" | "pending" | "waiting" | "running" | "queued" | "skipped" | "rejected" | "failed" | "missing" | "blocked";

interface ListingSlotStatus {
  state: ListingSlotState;
  /** 第 N 位 in the one queue while queued, when known. */
  position: number | null;
  /** Finished images marked neither 保留 nor 不用, oldest first; only while 待你挑. */
  pending: VisualGenerationOutput[];
  /** Why the newest try did not work out; only while 没做成. */
  failureKind: SlotFailureKind | null;
  /** Safe API wording when the submission was refused before a job existed. */
  submissionError?: ProductImageRequestRejection;
}

/** A colour switched on for the link. */
interface ListingVariantInput {
  sourceSkuId: string;
  /** Its visual product id, which the generator takes. */
  skuId: string;
  /** It has a product photo, so its images can be made. */
  usable: boolean;
}

interface ListingCompleteness {
  /** One row per colour for sale, in catalog order: its 白底图 and 细节图. */
  variants: Array<ListingVariantInput & { white: ListingSlotStatus; detail: ListingSlotStatus }>;
  /** The 主色: the colour of the 静物图 and the example scene. */
  mainSkuId: string | null;
  /** null with fewer than two colours for sale. */
  combo: ListingSlotStatus | null;
  /** The colours a new 组合图 shows. */
  comboSkuIds: string[];
  still: ListingSlotStatus;
  scene: ListingSlotStatus;
  /** Requirements met (kept), and in all. */
  done: number;
  total: number;
}

type ListingSlotItem = Pick<VisualGenerationJob, "jobType" | "catalogProductId" | "sourceSkuId" | "productIds">;

/**
 * One requirement from its own jobs and images: 已保留 wins, then images
 * waiting for 保留 / 不用, a request being sent, a job running or queued (its
 * 第 N 位); otherwise 不做 when the owner chose 不做这张, 缺原图 when it
 * cannot be made, 没做成 (with the reason) when the newest try failed, else
 * 缺. 修改 and 不用 images count as neither kept nor waiting.
 */
function listingSlotStatus(
  jobs: readonly Pick<VisualGenerationJob, "jobId" | "status" | "createdAt" | "failureKind">[],
  outputs: readonly VisualGenerationOutput[],
  request: ProductImageRequestState | undefined,
  makeable: boolean,
  positions: ReadonlyMap<string, number | null>,
  skipped = false,
): ListingSlotStatus {
  const status = (state: ListingSlotState, position: number | null = null, pending: VisualGenerationOutput[] = [], failureKind: SlotFailureKind | null = null): ListingSlotStatus => ({ state, position, pending, failureKind });
  if (outputs.some((output) => output.reviewDecision === "keep")) return status("kept");
  const pending = outputs
    .filter((output) => !output.reviewDecision)
    .sort((left, right) => (left.createdAt ?? "").localeCompare(right.createdAt ?? ""));
  if (pending.length > 0) return status("pending", null, pending);
  if (request === "waiting") return status("waiting");
  const active = jobs.filter((job) => job.status === "queued" || job.status === "running");
  if (active.some((job) => job.status === "running")) return status("running");
  if (active.length > 0) {
    const known = active.flatMap((job) => {
      const position = positions.get(job.jobId);
      return typeof position === "number" && position > 0 ? [position] : [];
    });
    return status("queued", known.length > 0 ? Math.min(...known) : null);
  }
  if (skipped) return status("skipped");
  if (!makeable) return status("blocked");
  const latest = jobs.reduce<Pick<VisualGenerationJob, "status" | "createdAt" | "failureKind"> | null>((newest, job) => !newest || job.createdAt > newest.createdAt ? job : newest, null);
  if (productImageRequestRejected(request)) return { ...status("rejected"), submissionError: request };
  if (latest?.status === "failed") return status("failed", null, [], slotFailureKind(latest));
  return status("missing");
}

/**
 * The 完整度 of one product link. `onSale` are its colours switched on, in
 * catalog order; the 主色 is `mainSkuId` when it is one of them and can be
 * made, else the first that can. Scenes and 组合图 count only when every
 * colour they show is for sale; a 静物图 of any colour for sale counts.
 * `skipped` are the slots marked 不做这张: they say 不做 and leave the count
 * (scenes cannot be skipped).
 */
function listingCompleteness(
  jobs: readonly VisualGenerationJob[],
  outputs: readonly VisualGenerationOutput[],
  group: Pick<VisualCatalogProduct, "productId" | "skus">,
  onSale: readonly ListingVariantInput[],
  options: {
    mainSkuId?: string | null;
    requests?: Readonly<Record<string, ProductImageRequestState>>;
    positions?: ReadonlyMap<string, number | null>;
    skipped?: ReadonlySet<string>;
  } = {},
): ListingCompleteness {
  const productId = group.productId;
  const requests = options.requests ?? {};
  const positions = options.positions ?? new Map<string, number>();
  const skipped = options.skipped ?? new Set<string>();
  const sourceOf = new Map(group.skus.flatMap((sku) => sku.sourceSkuId ? [[sku.id, sku.sourceSkuId] as const] : []));
  const forSale = new Set(onSale.map((variant) => variant.sourceSkuId));
  const kindOf = (item: ListingSlotItem) => item.jobType || "scene";
  const showsOnlyForSale = (item: ListingSlotItem) => (item.productIds ?? []).length > 0
    && (item.productIds ?? []).every((id) => forSale.has(sourceOf.get(id) ?? ""));
  const slot = (match: (item: ListingSlotItem) => boolean, request: ProductImageRequestState | undefined, makeable: boolean, isSkipped = false) =>
    listingSlotStatus(jobs.filter(match), outputs.filter(match), request, makeable, positions, isSkipped);
  const own = (jobType: string, sourceSkuId: string) => (item: ListingSlotItem) =>
    kindOf(item) === jobType && item.catalogProductId === productId && item.sourceSkuId === sourceSkuId;
  const makeable = onSale.filter((variant) => variant.usable);
  const main = makeable.find((variant) => variant.skuId === options.mainSkuId) ?? makeable[0] ?? null;
  const variantSlot = (jobType: VisualProductImageJobType, variant: ListingVariantInput) => {
    const key = productImageSlotKey(jobType, productId, variant.sourceSkuId);
    return slot(own(jobType, variant.sourceSkuId), requests[key], variant.usable, skipped.has(key));
  };
  const variants = onSale.map((variant) => ({
    ...variant,
    white: variantSlot("truth_white", variant),
    detail: variantSlot("truth_detail", variant),
  }));
  const comboSkuIds = makeable.slice(0, MAX_COMBO_SKUS).map((variant) => variant.skuId);
  const comboKey = productImageSlotKey("truth_combo", productId, null);
  const combo = onSale.length >= 2
    ? slot(
      (item) => kindOf(item) === "truth_combo" && item.catalogProductId === productId && showsOnlyForSale(item),
      requests[comboKey],
      comboSkuIds.length >= 2,
      skipped.has(comboKey),
    )
    : null;
  // The 静物图 is one per link: 不做这张 on any colour for sale covers it.
  const still = slot(
    (item) => kindOf(item) === "truth_neutral" && item.catalogProductId === productId && forSale.has(item.sourceSkuId ?? ""),
    main ? requests[productImageSlotKey("truth_neutral", productId, main.sourceSkuId)] : undefined,
    Boolean(main),
    onSale.some((variant) => skipped.has(productImageSlotKey("truth_neutral", productId, variant.sourceSkuId))),
  );
  const scene = slot((item) => kindOf(item) === "scene" && showsOnlyForSale(item), undefined, makeable.length > 0);
  // 不做 leaves the count: the link is complete without it.
  const required = [...variants.flatMap((variant) => [variant.white, variant.detail]), ...(combo ? [combo] : []), still, scene]
    .filter((item) => item.state !== "skipped");
  return {
    variants,
    mainSkuId: main?.skuId ?? null,
    combo,
    comboSkuIds,
    still,
    scene,
    done: required.filter((item) => item.state === "kept").length,
    total: required.length,
  };
}

/**
 * 一键补齐: a request for every requirement that is 缺 or 没做成 — each
 * colour's 白底图, then each colour's 细节图, the 组合图 (two or more
 * colours), the 主色's 静物图 — never for one kept, waiting for 保留 / 不用
 * or on the way. Scenes need references the owner picks, so they are never
 * asked for here. At most 30 per tap.
 */
function listingFillSlots(completeness: ListingCompleteness, catalogProductId: string): ProductImageSlot[] {
  const needed = (status: ListingSlotStatus | null): boolean => status?.state === "missing" || status?.state === "failed" || status?.state === "rejected";
  const usable = completeness.variants.filter((variant) => variant.usable);
  const slots = [
    ...usable.filter((variant) => needed(variant.white)).map((variant) => listingVariantSlot("truth_white", catalogProductId, variant)),
    ...usable.filter((variant) => needed(variant.detail)).map((variant) => listingVariantSlot("truth_detail", catalogProductId, variant)),
  ];
  const combo = listingComboSlot(completeness, catalogProductId);
  if (combo && needed(completeness.combo)) slots.push(combo);
  const still = listingStillSlot(completeness, catalogProductId);
  if (still && needed(completeness.still)) slots.push(still);
  return slots.slice(0, MAX_JOBS_PER_RUN);
}

/** One colour's 白底图 / 细节图 / 静物图 of a link, as step 2 sends it. */
function listingVariantSlot(jobType: Exclude<VisualProductImageJobType, "truth_combo">, catalogProductId: string, variant: Pick<ListingVariantInput, "sourceSkuId" | "skuId">): ProductImageSlot {
  return {
    key: productImageSlotKey(jobType, catalogProductId, variant.sourceSkuId),
    jobType,
    label: productImageSlotDefinitions.find((definition) => definition.jobType === jobType)?.label ?? "",
    variantLabel: null,
    catalogProductId,
    sourceSkuId: variant.sourceSkuId,
    productIds: [variant.skuId],
  };
}

/** The link's 组合图 (the colours for sale that can be made); null below two of them. */
function listingComboSlot(completeness: Pick<ListingCompleteness, "comboSkuIds">, catalogProductId: string): ProductImageSlot | null {
  if (completeness.comboSkuIds.length < 2) return null;
  return {
    key: productImageSlotKey("truth_combo", catalogProductId, null),
    jobType: "truth_combo",
    label: "组合图",
    variantLabel: null,
    catalogProductId,
    sourceSkuId: null,
    productIds: [...completeness.comboSkuIds],
  };
}

/** The link's 静物图: made for the 主色; null when no colour for sale can be made. */
function listingStillSlot(completeness: Pick<ListingCompleteness, "variants" | "mainSkuId">, catalogProductId: string): ProductImageSlot | null {
  const main = completeness.variants.find((variant) => variant.usable && variant.skuId === completeness.mainSkuId);
  return main ? listingVariantSlot("truth_neutral", catalogProductId, main) : null;
}

const listingSlotTexts: Record<ListingSlotState, string> = {
  kept: "已保留",
  pending: "待你挑",
  waiting: "提交中",
  running: "生成中",
  queued: "排队中",
  skipped: "不做",
  rejected: "提交未成功",
  failed: "没做成",
  missing: "缺",
  blocked: "缺原图",
};

/** A chip's state in words; a queued job says its 第 N 位 in the one queue. */
function listingSlotText(status: Pick<ListingSlotStatus, "state" | "position">): string {
  return status.state === "queued" && status.position ? `排队 第 ${status.position} 位` : listingSlotTexts[status.state];
}

/** 全部有货颜色上架: the colours in stock (every one not known to be sold out when no stock is known). */
function inStockListingSkuIds(skus: readonly Pick<ListingSku, "sourceSkuId" | "stock" | "soldOut">[]): string[] {
  const inStock = skus.filter((sku) => typeof sku.stock === "number" && sku.stock > 0);
  return (inStock.length > 0 ? inStock : skus.filter((sku) => !sku.soldOut)).map((sku) => sku.sourceSkuId);
}

/**
 * 去做场景图: the choice for step 3 — only this product, its 主色 (the first
 * of `forSale` it can use) first, then the colours for sale it had chosen
 * before. Empty when none of the colours for sale can be used.
 */
function sceneStepSelection(selectedProductIds: readonly string[], groupSkuIds: readonly string[], forSale: readonly string[]): string[] {
  const usable = forSale.filter((id) => groupSkuIds.includes(id));
  const main = usable[0];
  if (!main) return [];
  const others = selectedProductIds.filter((id) => id !== main && usable.includes(id));
  return [...new Set([main, ...others])].slice(0, MAX_VARIANTS_PER_PRODUCT);
}

/** What 一键补齐 hands the workbench: the product and its colours for sale. */
interface ListingFillInput {
  group: Pick<VisualCatalogProduct, "productId" | "skus">;
  onSale: ListingVariantInput[];
  mainSkuId: string | null;
}

/**
 * 缺项: only what the link's images still lack, in plain words — per colour
 * for sale a kept 白底图 and 细节图, a 组合图 with two or more colours, a
 * scene, at least one 主图. They are warnings: the export still works and
 * the owner decides.
 */
function listingGapTexts(completeness: Pick<ListingCompleteness, "variants" | "combo" | "scene">, mainCount: number): string[] {
  const gaps: string[] = [];
  if (completeness.variants.length === 0) gaps.push("还没有上架的颜色");
  // 不做 is the owner's choice, not a gap.
  const lacks = (status: Pick<ListingSlotStatus, "state">) => status.state !== "kept" && status.state !== "skipped";
  const withoutWhite = completeness.variants.filter((variant) => lacks(variant.white)).length;
  const withoutDetail = completeness.variants.filter((variant) => lacks(variant.detail)).length;
  if (withoutWhite > 0) gaps.push(`还有 ${withoutWhite} 个颜色没有白底图`);
  if (withoutDetail > 0) gaps.push(`还有 ${withoutDetail} 个颜色没有细节图`);
  if (completeness.combo && lacks(completeness.combo)) gaps.push("还没有组合图");
  if (completeness.scene.state !== "kept") gaps.push("还没有场景图");
  if (mainCount < 1) gaps.push("主图至少 1 张");
  return gaps;
}

/**
 * A message from the site that is already plain Chinese (short, no paths,
 * codes or English words), else "". A variant name in 「」 (e.g. 「PINK」) is
 * a product fact and may hold letters.
 */
function plainServerText(message: string | null | undefined): string {
  const text = (message ?? "").trim();
  if (!text || text.length > 80 || !/\p{Script=Han}/u.test(text)) return "";
  if (/[\\<>{}]|\/(?:Users|Applications|private|tmp|var|home)\b/.test(text)) return "";
  if (/[/]|[A-Za-z]{3,}/.test(text.replace(/「[^「」]{0,40}」/g, ""))) return "";
  return text;
}

const exportFailureTexts: Record<string, string> = {
  ADMIN_REQUIRED: "只有管理员能导出",
  EXPORT_IN_PROGRESS: "这款正在导出，请稍等",
};

/** What a failed 导出 says: the slow link, a plain reason from the site, or a short fallback. */
function exportFailureText(error: unknown): string {
  if (isSlowNetworkError(error)) return "网络有点慢，导出还在进行，稍后点刷新看导出记录";
  if (error instanceof ApiError) {
    if (Object.prototype.hasOwnProperty.call(exportFailureTexts, error.code)) return exportFailureTexts[error.code];
    if (error.status === 401 || error.status === 403) return "请刷新页面后重试";
    if (error.status >= 400 && error.status < 500) return plainServerText(error.message) || "提交内容有误，请刷新后重试";
    return plainServerText(error.message) || "导出没成功，请稍后再试";
  }
  return "导出没成功，请稍后再试";
}

/** v001 for version 1 (or "v001" / "网站导出_v001" / "1" as sent); "" when it is not a version. */
function exportVersionText(value: unknown): string {
  const match = /^(?:网站导出_)?v?(\d{1,6})$/i.exec(String(value ?? "").trim());
  const number = match ? Number(match[1]) : 0;
  return number > 0 ? `v${String(number).padStart(3, "0")}` : "";
}

/**
 * An export folder as the owner finds it in FurryJoy (图片素材库/01_按宝贝链接/
 * 006_…/v001); never a full path of the Mac.
 */
function exportFolderText(dir: string | null | undefined): string {
  const text = String(dir ?? "").replace(/\\/g, "/").replace(/\/+$/, "").trim();
  if (!text) return "";
  const library = text.indexOf("图片素材库/");
  if (library !== -1) return text.slice(library);
  const root = text.lastIndexOf("/FurryJoy/");
  if (root !== -1) return text.slice(root + "/FurryJoy/".length);
  const outputs = text.indexOf("outputs/");
  if (outputs !== -1) return text.slice(outputs);
  if (text.startsWith("/") || /^[A-Za-z]:\//.test(text)) return text.split("/").filter(Boolean).slice(-3).join("/");
  return text;
}

/** 主图 5 张 · 规格图 3 张 · 详情 12 张, from the files of one export. */
function exportFilesText(files: readonly Pick<DoubaoExportFile, "role">[]): string {
  const roles = [["main", "主图"], ["sku", "规格图"], ["detail", "详情"]] as const;
  return roles.flatMap(([role, name]) => {
    const count = files.filter((file) => file.role === role).length;
    return count > 0 ? [`${name} ${count} 张`] : [];
  }).join(" · ");
}

function listingStrings(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];
}

/** The export's answer made safe to show; null when it names no folder or version. */
function normalizeExportResult(value: unknown): DoubaoExportResult | null {
  const item = value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};
  const version = exportVersionText(item.version);
  if (typeof item.exportDir !== "string" || !item.exportDir || !version) return null;
  const files = (Array.isArray(item.files) ? item.files : []).flatMap((entry): DoubaoExportFile[] => {
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) return [];
    const file = entry as Record<string, unknown>;
    if (typeof file.file !== "string" || !file.file) return [];
    return [{
      file: file.file,
      role: typeof file.role === "string" ? file.role : "",
      order: typeof file.order === "number" && Number.isFinite(file.order) ? file.order : null,
      sourceSkuId: typeof file.sourceSkuId === "string" ? file.sourceSkuId : null,
      variant: typeof file.variant === "string" ? file.variant : null,
    }];
  });
  return {
    exportDir: item.exportDir,
    targetFolder: exportFolderName(item.targetFolder) || exportParentFolder(item.exportDir),
    version,
    createdAt: typeof item.createdAt === "string" ? item.createdAt : null,
    files,
  };
}

/**
 * Newest first: by time when both are known (versions of an older folder and
 * of the one picked in 导出到 can share a number), else by version.
 */
function exportVersionOrder(left: Pick<DoubaoExportVersion, "version" | "createdAt">, right: Pick<DoubaoExportVersion, "version" | "createdAt">): number {
  const leftTime = Date.parse(left.createdAt ?? "");
  const rightTime = Date.parse(right.createdAt ?? "");
  if (Number.isFinite(leftTime) && Number.isFinite(rightTime) && leftTime !== rightTime) return rightTime - leftTime;
  return Number(right.version.slice(1)) - Number(left.version.slice(1));
}

/** GET /api/doubao-export made safe to show: versions with a folder (newest first), the numbered folders (by number) and the name a new folder would get. */
function normalizeExportVersions(value: unknown): DoubaoExportsData {
  const data = value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};
  const exports = (Array.isArray(data.exports) ? data.exports : []).flatMap((entry): DoubaoExportVersion[] => {
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) return [];
    const item = entry as Record<string, unknown>;
    const version = exportVersionText(item.version);
    if (!version || typeof item.exportDir !== "string" || !item.exportDir) return [];
    return [{
      version,
      createdAt: typeof item.createdAt === "string" ? item.createdAt : null,
      exportDir: item.exportDir,
      fileCount: typeof item.fileCount === "number" && Number.isFinite(item.fileCount) && item.fileCount > 0 ? Math.round(item.fileCount) : 0,
    }];
  });
  const seen = new Set<string>();
  const folders = (Array.isArray(data.folders) ? data.folders : []).flatMap((entry): DoubaoExportFolder[] => {
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) return [];
    const item = entry as Record<string, unknown>;
    const name = exportFolderName(item.name);
    if (!name || seen.has(name)) return [];
    seen.add(name);
    return [{ name, number: Number(name.slice(0, 3)), isLinked: item.isLinked === true }];
  });
  return {
    exports: exports.sort(exportVersionOrder),
    folders: sortExportFolders(folders),
    suggestedNewFolder: exportFolderName(data.suggestedNewFolder) || null,
  };
}

// ---------------------------------------------------------------------------
// 导出到: the numbered folders of 图片素材库/01_按宝贝链接 — the owner's own
// (006_怪兽绳结, with their 主图 inside) and ones an export made. Each export
// goes into a new 网站导出_vNNN folder inside the one picked; "new" makes a
// folder named like suggestedNewFolder.
// ---------------------------------------------------------------------------

const EXPORT_NEW_FOLDER = "new";
// As long as the Mac allows (200): a longer name dropped here could be this product's folder, and 新建 would make a second one.
const EXPORT_FOLDER_NAME = /^\d{3}_[^/\\\u0000-\u001f\u007f-\u009f]{1,196}$/u;

/** "006_怪兽绳结" when the text is one numbered folder name (no path), else "". */
function exportFolderName(value: unknown): string {
  const text = typeof value === "string" ? value.trim() : "";
  return EXPORT_FOLDER_NAME.test(text) && !text.includes("..") ? text : "";
}

/** By number, then by name. */
function sortExportFolders(folders: readonly DoubaoExportFolder[]): DoubaoExportFolder[] {
  return [...folders].sort((left, right) => left.number - right.number || (left.name < right.name ? -1 : left.name > right.name ? 1 : 0));
}

/** The numbered folder an export went into: 006_怪兽绳结 for …/006_怪兽绳结/网站导出_v001; "" when there is none. */
function exportParentFolder(dir: string | null | undefined): string {
  const parts = exportFolderText(dir).split("/").filter(Boolean);
  return parts.length >= 2 ? exportFolderName(parts[parts.length - 2]) : "";
}

/**
 * The folders 导出到 offers: as last read, plus the one the last export just
 * went into (a new folder is not in the list until it is read again), which
 * is this product's only folder from then on.
 */
function exportTargetFolders(folders: readonly DoubaoExportFolder[], exportedFolder: string): DoubaoExportFolder[] {
  const name = exportFolderName(exportedFolder);
  if (!name) return [...folders];
  const linked = folders.map((folder) => ({ ...folder, isLinked: folder.name === name }));
  if (folders.some((folder) => folder.name === name)) return linked;
  return sortExportFolders([...linked, { name, number: Number(name.slice(0, 3)), isLinked: true }]);
}

/** Letters and digits only, to compare names: "怪兽绳结 50*15cm" → "怪兽绳结5015cm". */
function exportNameKey(text: string | null | undefined): string {
  return String(text ?? "").normalize("NFKC").toLowerCase().replace(/[^\p{L}\p{N}]+/gu, "");
}

/** What a product goes by, to find the owner's folder for it: its title (each part around " · ") and its colour / style names. */
function exportProductNames(group: Pick<VisualCatalogProduct, "label" | "skus">): string[] {
  const names = [group.label, ...group.label.split(" · ")];
  for (const sku of group.skus) names.push(sku.style ?? "", sku.color ?? "");
  return [...new Set(names.map((name) => name.trim()).filter(Boolean))];
}

/** The product as 导出到 matches it against folder names. */
interface ExportTargetProduct {
  catalogProductId: string;
  /** From exportProductNames. */
  names: readonly string[];
}

/**
 * Where 导出到 points before the owner picks: the folder this product already
 * exports to; else the one folder named like the product — its name holds
 * the short name a new folder would get, or the product's title holds the
 * folder's name (006_怪兽绳结 for a 怪兽绳结 toy), or the folder's name
 * holds a colour / style name (007_绳结饼干薯片 for 绳结饼干); else a new
 * folder. Two or more look-alikes: a new folder, the owner picks.
 */
function defaultExportTarget(folders: readonly DoubaoExportFolder[], suggestedNewFolder: string | null, product: ExportTargetProduct): string {
  const linked = folders.filter((folder) => folder.isLinked);
  if (linked.length > 0) {
    // The folder picked in 导出到 comes before an older one named after the product id.
    const picked = linked.filter((folder) => !folder.name.endsWith(`_${product.catalogProductId}`));
    const pool = picked.length > 0 ? picked : linked;
    return pool[pool.length - 1].name;
  }
  const shortName = exportNameKey(exportFolderName(suggestedNewFolder).slice(4));
  const names = product.names.map(exportNameKey).filter(Boolean);
  const alike = folders.filter((folder) => {
    const key = exportNameKey(folder.name.slice(4));
    if (key.length < 2) return false;
    if (shortName.length >= 2 && key.includes(shortName)) return true;
    return names.some((name) => name.includes(key) || (name.length >= 3 && key.includes(name)));
  });
  return alike.length === 1 ? alike[0].name : EXPORT_NEW_FOLDER;
}

/** 导出到 now: the owner's pick while it is still offered, else defaultExportTarget. */
function exportTarget(folders: readonly DoubaoExportFolder[], suggestedNewFolder: string | null, product: ExportTargetProduct, chosen: string | null): string {
  if (chosen === EXPORT_NEW_FOLDER || (chosen && folders.some((folder) => folder.name === chosen))) return chosen;
  return defaultExportTarget(folders, suggestedNewFolder, product);
}

/** A choice of 导出到 in words: 新建 008_怪兽, or 006_怪兽绳结（这款） for this product's folder. */
function exportTargetLabel(value: string, folders: readonly DoubaoExportFolder[], suggestedNewFolder: string | null): string {
  if (value === EXPORT_NEW_FOLDER) return suggestedNewFolder ? `新建 ${suggestedNewFolder}` : "新建文件夹";
  return folders.find((folder) => folder.name === value)?.isLinked ? `${value}（这款）` : value;
}

/** The line under 导出到: what happens to the folder picked; 新建 while this product has a folder says it makes a second one. */
function exportTargetNote(value: string, folders: readonly DoubaoExportFolder[], chosen: string | null): string {
  if (value === EXPORT_NEW_FOLDER) {
    const own = folders.find((folder) => folder.isLinked);
    return own ? `这款已经有文件夹 ${own.name}，会再新建一个` : "会新建这个文件夹，图放进里面的「网站导出_v001」";
  }
  const keep = "图放进里面新建的「网站导出」文件夹，原有的文件不动";
  if (folders.find((folder) => folder.name === value)?.isLinked) return `这款之前导出到这里。${keep}`;
  return chosen === value ? keep : `按商品名找到的文件夹，请确认是这款。${keep}`;
}

/** A product's earlier exports as last read; `failed` when the last read did not work. */
interface ListingExportsState {
  data: DoubaoExportsData | null;
  failed: boolean;
}

/** 最新导出 in words: `v002 · 图片素材库/…/v002`, 还没导出, or why it is not known yet. */
function listingLatestExportText(state: ListingExportsState | undefined): string {
  const latest = state?.data?.exports[0];
  if (latest) {
    const folder = exportFolderText(latest.exportDir);
    return folder ? `${latest.version} · ${folder}` : latest.version;
  }
  if (state?.data) return "还没导出";
  return state?.failed ? "暂时读不到，请刷新" : "读取中…";
}

/** 10月2日 14:05 in the viewer's time. */
function formatListingTime(value: string | null | undefined): string {
  const time = Date.parse(value ?? "");
  if (!Number.isFinite(time)) return "";
  const date = new Date(time);
  return `${date.getMonth() + 1}月${date.getDate()}日 ${formatClock(date)}`;
}

interface ListingAgentSummaryInput {
  themeTitle: string;
  productName: string;
  catalogProductId: string;
  skuCount: number;
  keptCount: number;
  /** 最新导出, from listingLatestExportText. */
  latestExport: string;
  link: string;
}

/**
 * Plain lines, one fact each, that say exactly which 宝贝 this is and where
 * its newest local export is — for the owner to read or paste to an agent.
 */
function listingAgentSummary(input: ListingAgentSummaryInput): string {
  return [
    `主题：${input.themeTitle}`,
    `商品：${input.productName}`,
    `宝贝编号：${input.catalogProductId}`,
    `规格：${input.skuCount} 个`,
    `保留的图：${input.keptCount} 张`,
    `最新导出：${input.latestExport}`,
    `链接：${input.link}`,
  ].join("\n");
}

/** What a card remembers in this browser: the arranged 主图 and the 上架 switches. */
interface ListingCardState {
  /** null until the 主图 are arranged: the default order. */
  mainOrder: string[] | null;
  /** Colours switched on (true) or off (false); unset = on, except a colour the 货盘 shows sold out (off). */
  listing: Record<string, boolean>;
}

function readListingCardStates(): Record<string, unknown> {
  try {
    const value = JSON.parse(localStorage.getItem(VISUAL_LISTING_CARDS_KEY) ?? "{}") as unknown;
    return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};
  } catch {
    return {};
  }
}

function writeListingCardState(key: string, state: ListingCardState) {
  try {
    const states = readListingCardStates();
    states[key] = { mainOrder: state.mainOrder, listing: state.listing, updatedAt: new Date().toISOString() };
    localStorage.setItem(VISUAL_LISTING_CARDS_KEY, JSON.stringify(states));
  } catch {
    // Storage can be full or disabled; the card keeps working in memory.
  }
}

/** Where a card starts: what this browser remembered for it, else the default order and every colour on but those the 货盘 shows sold out. */
function startListingCardState(key: string, skuOrder: readonly string[]): ListingCardState {
  const stored = readListingCardStates()[key];
  if (!stored || typeof stored !== "object" || Array.isArray(stored)) return { mainOrder: null, listing: {} };
  const candidate = stored as Record<string, unknown>;
  const listing = candidate.listing && typeof candidate.listing === "object" && !Array.isArray(candidate.listing)
    ? Object.fromEntries(Object.entries(candidate.listing as Record<string, unknown>)
      .filter((entry): entry is [string, boolean] => skuOrder.includes(entry[0]) && typeof entry[1] === "boolean"))
    : {};
  return { mainOrder: Array.isArray(candidate.mainOrder) ? listingStrings(candidate.mainOrder) : null, listing };
}

/**
 * Copies text: the clipboard where the page may use it (https), else the
 * older copy command; false when neither worked, so the text is shown to
 * copy by hand.
 */
async function copyText(text: string): Promise<boolean> {
  try {
    if (window.isSecureContext && navigator.clipboard) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Falls back to the copy command below.
  }
  try {
    const area = document.createElement("textarea");
    area.value = text;
    area.setAttribute("readonly", "");
    area.style.position = "fixed";
    area.style.top = "0";
    area.style.opacity = "0";
    document.body.appendChild(area);
    area.select();
    area.setSelectionRange(0, text.length);
    const copied = document.execCommand("copy");
    area.remove();
    return copied;
  } catch {
    return false;
  }
}

/**
 * Step 5: a card per product of the theme with at least one image marked
 * 保留. Reads the kept images and each product's earlier exports each time it
 * opens; it never polls.
 */
function ListingBoard({
  projectId, themeTitle, singleProductId, linkFor, onOpenProduct, onShowAll,
  groups, outputs, jobs, queuePositions, usableSkuIds, requests, busyOutputs, mainSkuIdOf, outputsUnavailable, canExport, visit, notify,
  onRefreshOutputs, onPreview, onMakeImages, onReview, onFill, onMakeScenes, onLoadProduct, slotFix, ownerPhotos,
}: {
  projectId: string;
  themeTitle: string;
  /** Show only this 宝贝 ("" = every 宝贝 of the theme). */
  singleProductId: string;
  /** The address of one 宝贝 ("" = this theme's 宝贝 list). */
  linkFor: (catalogProductId: string) => string;
  /** 单独打开: one 宝贝 on its own. */
  onOpenProduct: (catalogProductId: string) => void;
  /** 查看全部: back to every 宝贝 of the theme. */
  onShowAll: () => void;
  groups: readonly VisualCatalogProduct[];
  /** null until the generator's images for this theme have arrived. */
  outputs: readonly VisualGenerationOutput[] | null;
  /** Every job of the theme (from the same records as `outputs`), for 完整度. */
  jobs: readonly VisualGenerationJob[] | null;
  /** 第 N 位 of each queued job in the one queue. */
  queuePositions: ReadonlyMap<string, number | null>;
  /** Colours with a product photo, whose images can be made. */
  usableSkuIds: ReadonlySet<string>;
  /** Product-image requests being sent, or refused (by slot). */
  requests: Readonly<Record<string, ProductImageRequestState>>;
  /** Images with a 保留 / 不用 on the way. */
  busyOutputs: Readonly<Record<string, "review" | "revise">>;
  /** The 主色 a product was given on step 1. */
  mainSkuIdOf: (catalogProductId: string) => string | null;
  outputsUnavailable: boolean;
  /** Only the owner may export; the site refuses others. */
  canExport: boolean;
  /** Changes on each entry into the step. */
  visit: number;
  notify: (message: string) => void;
  onRefreshOutputs: () => Promise<void>;
  onPreview: (imageUrl: string, label: string) => void;
  onMakeImages: () => void;
  /** 保留 / 不用 on an image waiting for a choice (the same route as step 2). */
  onReview: (output: VisualGenerationOutput, choice: "keep" | "reject") => void;
  /** 一键补齐: sends what a product lacks; resolves with how many were sent. */
  onFill: (input: ListingFillInput) => Promise<number>;
  /** 去做场景图: step 3 with this product, the given colours (主色 first). */
  onMakeScenes: (catalogProductId: string, forSale: string[]) => void;
  /** Reads a shown product's 不做 slots and 我上传的照片 (with its exports, once per visit and 刷新). */
  onLoadProduct: (catalogProductId: string) => void;
  /** 重做 / 上传照片 / 不做这张 / 恢复 on images that did not work out. */
  slotFix: SlotFixControls;
  /** 我上传的照片 per product, with 删除. */
  ownerPhotos: OwnerPhotoControls;
}) {
  const [refreshing, setRefreshing] = useState(false);
  // Each product's earlier exports, for this theme only.
  const [exportLists, setExportLists] = useState<{ projectId: string; lists: Record<string, ListingExportsState> }>({ projectId, lists: {} });
  // 刷新 reads every product's exports again.
  const [exportRound, setExportRound] = useState(0);
  const exportRequested = useRef(new Map<string, string>());
  // The newest export-list read per theme and product.
  const exportReads = useRef(new Map<string, number>());
  const currentProjectId = useRef(projectId);
  currentProjectId.current = projectId;

  const refresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await onRefreshOutputs();
    } finally {
      setRefreshing(false);
    }
  }, [onRefreshOutputs]);
  useEffect(() => { void refresh(); }, [refresh, visit]);

  const loadExports = useCallback(async (catalogProductId: string) => {
    const id = projectId;
    // Only the newest read of a product counts: an older answer arriving late
    // would bring back a folder list from before an export (导出到 could then
    // offer 新建 again for a product that has its folder).
    const key = `${id}:${catalogProductId}`;
    const ticket = (exportReads.current.get(key) ?? 0) + 1;
    exportReads.current.set(key, ticket);
    const newest = () => currentProjectId.current === id && exportReads.current.get(key) === ticket;
    const save = (entry: (previous: ListingExportsState | undefined) => ListingExportsState) => setExportLists((current) => {
      const lists = current.projectId === id ? current.lists : {};
      return { projectId: id, lists: { ...lists, [catalogProductId]: entry(lists[catalogProductId]) } };
    });
    try {
      const data = normalizeExportVersions(await api<unknown>(
        `/api/doubao-export?projectId=${encodeURIComponent(id)}&catalogProductId=${encodeURIComponent(catalogProductId)}`,
        { timeoutMs: LISTING_REQUEST_TIMEOUT_MS },
      ));
      if (newest()) save(() => ({ data, failed: false }));
    } catch {
      // The last list read stays on the page.
      if (newest()) save((previous) => ({ data: previous?.data ?? null, failed: true }));
    }
  }, [projectId]);

  const products = useMemo(() => outputs
    ? groups.flatMap((group) => {
      const images = keptListingImages(outputs, group);
      return images.length > 0 ? [{ group, images }] : [];
    })
    : [], [groups, outputs]);

  // One 宝贝 on its own (from 单独打开 or a link): only its card, with its facts on top.
  const single = singleProductId ? products.find((entry) => entry.group.productId === singleProductId) ?? null : null;
  const shown = singleProductId ? (single ? [single] : []) : products;
  const shownIds = shown.map((entry) => entry.group.productId).join("\n");
  const allLink = linkFor("");
  const exportsOf = (catalogProductId: string) => exportLists.projectId === projectId ? exportLists.lists[catalogProductId] : undefined;

  // Each shown product's exports are read once per entry into the step (and per 刷新).
  const round = `${projectId}|${visit}|${exportRound}`;
  useEffect(() => {
    for (const catalogProductId of shownIds ? shownIds.split("\n") : []) {
      if (exportRequested.current.get(catalogProductId) === round) continue;
      exportRequested.current.set(catalogProductId, round);
      void loadExports(catalogProductId);
      onLoadProduct(catalogProductId);
    }
  }, [loadExports, round, shownIds]);

  function refreshAll() {
    setExportRound((value) => value + 1);
    void refresh();
  }

  // 查看全部 is a real link (it opens in a new tab too); a plain tap stays on this page.
  function showAll(event: MouseEvent<HTMLAnchorElement>) {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    onShowAll();
  }

  return <>
    <div className="visual-step-toolbar listing-toolbar" data-testid="listing-toolbar">
      <span aria-live="polite">{!outputs ? "加载中…" : singleProductId ? `宝贝编号 ${singleProductId}` : `${products.length} 款宝贝`}</span>
      <button type="button" className="button soft composer-next" data-testid="listing-refresh" disabled={refreshing} onClick={refreshAll}>{refreshing ? "读取中…" : "刷新"}</button>
    </div>
    {singleProductId && <nav className="listing-single-bar" aria-label="只看这一款" data-testid="listing-single-bar">
      <span>只看这一款</span>
      <i aria-hidden="true">·</i>
      <a href={allLink} data-testid="listing-show-all" onClick={showAll}>查看全部</a>
    </nav>}
    {outputsUnavailable && <small className="generation-service-error" role="alert">生成服务暂时不可用，请稍后再试</small>}
    {!outputs
      ? <div className="reference-empty"><strong>{outputsUnavailable ? "暂时打不开" : "加载中…"}</strong>{outputsUnavailable && <button type="button" className="button soft" onClick={refreshAll}>重试</button>}</div>
      : singleProductId && !single
        ? <div className="reference-empty" data-testid="listing-single-empty"><strong>这款还没有保留的图</strong><a className="button soft" href={allLink} data-testid="listing-single-empty-all" onClick={showAll}>看这个主题的全部宝贝</a></div>
        : products.length === 0
          ? <div className="reference-empty" data-testid="listing-empty"><strong>还没有保留的图</strong><button type="button" className="button soft" onClick={onMakeImages}>去商品图</button></div>
          : <div className={`listing-board${single ? " single" : ""}`} data-testid="listing-board">
            {single && <ListingAgentSummary
              text={listingAgentSummary({
                themeTitle,
                productName: single.group.label,
                catalogProductId: single.group.productId,
                skuCount: listingSkus(single.group).length,
                keptCount: single.images.length,
                latestExport: listingLatestExportText(exportsOf(single.group.productId)),
                link: linkFor(single.group.productId),
              })}
              notify={notify}
            />}
            {shown.map(({ group, images }) => <ListingCard
              key={`${projectId}:${group.productId}`}
              projectId={projectId}
              link={linkFor(group.productId)}
              onOpenSingle={single ? null : () => onOpenProduct(group.productId)}
              group={group}
              images={images}
              exports={exportsOf(group.productId)}
              canExport={canExport}
              notify={notify}
              onPreview={onPreview}
              onExported={() => void loadExports(group.productId)}
              jobs={jobs ?? []}
              outputs={outputs}
              queuePositions={queuePositions}
              usableSkuIds={usableSkuIds}
              requests={requests}
              busyOutputs={busyOutputs}
              preferredMainSkuId={mainSkuIdOf(group.productId)}
              onReview={onReview}
              onFill={onFill}
              onMakeScenes={onMakeScenes}
              slotFix={slotFix}
              photos={ownerPhotos.byProduct[group.productId] ?? []}
              photoControls={ownerPhotos}
            />)}
          </div>}
  </>;
}

/**
 * The top of one 宝贝's own view: plain, selectable lines saying exactly which
 * 宝贝 this is and where its newest local export is (to read or paste to an
 * agent), with 复制.
 */
function ListingAgentSummary({ text, notify }: { text: string; notify: (message: string) => void }) {
  async function copySummary() {
    notify(await copyText(text) ? "已复制" : "没能自动复制，请选中上面的文字复制");
  }

  return <section className="listing-agent" aria-label="这款宝贝的信息" data-testid="listing-agent-summary">
    <pre className="listing-agent-text" data-testid="listing-agent-text">{text}</pre>
    <div className="listing-agent-actions">
      <button type="button" className="button soft" data-testid="listing-agent-copy" onClick={() => void copySummary()}>复制</button>
    </div>
  </section>;
}

/** One kept image: tap for the large view; actions underneath. */
function ListingThumb({ image, tag, name, onPreview, children }: {
  image: ListingImage;
  /** Shown before the kind, e.g. 待你挑. */
  tag?: string;
  /** Said instead of the kind, e.g. 规格图 for a colour's 白底图. */
  name?: string;
  onPreview: (imageUrl: string, label: string) => void;
  children?: ReactNode;
}) {
  const kind = name ?? listingKindNames[image.kind];
  const label = [tag, image.together ? "合拍" : "", kind].filter(Boolean).join(" · ");
  return <div className="listing-thumb">
    <button type="button" className="product-image-thumb" aria-label={`看${kind}`} onClick={() => onPreview(image.imageUrl, kind)}>
      <RetryImage src={outputPreviewUrl(image.imageUrl)} />
      <span className={`listing-thumb-tag${image.together ? " together" : ""}`}>{label}</span>
    </button>
    {children && <div className="listing-thumb-actions">{children}</div>}
  </div>;
}

/** One requirement of the link: what it is, then where it stands (已保留, 缺 …). */
/**
 * 第 N 版 on an image that has been redone: tapping it lists every version
 * (small picture, 保留 / 不用, 最新) and picks the one the card shows.
 */
function VersionPicker({ versions, currentId, onPick, testId, align = "left" }: {
  versions: readonly VisualGenerationOutput[];
  currentId: string;
  onPick: (outputId: string) => void;
  testId: string;
  align?: "left" | "right";
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const closeOutside = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", closeOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);
  const index = versions.findIndex((version) => version.outputId === currentId);
  if (versions.length < 2 || index === -1) return null;
  return <div className={`version-picker ${align}`} ref={root} data-testid={testId}>
    <button type="button" className="version-tag" aria-haspopup="menu" aria-expanded={open} data-testid={`${testId}-tag`} onClick={() => setOpen((current) => !current)}>
      第 {index + 1} 版<small>/{versions.length}</small><i aria-hidden="true">▾</i>
    </button>
    {open && <div className="version-menu" role="menu" aria-label="选一个版本">
      {versions.map((version, position) => <button
        key={version.outputId}
        type="button"
        role="menuitemradio"
        aria-checked={version.outputId === currentId}
        className={version.outputId === currentId ? "active" : ""}
        data-testid={`${testId}-v${position + 1}`}
        onClick={() => {
          onPick(version.outputId);
          setOpen(false);
        }}
      >
        <i className="version-thumb" aria-hidden="true"><RetryImage src={outputPreviewUrl(version.imageUrl)} /></i>
        <b>第 {position + 1} 版</b>
        {version.reviewDecision === "keep" && <small>保留</small>}
        {version.reviewDecision === "reject" && <small className="rejected">不用</small>}
        {position === versions.length - 1 && <small>最新</small>}
      </button>)}
    </div>}
  </div>;
}

function ListingChip({ name, status, testId }: { name: string; status: ListingSlotStatus; testId?: string }) {
  return <span className={`listing-chip ${status.state}`} data-testid={testId}><b>{name}</b>{listingSlotText(status)}</span>;
}

/** 重做 / 上传照片 / 不做这张 / 恢复 on images that did not work out (the workbench sends them). */
interface SlotFixControls {
  /** Slots marked 不做这张. */
  skipped: ReadonlySet<string>;
  /** Slots whose 不做这张 / 恢复 is on its way. */
  skipBusy: Readonly<Record<string, boolean>>;
  /** Photos on their way, by slot. */
  uploads: Readonly<Record<string, OwnerPhotoUploadState>>;
  onRedo: (slot: ProductImageSlot) => void;
  onUpload: (slot: ProductImageSlot, file: File) => void;
  onSkip: (slot: ProductImageSlot, skipped: boolean) => void;
  /** Slots whose 用原图放大 is on its way. */
  upscaling: Readonly<Record<string, boolean>>;
  onUpscale: (slot: ProductImageSlot) => void;
  /** What 找同款 searches for this slot's product. */
  searchText: (slot: ProductImageSlot) => string;
}

/** 我上传的照片 of the theme's products, with 删除. */
interface OwnerPhotoControls {
  byProduct: Readonly<Record<string, readonly OwnerPhoto[]>>;
  /** Photos picked on this page show from the file itself, not over the slow link. */
  previews: Readonly<Record<string, string>>;
  /** Photos whose 删除 is on its way. */
  deleting: Readonly<Record<string, boolean>>;
  onDelete: (photo: OwnerPhoto) => void;
}

/**
 * A refused submission has no job and therefore no image to upload against,
 * enlarge, search for or skip. The only safe recovery on the slot is to try
 * the submission again (a page refresh also clears this transient state).
 */
function SlotSubmissionActions({ testId, error, onRetry }: {
  testId: string;
  error: ProductImageRequestRejection;
  onRetry: () => void;
}) {
  return <div className="slot-fix-actions submission-rejected" data-testid={testId} data-error-code={error.code}>
    <button type="button" className="button primary" data-testid={`${testId}-retry`} onClick={onRetry}>重试提交</button>
  </div>;
}

/**
 * 重做 / 上传照片 / 不做这张 under an image that did not work out. With no
 * clear photo of the colour, 上传照片 leads (重做 alone would fail again);
 * otherwise 重做 does. The photo goes as it is: JPG, PNG or WebP.
 */
function SlotFixActions({ testId, kind, busy, upscaling, searchText, onRedo, onUpload, onUpscale, onSkip }: {
  testId: string;
  kind: SlotFailureKind;
  /** 不做这张 is on its way. */
  busy: boolean;
  /** 用原图放大 is on its way. */
  upscaling: boolean;
  searchText: string;
  onRedo: () => void;
  onUpload: (file: File) => void;
  onUpscale: () => void;
  onSkip: () => void;
}) {
  const fileInput = useRef<HTMLInputElement | null>(null);
  const photoFirst = kind === "source_missing";
  return <div className="slot-fix-actions" data-testid={testId}>
    <button type="button" className={`button ${photoFirst ? "soft" : "primary"}`} data-testid={`${testId}-redo`} disabled={busy} onClick={onRedo}>重做</button>
    <button type="button" className={`button ${photoFirst ? "primary" : "soft"}`} data-testid={`${testId}-upload`} disabled={busy} onClick={() => fileInput.current?.click()}>上传照片</button>
    <input ref={fileInput} type="file" accept="image/jpeg,image/png,image/webp" hidden tabIndex={-1} data-testid={`${testId}-file`} onChange={(event) => {
      const file = event.currentTarget.files?.[0];
      // The same photo can be picked again after a failed try.
      event.currentTarget.value = "";
      if (file) onUpload(file);
    }} />
    <button type="button" className="button soft" data-testid={`${testId}-upscale`} disabled={busy || upscaling} aria-busy={upscaling} onClick={onUpscale}>{upscaling ? "放大中…" : "用原图放大"}</button>
    <button type="button" className="button ghost" data-testid={`${testId}-skip`} disabled={busy} aria-busy={busy} onClick={onSkip}>不做这张</button>
    <SameStyleLinks text={searchText} testId={`${testId}-same-style`} />
  </div>;
}

/** A photo on its way: a thin bar and 上传中 N%. */
function SlotUploadProgress({ state }: { state: OwnerPhotoUploadState }) {
  return <span className="slot-upload" role="status">
    <span className="slot-upload-bar" role="progressbar" aria-label="上传照片" aria-valuemin={0} aria-valuemax={100} aria-valuenow={state.percent}><i style={{ width: `${state.percent}%` }} /></span>
    <small>{ownerPhotoUploadText(state)}</small>
  </span>;
}

/**
 * In a 宝贝 card, under its chips: an image that did not work out (why, then
 * 重做 / 上传照片 / 不做这张), one marked 不做 (with 恢复), or a photo on its
 * way. Nothing for any other state. `restore` are the slots 恢复 clears (the
 * slot itself unless said).
 */
function SlotFixRow({ name, status, slot, restore, controls, testId }: {
  name: string;
  status: ListingSlotStatus | null;
  /** The image 重做 / 上传照片 / 不做这张 act on; null when it cannot be made. */
  slot: ProductImageSlot | null;
  restore?: readonly ProductImageSlot[];
  controls: SlotFixControls;
  testId: string;
}) {
  if (!status) return null;
  const restoring = restore ?? (slot ? [slot] : []);
  if (status.state === "skipped") {
    if (restoring.length === 0) return null;
    const busy = restoring.some((item) => controls.skipBusy[item.key]);
    return <div className="slot-fix skipped" data-testid={testId}>
      <span className="slot-fix-reason"><b>{name}</b>不做这张</span>
      <button type="button" className="button soft" data-testid={`${testId}-restore`} disabled={busy} aria-busy={busy} onClick={() => restoring.forEach((item) => controls.onSkip(item, false))}>恢复</button>
    </div>;
  }
  if (!slot) return null;
  if (status.state === "rejected" && status.submissionError) {
    return <div className="slot-fix rejected" data-testid={testId}>
      <span className="slot-fix-reason"><b>{name}</b><span data-testid={`${testId}-submission`}>提交未成功</span></span>
      <small className="slot-fix-hint">{status.submissionError.message}</small>
      <SlotSubmissionActions
        testId={`${testId}-actions`}
        error={status.submissionError}
        onRetry={() => controls.onRedo(slot)}
      />
    </div>;
  }
  const upload = controls.uploads[slot.key];
  if (status.state !== "failed") {
    return upload ? <div className="slot-fix" data-testid={testId}><span className="slot-fix-reason"><b>{name}</b></span><SlotUploadProgress state={upload} /></div> : null;
  }
  const kind = status.failureKind ?? "other";
  const busy = Boolean(controls.skipBusy[slot.key]);
  return <div className="slot-fix failed" data-testid={testId}>
    <span className="slot-fix-reason"><b>{name}</b><span data-testid={`${testId}-reason`}>{slotFailureTexts[kind]}</span></span>
    {slotFailureHints[kind] && <small className="slot-fix-hint">{slotFailureHints[kind]}</small>}
    {upload
      ? <SlotUploadProgress state={upload} />
      : <SlotFixActions
        testId={`${testId}-actions`}
        kind={kind}
        busy={busy}
        upscaling={Boolean(controls.upscaling[slot.key])}
        searchText={controls.searchText(slot)}
        onRedo={() => controls.onRedo(slot)}
        onUpload={(file) => controls.onUpload(slot, file)}
        onUpscale={() => controls.onUpscale(slot)}
        onSkip={() => controls.onSkip(slot, true)}
      />}
  </div>;
}

/**
 * 我上传的照片 of one colour (or the whole product), small: a tap shows it
 * large; 删除 asks once more (确认删除 / 算了) and removes only that photo.
 */
function OwnerPhotoStrip({ title, photos, controls, onPreview, testId }: {
  title: string;
  photos: readonly OwnerPhoto[];
  controls: OwnerPhotoControls;
  onPreview: (imageUrl: string, label: string) => void;
  testId: string;
}) {
  const [confirming, setConfirming] = useState("");
  if (photos.length === 0) return null;
  return <section className="owner-photos" aria-label={title} data-testid={testId}>
    <span className="owner-photos-title">{title}<small>{photos.length}</small></span>
    <ul className="owner-photo-list">{photos.map((photo) => {
      // A photo picked on this page shows from the file; others load their small copy, the full one on a tap.
      const picked = controls.previews[photo.id];
      const deleting = Boolean(controls.deleting[photo.id]);
      return <li key={photo.id} className="owner-photo">
        <button type="button" className="owner-photo-thumb" aria-label="看我上传的照片" onClick={() => onPreview(picked ?? photo.url, "我上传的照片")}><RetryImage src={picked ?? photo.previewUrl ?? photo.url} /></button>
        {confirming === photo.id
          ? <span className="owner-photo-confirm">
            <button type="button" className="owner-photo-delete danger" data-testid={`${testId}-confirm-${photo.id}`} onClick={() => {
              setConfirming("");
              controls.onDelete(photo);
            }}>确认删除</button>
            <button type="button" className="owner-photo-delete" data-testid={`${testId}-cancel-${photo.id}`} onClick={() => setConfirming("")}>算了</button>
          </span>
          : <button type="button" className="owner-photo-delete" data-testid={`${testId}-delete-${photo.id}`} disabled={deleting} aria-busy={deleting} onClick={() => setConfirming(photo.id)}>{deleting ? "删除中…" : "删除"}</button>}
      </li>;
    })}</ul>
  </section>;
}

/** An image waiting for 保留 / 不用, shown where it belongs. */
interface ListingPendingImage {
  output: VisualGenerationOutput;
  image: ListingImage;
  label: string;
}

function listingPendingImage(output: VisualGenerationOutput, kind: ListingImageKind, label: string): ListingPendingImage {
  return {
    output,
    label,
    image: { outputId: output.outputId, kind, imageUrl: output.imageUrl, sourceSkuId: output.sourceSkuId ?? null, skuIds: [], together: false, createdAt: output.createdAt ?? "" },
  };
}

/**
 * A product's card (one Taobao link): 完整度 (组合图 / 静物图 / 场景图 and
 * 一键补齐), the 5 主图 in one row, one container per colour (上架 switch,
 * 白底 / 细节 state, images waiting for 保留 / 不用 and the colour's kept
 * images), the shared 细节图 / 静物图 / 组合图 / 场景图 (合拍 included), 缺项
 * as warnings, then 导出到本地文件夹 with its result and earlier exports.
 */
function ListingCard({
  projectId, link, onOpenSingle, group, images, exports, canExport, notify, onPreview, onExported,
  jobs, outputs, queuePositions, usableSkuIds, requests, busyOutputs, preferredMainSkuId, onReview, onFill, onMakeScenes,
  slotFix, photos, photoControls,
}: {
  projectId: string;
  /** This 宝贝's own address (复制链接 / 单独打开). */
  link: string;
  /** 单独打开; null while this 宝贝 is already shown on its own. */
  onOpenSingle: (() => void) | null;
  group: VisualCatalogProduct;
  images: readonly ListingImage[];
  /** Its earlier exports; undefined until first read. */
  exports: ListingExportsState | undefined;
  canExport: boolean;
  notify: (message: string) => void;
  onPreview: (imageUrl: string, label: string) => void;
  /** Reads the product's exports again. */
  onExported: () => void;
  /** Every job and image of the theme: 完整度 counts all of them, not only kept ones. */
  jobs: readonly VisualGenerationJob[];
  outputs: readonly VisualGenerationOutput[];
  queuePositions: ReadonlyMap<string, number | null>;
  usableSkuIds: ReadonlySet<string>;
  requests: Readonly<Record<string, ProductImageRequestState>>;
  busyOutputs: Readonly<Record<string, "review" | "revise">>;
  /** The 主色 from step 1; the first colour for sale is used when it is not for sale. */
  preferredMainSkuId: string | null;
  onReview: (output: VisualGenerationOutput, choice: "keep" | "reject") => void;
  onFill: (input: ListingFillInput) => Promise<number>;
  onMakeScenes: (catalogProductId: string, forSale: string[]) => void;
  /** 重做 / 上传照片 / 不做这张 / 恢复 on images that did not work out. */
  slotFix: SlotFixControls;
  /** This product's 我上传的照片, newest first. */
  photos: readonly OwnerPhoto[];
  photoControls: OwnerPhotoControls;
}) {
  const skus = useMemo(() => listingSkus(group), [group]);
  const skuOrder = useMemo(() => skus.map((sku) => sku.sourceSkuId), [skus]);
  const stateKey = `${projectId}:${group.productId}`;
  const [cardState, setCardState] = useState<ListingCardState>(() => startListingCardState(stateKey, skuOrder));
  // 导出 is on its way; the ref also stops a quick double tap.
  const [exporting, setExporting] = useState(false);
  const exportingRef = useRef(false);
  // No answer in time: the export most likely goes on; 刷新 shows it in 导出记录.
  const [exportSlow, setExportSlow] = useState(false);
  const [result, setResult] = useState<DoubaoExportResult | null>(null);
  // 导出到 as the owner picked it; null: the folder picked for them.
  const [chosenTarget, setChosenTarget] = useState<string | null>(null);
  // The folder the last export went into, until the folder list is read again.
  const [exportedFolder, setExportedFolder] = useState("");
  // A folder path the clipboard refused, shown to copy by hand.
  const [pathToCopy, setPathToCopy] = useState("");
  // 复制链接 could not reach the clipboard: the address shows to copy by hand.
  const [linkCopyFailed, setLinkCopyFailed] = useState(false);
  // 一键补齐 is sending this card's requests; the ref also stops a quick double tap.
  const [filling, setFilling] = useState(false);
  const fillingRef = useRef(false);
  // The empty 主图 place whose ＋ picker is open (0-based), else null.
  const [mainPicker, setMainPicker] = useState<number | null>(null);
  const mainSectionRef = useRef<HTMLElement | null>(null);
  const mainPickerRef = useRef<HTMLDivElement | null>(null);
  // The ＋ that opened the picker gets the focus back when it closes.
  const mainPickerOpener = useRef<HTMLButtonElement | null>(null);

  // The colours for sale: 上架 on (the default for every colour but those the 货盘 shows sold out).
  const listedSkus = listedListingSkus(skus, cardState.listing);
  const listedIds = listedSkus.map((sku) => sku.sourceSkuId);
  const listedSet = new Set(listedIds);
  // 完整度: what this link still lacks, from every job and image of the product.
  const forSaleVariants = listedSkus.map((sku): ListingVariantInput => ({ sourceSkuId: sku.sourceSkuId, skuId: sku.skuId, usable: usableSkuIds.has(sku.skuId) }));
  const completeness = listingCompleteness(jobs, outputs, group, forSaleVariants, { mainSkuId: preferredMainSkuId, requests, positions: queuePositions, skipped: slotFix.skipped });
  const fillSlots = listingFillSlots(completeness, group.productId);
  // What 重做 / 上传照片 / 不做这张 act on for the product's 组合图 and 静物图.
  const comboSlot = listingComboSlot(completeness, group.productId);
  const stillSlot = listingStillSlot(completeness, group.productId);
  // 恢复 on the 静物图 clears 不做 on whichever colour it was set.
  const skippedStillSlots = completeness.variants
    .map((variant) => listingVariantSlot("truth_neutral", group.productId, variant))
    .filter((slot) => slotFix.skipped.has(slot.key));
  // 我上传的照片: each colour's in its container; whole-product ones (and other colours') with 完整度.
  const cardSkuIds = new Set(skus.map((sku) => sku.sourceSkuId));
  const productPhotos = photos.filter((photo) => !photo.sourceSkuId || !cardSkuIds.has(photo.sourceSkuId));
  const statusById = new Map(completeness.variants.map((variant) => [variant.sourceSkuId, variant]));
  const inStockIds = inStockListingSkuIds(skus);
  const switchedOffInStock = skus.filter((sku) => !listedSet.has(sku.sourceSkuId) && inStockIds.includes(sku.sourceSkuId));
  const complete = completeness.variants.length > 0 && completeness.done === completeness.total;
  const mainSourceSkuId = skus.find((sku) => sku.skuId === completeness.mainSkuId)?.sourceSkuId ?? null;
  // Images of colours switched off stay out of this link.
  const saleImages = listingSaleImages(images, listedIds);
  const mainIds = listingMainIds(cardState.mainOrder, saleImages, listedIds, mainSourceSkuId);
  const imageGroups = listingImageGroups(saleImages, mainIds, skuOrder);
  const variantImagesById = new Map(imageGroups.variants.map((entry) => [entry.sourceSkuId, entry]));
  const mainIdSet = new Set(mainIds);
  const mainFull = mainIds.length >= LISTING_MAX_MAIN_IMAGES;
  // ＋ offers every kept image not in 主图 yet; 补满 5 张 (only once the owner
  // arranged 主图) adds what 主图 would take by itself, keeping their order.
  const mainChoices = listingMainChoices(saleImages, mainIds, listedIds, mainSourceSkuId);
  const mainFilled = cardState.mainOrder !== null && !mainFull ? filledListingMainIds(mainIds, saleImages, listedIds, mainSourceSkuId) : mainIds;
  const canFillMain = mainFilled.length > mainIds.length;
  const variantNames = new Map(skus.map((sku) => [sku.sourceSkuId, sku.variant]));
  const gaps = listingGapTexts(completeness, mainIds.length);
  // The export names the colours for sale the generator knows (a product photo or a kept image).
  const exportSkuIds = listedSkus
    .filter((sku) => usableSkuIds.has(sku.skuId) || saleImages.some((image) => image.skuIds.includes(sku.sourceSkuId)))
    .map((sku) => sku.sourceSkuId);
  // 导出到: offered once the folders are read, so an export never makes a second folder for this product.
  const exportData = exports?.data ?? null;
  const targetFolders = exportData ? exportTargetFolders(exportData.folders, exportedFolder) : [];
  const suggestedNewFolder = exportData?.suggestedNewFolder ?? null;
  const target = exportData
    ? exportTarget(targetFolders, suggestedNewFolder, { catalogProductId: group.productId, names: exportProductNames(group) }, chosenTarget)
    : null;
  const exportBody = listingExportBody(projectId, group.productId, mainIds, imageGroups, exportSkuIds, target ?? EXPORT_NEW_FOLDER);
  const exportReady = exportBody.mainOutputIds.length > 0 && exportBody.listedSkuIds.length > 0;
  const history = exportData?.exports ?? [];
  const productPending = [
    ...(completeness.combo?.pending ?? []).map((output) => listingPendingImage(output, "combo", "组合图")),
    ...completeness.still.pending.map((output) => listingPendingImage(output, "still", "模特图")),
    ...completeness.scene.pending.map((output) => listingPendingImage(output, "scene", "场景图")),
  ];

  // The 主图 order and 上架 switches are kept in this browser.
  function change(next: (current: ListingCardState) => ListingCardState) {
    setCardState((current) => {
      const updated = next(current);
      writeListingCardState(stateKey, updated);
      return updated;
    });
  }

  function setListed(sourceSkuId: string, value: boolean) {
    change((current) => ({ ...current, listing: { ...current.listing, [sourceSkuId]: value } }));
  }

  // 全部有货颜色上架: every colour in stock switched on at once.
  function listAllInStock() {
    change((current) => ({ ...current, listing: { ...current.listing, ...Object.fromEntries(inStockIds.map((id) => [id, true])) } }));
  }

  function arrangeMain(next: string[]) {
    change((current) => ({ ...current, mainOrder: next }));
  }

  function addMain(outputId: string) {
    if (mainFull) {
      notify(`主图最多 ${LISTING_MAX_MAIN_IMAGES} 张`);
      return;
    }
    arrangeMain([...mainIds, outputId]);
  }

  // 补满 5 张: the owner's 主图 stay as arranged; the empty places fill up.
  function fillMain() {
    if (!canFillMain) return;
    arrangeMain(mainFilled);
    notify(`主图补上了 ${mainFilled.length - mainIds.length} 张`);
  }

  // ＋ on an empty place: opens (or closes) the picker for it.
  function toggleMainPicker(index: number, opener: HTMLButtonElement) {
    mainPickerOpener.current = opener;
    setMainPicker((current) => current === index ? null : index);
  }

  function closeMainPicker(focusOpener: boolean) {
    setMainPicker(null);
    if (focusOpener && mainPickerOpener.current?.isConnected) mainPickerOpener.current.focus();
  }

  // A choice in the picker goes into that place.
  function chooseMain(outputId: string) {
    if (mainPicker === null) return;
    if (mainFull) {
      notify(`主图最多 ${LISTING_MAX_MAIN_IMAGES} 张`);
      setMainPicker(null);
      return;
    }
    arrangeMain(placeListingImage(mainIds, outputId, mainPicker));
    setMainPicker(null);
  }

  // A picker for a place that is no longer empty closes.
  useEffect(() => {
    if (mainPicker !== null && (mainFull || mainPicker < mainIds.length)) setMainPicker(null);
  }, [mainFull, mainIds.length, mainPicker]);

  // Open picker: Escape or a tap anywhere outside it (other than a ＋ of this card) closes it.
  useEffect(() => {
    if (mainPicker === null) return;
    const picker = mainPickerRef.current;
    const first = picker?.querySelector<HTMLButtonElement>(".listing-main-choice") ?? picker?.querySelector<HTMLButtonElement>("button");
    first?.focus({ preventScroll: true });
    picker?.scrollIntoView?.({ block: "nearest" });
    function closeOnEscape(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      closeMainPicker(true);
    }
    function closeOnTapOutside(event: PointerEvent) {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (mainPickerRef.current?.contains(target)) return;
      if (mainSectionRef.current?.contains(target) && target.closest(".listing-main-add")) return;
      setMainPicker(null);
    }
    window.addEventListener("keydown", closeOnEscape, true);
    document.addEventListener("pointerdown", closeOnTapOutside);
    return () => {
      window.removeEventListener("keydown", closeOnEscape, true);
      document.removeEventListener("pointerdown", closeOnTapOutside);
    };
  }, [mainPicker]);

  // Under every kept image outside 主图: put it in, or say it already is one.
  function mainButton(image: ListingImage): ReactNode {
    if (mainIdSet.has(image.outputId)) return <button type="button" disabled>已在主图</button>;
    return <button type="button" data-testid={`listing-main-add-${image.outputId}`} disabled={mainFull} onClick={() => addMain(image.outputId)}>放进主图</button>;
  }

  function pendingThumb({ output, image, label }: ListingPendingImage): ReactNode {
    const reviewing = Boolean(busyOutputs[output.outputId]);
    return <ListingThumb key={output.outputId} image={image} name={label} tag="待你挑" onPreview={onPreview}>
      <button type="button" data-testid={`listing-keep-${output.outputId}`} disabled={reviewing} aria-busy={reviewing} onClick={() => onReview(output, "keep")}>保留</button>
      <button type="button" data-testid={`listing-reject-${output.outputId}`} disabled={reviewing} aria-busy={reviewing} onClick={() => onReview(output, "reject")}>不用</button>
    </ListingThumb>;
  }

  // 一键补齐: the workbench decides again from the newest records and sends
  // what is 缺 or 没做成, one request after another.
  async function fillMissing() {
    if (fillingRef.current) return;
    fillingRef.current = true;
    setFilling(true);
    try {
      await onFill({ group, onSale: forSaleVariants, mainSkuId: completeness.mainSkuId });
    } finally {
      fillingRef.current = false;
      setFilling(false);
    }
  }

  // 去做场景图: step 3 with this product, its 主色 first; the owner picks the references.
  function makeScenes() {
    const usable = forSaleVariants.filter((variant) => variant.usable).map((variant) => variant.skuId);
    onMakeScenes(group.productId, completeness.mainSkuId ? [completeness.mainSkuId, ...usable] : usable);
  }

  // Once a folder list names the folder the last export went into as this
  // product's, the list alone says so (an older or slower list does not).
  useEffect(() => {
    if (exportedFolder && exportData?.folders.some((folder) => folder.name === exportedFolder && folder.isLinked)) setExportedFolder("");
  }, [exportData, exportedFolder]);

  // 导出到本地文件夹: the Mac writes a new 网站导出_vNNN folder inside the
  // folder picked in 导出到; nothing else there is touched.
  async function exportImages() {
    if (exportingRef.current) return;
    if (!canExport || !exportReady || !target) {
      notify(!canExport ? "只有管理员能导出" : exportBody.mainOutputIds.length === 0 ? "主图至少 1 张" : !exportReady ? "还没有上架的颜色" : "文件夹还没读到，请稍等或刷新");
      return;
    }
    exportingRef.current = true;
    setExporting(true);
    setExportSlow(false);
    try {
      const answer = await api<unknown>("/api/doubao-export", {
        method: "POST",
        ...jsonBody(exportBody),
        timeoutMs: LISTING_EXPORT_TIMEOUT_MS,
      });
      const exported = normalizeExportResult(answer);
      if (!exported) throw new ApiError(502, "INVALID_RESPONSE", "导出没成功，请稍后再试");
      setResult(exported);
      // That folder is this product's now (a new one too): the next export goes there again.
      setExportedFolder(exported.targetFolder);
      setChosenTarget(exported.targetFolder || null);
      setPathToCopy("");
      notify(`已导出 ${exported.version}`);
    } catch (caught) {
      notify(exportFailureText(caught));
      // No answer in time: the Mac most likely goes on; the list shows it once done.
      if (isSlowNetworkError(caught)) {
        setExportSlow(true);
        // A new folder is most likely being made under the name offered: the
        // next folder list picks it (or this product's folder) rather than 新建 again.
        if (exportBody.targetFolder === EXPORT_NEW_FOLDER) setChosenTarget(suggestedNewFolder);
      }
      // The folder is gone (moved or renamed in Finder): pick again from the new list.
      if (caught instanceof ApiError && caught.code === "TARGET_FOLDER_NOT_FOUND") {
        setExportedFolder("");
        setChosenTarget(null);
      }
    } finally {
      exportingRef.current = false;
      setExporting(false);
      onExported();
    }
  }

  // 复制路径: the folder as the owner finds it in FurryJoy.
  async function copyPath(dir: string) {
    const folder = exportFolderText(dir);
    if (await copyText(folder)) {
      setPathToCopy("");
      notify("路径已复制");
    } else {
      setPathToCopy(folder);
      notify("没能自动复制，请手动复制");
    }
  }

  // 复制链接: this 宝贝's own address, to send to someone or to an agent.
  async function copyLink() {
    if (await copyText(link)) {
      setLinkCopyFailed(false);
      notify("链接已复制");
    } else {
      setLinkCopyFailed(true);
      notify("没能自动复制，请手动复制");
    }
  }

  // 单独打开 is a real link (it opens in a new tab too); a plain tap stays on this page.
  function openSingle(event: MouseEvent<HTMLAnchorElement>) {
    if (!onOpenSingle || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    onOpenSingle();
  }

  return <article className="listing-card" data-testid={`listing-card-${group.productId}`}>
    <header className="product-image-card-head listing-card-head">
      <ProductImage src={group.sourceImage?.url ?? null} alt="" />
      <div>
        <strong>{group.label}</strong>
        <small>{`${skus.length} 个规格 · ${images.length} 张保留的图`}</small>
        <small className="listing-card-id" data-testid={`listing-id-${group.productId}`}>宝贝编号 <b>{group.productId}</b></small>
      </div>
      <div className="listing-card-links">
        <button type="button" className="button soft" data-testid={`listing-copy-link-${group.productId}`} onClick={() => void copyLink()}>复制链接</button>
        {onOpenSingle && <a className="button soft" href={link} data-testid={`listing-open-${group.productId}`} onClick={openSingle}>单独打开</a>}
      </div>
      {linkCopyFailed && <input className="listing-handoff listing-link-input" readOnly value={link} aria-label="这款宝贝的链接" data-testid={`listing-link-${group.productId}`} onFocus={(event) => event.currentTarget.select()} />}
    </header>

    <section className={`listing-completeness${complete ? " done" : ""}`} aria-label="完整度" data-testid={`listing-completeness-${group.productId}`}>
      <span className="listing-section-title">完整度<small>{`${completeness.done}/${completeness.total}`}</small>{complete && <small className="listing-completeness-done">图都齐了</small>}</span>
      {completeness.variants.length === 0
        ? <small className="listing-empty-note">还没有上架的颜色</small>
        : <div className="listing-chips">
          {completeness.combo && <ListingChip name="组合图" status={completeness.combo} testId={`listing-chip-combo-${group.productId}`} />}
          <ListingChip name="模特图" status={completeness.still} testId={`listing-chip-still-${group.productId}`} />
          <ListingChip name="场景图" status={completeness.scene} testId={`listing-chip-scene-${group.productId}`} />
        </div>}
      {completeness.combo && <SlotFixRow name="组合图" status={completeness.combo} slot={comboSlot} controls={slotFix} testId={`listing-fix-combo-${group.productId}`} />}
      <SlotFixRow name="模特图" status={completeness.still} slot={stillSlot} restore={skippedStillSlots} controls={slotFix} testId={`listing-fix-still-${group.productId}`} />
      <OwnerPhotoStrip title="我上传的照片 · 整款" photos={productPhotos} controls={photoControls} onPreview={onPreview} testId={`listing-owner-photos-${group.productId}`} />
      {productPending.length > 0 && <div className="listing-pending" data-testid={`listing-pending-${group.productId}`}>
        <span className="listing-section-title">待你挑<small>{productPending.length}</small></span>
        <div className="listing-thumbs">{productPending.map(pendingThumb)}</div>
      </div>}
      <div className="listing-completeness-actions">
        {switchedOffInStock.length > 0 && <button type="button" className="button soft" data-testid={`listing-list-all-${group.productId}`} onClick={listAllInStock}>全部有货颜色上架</button>}
        {completeness.scene.state !== "kept" && <button type="button" className="button soft" data-testid={`listing-make-scenes-${group.productId}`} disabled={!completeness.mainSkuId} onClick={makeScenes}>去做场景图</button>}
        <button type="button" className="button primary" data-testid={`listing-fill-${group.productId}`} disabled={fillSlots.length === 0 || filling} aria-busy={filling} onClick={() => void fillMissing()}>{filling ? "提交中…" : `一键补齐（${fillSlots.length} 张）`}</button>
      </div>
    </section>

    <section className="listing-section" aria-label="主图" data-testid={`listing-main-${group.productId}`} ref={mainSectionRef}>
      <div className="listing-main-head">
        <span className="listing-section-title">主图<small>{`${imageGroups.main.length}/${LISTING_MAX_MAIN_IMAGES}`}</small></span>
        {canFillMain && <button type="button" className="listing-main-fill" data-testid={`listing-main-fill-${group.productId}`} onClick={fillMain}>补满 5 张</button>}
      </div>
      <ol className="listing-main-strip">{Array.from({ length: LISTING_MAX_MAIN_IMAGES }, (_, index) => {
        const image = imageGroups.main[index];
        if (!image) {
          const open = mainPicker === index;
          return <li key={`empty-${index}`} className={`listing-main-empty${open ? " open" : ""}`}>
            <button type="button" className="listing-main-add" aria-label={`主图空位 ${index + 1}，放一张图`} title="放一张图" aria-haspopup="dialog" aria-expanded={open} data-testid={`listing-main-add-${group.productId}-${index + 1}`} onClick={(event) => toggleMainPicker(index, event.currentTarget)}>
              <span aria-hidden="true">＋</span>
              <small aria-hidden="true">{index + 1}</small>
            </button>
          </li>;
        }
        const kind = listingImageName(image);
        return <li key={image.outputId} data-testid={`listing-main-tile-${image.outputId}`}>
          <button type="button" className="listing-main-image" aria-label={`看第 ${index + 1} 张主图`} onClick={() => onPreview(image.imageUrl, `主图 ${index + 1} · ${kind}`)}>
            <RetryImage src={outputPreviewUrl(image.imageUrl)} />
            <b>{index + 1}</b>
            <small>{kind}</small>
          </button>
          <span className="listing-main-tools">
            <button type="button" aria-label="往前" title="往前" disabled={index === 0} onClick={() => arrangeMain(moveListingImage(mainIds, image.outputId, -1))}>↑</button>
            <button type="button" aria-label="往后" title="往后" disabled={index === imageGroups.main.length - 1} onClick={() => arrangeMain(moveListingImage(mainIds, image.outputId, 1))}>↓</button>
            <button type="button" aria-label="移出主图" title="移出主图" data-testid={`listing-main-remove-${image.outputId}`} onClick={() => arrangeMain(mainIds.filter((id) => id !== image.outputId))}>×</button>
          </span>
        </li>;
      })}</ol>
      {mainPicker !== null && <div className="listing-main-picker" role="dialog" aria-label="选一张放进主图" ref={mainPickerRef} data-testid={`listing-main-picker-${group.productId}`}>
        <div className="listing-main-picker-head">
          <strong>{`放进第 ${Math.min(mainPicker, mainIds.length) + 1} 张主图`}</strong>
          <button type="button" aria-label="关闭" title="关闭" data-testid={`listing-main-picker-close-${group.productId}`} onClick={() => closeMainPicker(true)}>×</button>
        </div>
        {mainChoices.length === 0
          ? <small className="listing-empty-note">保留的图都在主图里了</small>
          : <ul className="listing-main-choices">{mainChoices.map((image) => {
            const colour = image.skuIds.length === 1 ? variantNames.get(image.skuIds[0]) ?? "" : "";
            const label = [listingImageName(image), colour].filter(Boolean).join(" · ");
            return <li key={image.outputId}>
              <button type="button" className="listing-main-choice" aria-label={`放进主图：${label}`} title={label} data-testid={`listing-main-choice-${image.outputId}`} onClick={() => chooseMain(image.outputId)}>
                <span className="listing-main-choice-image"><RetryImage src={outputPreviewUrl(image.imageUrl)} /></span>
                <small>{listingImageName(image)}</small>
                {colour && <small>{colour}</small>}
              </button>
            </li>;
          })}</ul>}
      </div>}
    </section>

    <ul className="listing-skus" aria-label="每个颜色">{skus.map((sku) => {
      const listed = listedSet.has(sku.sourceSkuId);
      const status = statusById.get(sku.sourceSkuId);
      const own = variantImagesById.get(sku.sourceSkuId);
      // Its 规格图 first, then its 细节图, its scenes and older 白底图.
      const kept = own ? [...own.details, ...own.scenes, ...own.whites] : [];
      const pending = status
        ? [...status.white.pending.map((output) => listingPendingImage(output, "white", "白底图")), ...status.detail.pending.map((output) => listingPendingImage(output, "detail", "细节图"))]
        : [];
      return <li key={sku.sourceSkuId} className={`listing-sku${listed ? "" : " folded"}`} data-testid={`listing-variant-${sku.sourceSkuId}`}>
        <div className="listing-sku-row">
          <span className="listing-sku-copy">
            <span className="listing-sku-name">
              <strong>{sku.variant}</strong>
              {listed && status?.skuId === completeness.mainSkuId && <b>主色</b>}
            </span>
            <span className="listing-sku-stock">
              <small>{sku.stock === null ? "库存未知" : `库存 ${sku.stock}`}</small>
              {sku.soldOut && <small className="listing-sku-soldout" title={PALLET_STOCK_NOTE} data-testid={`listing-soldout-${sku.sourceSkuId}`}>{PALLET_SOLD_OUT_TEXT}</small>}
            </span>
          </span>
          {/* 上架 for every colour; one the 货盘 shows sold out starts off. */}
          <label className="listing-sku-onsale">
            <input type="checkbox" checked={listed} onChange={(event) => setListed(sku.sourceSkuId, event.target.checked)} aria-label={`${sku.variant}上架`} data-testid={`listing-onsale-${sku.sourceSkuId}`} />
            <span>上架</span>
          </label>
        </div>
        {listed && status && <>
          <span className="listing-chips">
            <ListingChip name="白底" status={status.white} testId={`listing-chip-white-${sku.sourceSkuId}`} />
            <ListingChip name="细节" status={status.detail} testId={`listing-chip-detail-${sku.sourceSkuId}`} />
          </span>
          <SlotFixRow name="白底" status={status.white} slot={status.usable ? listingVariantSlot("truth_white", group.productId, status) : null} controls={slotFix} testId={`listing-fix-white-${sku.sourceSkuId}`} />
          <SlotFixRow name="细节" status={status.detail} slot={status.usable ? listingVariantSlot("truth_detail", group.productId, status) : null} controls={slotFix} testId={`listing-fix-detail-${sku.sourceSkuId}`} />
          {pending.length > 0 && <div className="listing-thumbs" data-testid={`listing-pending-${sku.sourceSkuId}`}>{pending.map(pendingThumb)}</div>}
          <div className="listing-thumbs">
            {own?.skuImage
              ? <ListingThumb image={own.skuImage} name="规格图" onPreview={onPreview}>{mainButton(own.skuImage)}</ListingThumb>
              : <span className="listing-sku-missing" data-testid={`listing-sku-missing-${sku.sourceSkuId}`}>还没有白底图</span>}
            {kept.map((image) => <ListingThumb key={image.outputId} image={image} onPreview={onPreview}>{mainButton(image)}</ListingThumb>)}
          </div>
          <OwnerPhotoStrip title="我上传的照片" photos={photos.filter((photo) => photo.sourceSkuId === sku.sourceSkuId)} controls={photoControls} onPreview={onPreview} testId={`listing-owner-photos-${sku.sourceSkuId}`} />
        </>}
      </li>;
    })}</ul>

    {([
      ["细节图", imageGroups.details],
      ["模特图", imageGroups.stills],
      ["组合图", imageGroups.combos],
      ["场景图", imageGroups.scenes],
      ["其他图", imageGroups.others],
    ] as const).map(([title, list]) => list.length > 0 && <section className="listing-section" aria-label={title} key={title}>
      <span className="listing-section-title">{title}<small>{list.length}</small></span>
      <div className="listing-thumbs">{list.map((image) => <ListingThumb key={image.outputId} image={image} onPreview={onPreview}>{mainButton(image)}</ListingThumb>)}</div>
    </section>)}

    <section className={`listing-missing${gaps.length === 0 ? " done" : ""}`} aria-label="缺项" aria-live="polite" data-testid={`listing-missing-${group.productId}`}>
      <span className="listing-section-title">{gaps.length === 0 ? "图都齐了" : `还差 ${gaps.length} 项，也可以先导出`}</span>
      {gaps.length > 0 && <ul>{gaps.map((item) => <li key={item}>{item}</li>)}</ul>}
    </section>

    {canExport && <div className="listing-export-target" data-testid={`listing-export-target-${group.productId}`}>
      {target
        ? <>
          <label>
            <span>导出到</span>
            <select value={target} disabled={exporting} aria-label="导出到" data-testid={`listing-export-folder-${group.productId}`} onChange={(event) => setChosenTarget(event.currentTarget.value)}>
              {[EXPORT_NEW_FOLDER, ...targetFolders.map((folder) => folder.name)].map((value) => <option key={value} value={value}>{exportTargetLabel(value, targetFolders, suggestedNewFolder)}</option>)}
            </select>
          </label>
          <small>{exportTargetNote(target, targetFolders, chosenTarget)}</small>
        </>
        : <small role="status">{exports?.failed ? "文件夹暂时读不到，请刷新" : "正在读取文件夹…"}</small>}
    </div>}

    <div className="listing-actions">
      {exporting
        ? <small className="listing-export-note" role="status">正在导出，大约要 1 分钟</small>
        : exportSlow && <small className="listing-export-note" role="status" data-testid={`listing-export-slow-${group.productId}`}>网络有点慢，导出可能还在进行，稍后点刷新看导出记录</small>}
      <button type="button" className="button primary" data-testid={`listing-export-${group.productId}`} disabled={!canExport || !exportReady || !target || exporting} aria-busy={exporting} onClick={() => void exportImages()}>{exporting ? "导出中…" : "导出到本地文件夹"}</button>
      {!canExport && <small className="listing-admin-note" data-testid={`listing-admin-only-${group.productId}`}>只有管理员能导出</small>}
    </div>

    {result && <section className="listing-result" role="status" data-testid={`listing-result-${group.productId}`}>
      <strong>{`已导出 ${result.version}`}</strong>
      <span className="listing-export-path" data-testid={`listing-export-path-${group.productId}`}>{exportFolderText(result.exportDir)}</span>
      {result.files.length > 0 && <small>{exportFilesText(result.files)}</small>}
      <button type="button" className="button primary" data-testid={`listing-copy-path-${group.productId}`} onClick={() => void copyPath(result.exportDir)}>复制路径</button>
    </section>}
    {pathToCopy && <input className="listing-handoff" readOnly value={pathToCopy} aria-label="文件夹路径" data-testid={`listing-path-${group.productId}`} onFocus={(event) => event.currentTarget.select()} />}

    {history.length > 0 && <section className="listing-history" aria-label="导出记录" data-testid={`listing-history-${group.productId}`}>
      <span className="listing-section-title">导出记录<small>{history.length}</small></span>
      <ol>{history.map((item) => <li key={`${item.version} ${exportFolderText(item.exportDir)}`}>
        <b>{item.version}</b>
        <span>{[formatListingTime(item.createdAt), item.fileCount > 0 ? `${item.fileCount} 个文件` : ""].filter(Boolean).join(" · ")}</span>
        <button type="button" data-testid={`listing-history-copy-${group.productId}-${item.version}`} onClick={() => void copyPath(item.exportDir)}>复制路径</button>
        <small>{exportFolderText(item.exportDir)}</small>
      </li>)}</ol>
    </section>}
    {exports?.failed && !exports.data && <small className="listing-empty-note" role="status">导出记录暂时读不到，请刷新</small>}
  </article>;
}

const offerFilters: Array<{ value: "all" | MarketingOfferType; label: string }> = [
  { value: "all", label: "全部" },
  { value: "candidate_pool", label: "备选 · 售价未定" },
  { value: "single_9_9", label: "9.9 包邮" },
  { value: "choose_2_9_9", label: "9.9 任选 2" },
  { value: "buy_3_get_1_19_9", label: "19.9 买 3 送 1" },
  { value: "buy_3_get_1_24_9", label: "24.9 买 3 送 1" },
];

function MarketingCollectionRail({ data, error, offerFilter, activeId, onFilter, onSelect, onStartVisual }: {
  data: MarketingCollectionsData | null;
  error: string;
  offerFilter: "all" | MarketingOfferType;
  activeId: string;
  onFilter: (value: "all" | MarketingOfferType) => void;
  onSelect: (id: string) => void;
  onStartVisual: (id: string) => void;
}) {
  const items = data?.items.filter((item) => offerFilter === "all" || item.offer.type === offerFilter) ?? [];
  const finalProductCount = data?.items.reduce((total, item) => total + item.productCount, 0) ?? 0;
  const needsHdCount = data?.items.reduce((total, item) => total + item.needsHdCount, 0) ?? 0;
  return (
    <section className="marketing-panel glass-panel" aria-label="主题选品方案">
      <header className="marketing-header">
        <div><strong>狗狗主题选品方案</strong><span>{data ? `${data.items.length} 个主题 · ${finalProductCount} 款高清${needsHdCount > 0 ? ` · ${needsHdCount} 款待补高清` : ""}` : error ? "读取失败" : "读取中…"}</span></div>
        <div className="marketing-tabs" role="group" aria-label="促销方式">
          {offerFilters.map((filter) => <button type="button" key={filter.value} className={offerFilter === filter.value ? "active" : ""} aria-pressed={offerFilter === filter.value} onClick={() => onFilter(filter.value)}>{filter.label}</button>)}
        </div>
      </header>
      {error && <div className="marketing-error">主题读取失败：{error}</div>}
      {!error && <div className="collection-rail">
        {items.map((collection) => <CollectionCard key={collection.id} collection={collection} active={collection.id === activeId} onSelect={() => onSelect(collection.id)} onStartVisual={() => onStartVisual(collection.id)} />)}
      </div>}
    </section>
  );
}

function CollectionCard({ collection, active, onSelect, onStartVisual }: { collection: MarketingCollectionSummary; active: boolean; onSelect: () => void; onStartVisual: () => void }) {
  const palette = collection.palette.length >= 2 ? collection.palette : ["#edf4ff", "#f8f1ff"];
  const isCandidate = collection.offer.type === "candidate_pool";
  return (
    <article
      className={`collection-card ${active ? "active" : ""}`}
      style={{ "--campaign-a": palette[0], "--campaign-b": palette[1] } as React.CSSProperties}
    >
      <button
        type="button"
        className="collection-card-main"
        onClick={onSelect}
        aria-pressed={active}
        aria-label={`选中主题：${collection.title}，${collection.offer.label}，${collection.offerReady ? (isCandidate ? "可以挑选" : "可以组活动") : "待补高清"}`}
      >
        <span className="collection-mosaic" aria-hidden="true">
          {collection.covers.slice(0, 4).map((cover) => <ProductImage key={cover.productId} src={cover.thumbnailUrl} alt="" />)}
          {Array.from({ length: Math.max(0, 4 - collection.covers.length) }, (_, index) => <span className="mosaic-fallback" key={index}>🐾</span>)}
        </span>
        <span className="collection-copy">
          <span className="collection-offer">{collection.offer.label}</span>
          <strong>{collection.title}</strong>
          <span className="collection-meta">{collection.productCount} 款{isCandidate ? "高清备选" : "活动商品"} · 原 {collection.candidateCount} 款</span>
          <span className={`collection-meta image-readiness-line ${collection.offerReady ? "ready" : "warning"}`}>{collection.offerReady ? `${isCandidate ? "可以挑选" : "可以组活动"}${collection.needsHdCount > 0 ? ` · ${collection.needsHdCount} 款待补图` : ""}` : `图片不够 · ${collection.needsHdCount} 款待补高清`}</span>
          <span className="collection-budget">{isCandidate ? <>最高进价 {formatPrice(collection.worstGoodsCost)} · 售价未定</> : <>成本≤{formatPrice(collection.worstGoodsCost)} · 留给运费等 {formatPrice(collection.remainingBeforeNonGoodsCosts)}</>}</span>
          <span className="collection-detail-hint">{active ? "已选中" : "选中主题"} <b aria-hidden="true">›</b></span>
        </span>
      </button>
      <button type="button" className="collection-visual-start" data-testid={`start-visual-${collection.id}`} aria-label={`去做图：${collection.title}`} onClick={onStartVisual}>去做图 <span aria-hidden="true">→</span></button>
    </article>
  );
}

function ActiveCampaign({ collection, onDetails, onStartVisual, onExit }: { collection: MarketingCollectionSummary; onDetails: () => void; onStartVisual: () => void; onExit: () => void }) {
  const isCandidate = collection.offer.type === "candidate_pool";
  return (
    <section className="active-campaign glass-panel" aria-label={`当前主题：${collection.title}`}>
      <div className="campaign-title"><span style={{ background: collection.palette[0] }} aria-hidden="true" /><div><strong>{collection.title}</strong><small>{collection.offer.label} · {collection.themeTags.join(" · ")}</small></div></div>
      <div className="campaign-economics">
        <span><b>{collection.choiceCount}</b> 款{isCandidate ? "高清备选" : "活动商品"}{collection.giftCount > 0 && ` + ${collection.giftCount} 款赠品`}</span>
        <span>{isCandidate ? "最高进价" : "最高成本"} <b>{formatPrice(collection.worstGoodsCost)}</b></span>
        <span>{isCandidate ? <>售价 <b>未定</b></> : <>留给运费等 <b>{formatPrice(collection.remainingBeforeNonGoodsCosts)}</b></>}</span>
        <span>{collection.unknownStockCount > 0 ? <><b>{collection.unknownStockCount}</b> 款库存待问</> : <><b>全部</b> 库存已知</>}</span>
        <span>{collection.offerReady ? <><b>{collection.imageReadyCount}/{collection.productCount}</b> 图片合格</> : <><b>{collection.needsHdCount}</b> 款待补高清</>}</span>
      </div>
      <div className="campaign-caution"><span>{collection.skuRule}</span><small>{collection.warnings.join(" · ")}</small></div>
      <div className="campaign-actions"><button type="button" className="button primary" data-testid={`active-start-visual-${collection.id}`} aria-label={`去做图：${collection.title}`} onClick={onStartVisual}>去做图</button><button type="button" className="button soft" onClick={onDetails}>看详情</button><button type="button" className="button ghost" onClick={onExit}>退出主题</button></div>
    </section>
  );
}

function conservativeProductPrice(product: ProductSummary): number | null {
  return product.priceMax ?? product.priceMin;
}

function maximumProductPrice(products: ProductSummary[]): number | null {
  if (products.length === 0) return 0;
  const prices = products.map(conservativeProductPrice);
  if (prices.some((price) => price === null)) return null;
  return Math.max(...(prices as number[]));
}

function CampaignDetailDrawer({ collection, onClose, onStartVisual, onOpenProduct }: {
  collection: MarketingCollectionSummary;
  onClose: () => void;
  onStartVisual: () => void;
  onOpenProduct: (id: string) => void;
}) {
  const [data, setData] = useState<CatalogData | null>(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setError("");
    try {
      const next = await api<CatalogData>(`/api/catalog?collection=${encodeURIComponent(collection.id)}&page=1&pageSize=100&sort=price_asc`);
      setData(next);
    } catch (caught) {
      setError(errorMessage(caught));
    }
  }, [collection.id]);

  useEffect(() => { setData(null); void load(); }, [load]);
  useEffect(() => {
    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [onClose]);

  const productsById = useMemo(() => new Map(data?.items.map((product) => [product.id, product]) ?? []), [data]);
  const choiceProducts = collection.members.flatMap((member) => {
    if (member.role !== "choice") return [];
    const product = productsById.get(member.productId);
    return product ? [product] : [];
  });
  const giftProducts = collection.members.flatMap((member) => {
    if (member.role !== "gift") return [];
    const product = productsById.get(member.productId);
    return product ? [product] : [];
  });
  const suppliers = [...new Set((data?.items ?? []).map((product) => product.supplier.name))];
  const choiceUnitWorst = maximumProductPrice(choiceProducts);
  const giftUnitWorst = maximumProductPrice(giftProducts);
  const paidGoodsCost = choiceUnitWorst === null ? null : choiceUnitWorst * collection.offer.paidQty;
  const giftQuantity = Math.max(0, collection.offer.deliveredQty - collection.offer.paidQty);
  const giftGoodsCost = giftUnitWorst === null ? null : giftUnitWorst * giftQuantity;
  const isCandidate = collection.offer.type === "candidate_pool";
  const readiness = collection.missingProductCount > 0 || collection.missingPriceCount > 0
    ? "数据有缺"
    : !collection.offerReady
      ? "待补高清"
      : collection.unknownStockCount > 0
        ? "库存和运费待问"
        : isCandidate ? "可以挑选" : "运费待算";

  return (
    <div className="drawer-backdrop campaign-detail-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <aside className="campaign-drawer glass-panel" aria-modal="true" role="dialog" aria-label={`${collection.title}主题详情`}>
        <button className="drawer-close" onClick={onClose} aria-label="关闭主题详情">×</button>
        <header className="campaign-detail-hero" style={{ "--campaign-a": collection.palette[0], "--campaign-b": collection.palette[1] ?? collection.palette[0] } as React.CSSProperties}>
          <div className="campaign-detail-mosaic" aria-hidden="true">
            {collection.covers.slice(0, 4).map((cover) => <ProductImage key={cover.productId} src={cover.thumbnailUrl} alt="" />)}
            {Array.from({ length: Math.max(0, 4 - collection.covers.length) }, (_, index) => <span className="mosaic-fallback" key={index}>🐾</span>)}
          </div>
          <div className="campaign-detail-heading">
            <div className="campaign-detail-kicker"><span>{collection.offer.label}</span><b>{readiness}</b></div>
            <h2>{collection.title}</h2>
            <p>{collection.subtitle}</p>
            <div className="campaign-detail-tags">{collection.themeTags.map((tag) => <span key={tag}>{tag}</span>)}</div>
          </div>
        </header>

        <div className="campaign-detail-content">
          <section className="campaign-detail-section" aria-label="价格和成本">
            <div className="campaign-section-title"><div><h3>价格和成本</h3></div></div>
            <div className="campaign-kpi-grid">
              {isCandidate
                ? <div><small>售价</small><strong>未定</strong><span>先挑货，再定价</span></div>
                : <div><small>活动售价</small><strong>{formatPrice(collection.offer.salePrice)}</strong><span>付 {collection.offer.paidQty} 件 · 得 {collection.offer.deliveredQty} 件</span></div>}
              <div><small>{isCandidate ? "最高进价" : "最高成本"}</small><strong>{formatPrice(collection.worstGoodsCost)}</strong><span>{isCandidate ? "按最贵的一款算" : "按最贵的组合算"}</span></div>
              {isCandidate
                ? <div><small>玩法</small><strong>未定</strong><span>组合和赠品之后再定</span></div>
                : <div><small>留给运费等</small><strong>{formatPrice(collection.remainingBeforeNonGoodsCosts)}</strong><span>运费、包装、平台费、售后</span></div>}
              <div><small>库存</small><strong>{collection.unknownStockCount === 0 ? "全部已知" : `${collection.unknownStockCount} 款待问`}</strong><span>{collection.productCount - collection.unknownStockCount} / {collection.productCount} 款有库存数</span></div>
              <div><small>图片合格</small><strong>{collection.imageReadyCount} / {collection.productCount}</strong><span>平均 {collection.averageImageQualityScore ?? "—"} 分 · {collection.needsHdCount} 款待补高清</span></div>
            </div>
            <div className="campaign-cost-breakdown">
              <span>{isCandidate ? <>最高进价 <b>{formatPrice(choiceUnitWorst)}</b>，售价未定</> : <>正价款最高 <b>{formatPrice(choiceUnitWorst)}</b> × {collection.offer.paidQty} = <b>{formatPrice(paidGoodsCost)}</b></>}</span>
              {giftQuantity > 0 && <span>赠品最高 <b>{formatPrice(giftUnitWorst)}</b> × {giftQuantity} = <b>{formatPrice(giftGoodsCost)}</b></span>}
              <span>共 <b>{suppliers.length || "—"}</b> 家供应商，能否混装、重量和运费待问</span>
            </div>
          </section>

          <section className="campaign-detail-section" aria-label="规则和注意事项">
            <div className="campaign-section-title"><div><h3>规则和注意事项</h3></div></div>
            <div className="campaign-rule-card"><strong>{isCandidate ? "怎么挑" : "顾客怎么买"}</strong><p>{collection.skuRule}</p></div>
            <ul className="campaign-warning-list">
              {collection.warnings.map((warning) => <li key={warning}>{warning}</li>)}
              <li>{collection.platformNote}</li>
            </ul>
          </section>

          {error && <div className="state-panel"><strong>主题商品读取失败</strong><p>{error}</p><button type="button" className="button soft" onClick={() => void load()}>重试</button></div>}
          {!data && !error && <div className="campaign-loading">正在整理主题商品…</div>}
          {data && <>
            <CampaignProductPool title={isCandidate ? "高清备选" : "正价商品"} subtitle={`${choiceProducts.length} 款 · 原 ${collection.candidateCount} 款`} products={choiceProducts} role="choice" candidate={isCandidate} onOpenProduct={onOpenProduct} />
            {giftProducts.length > 0 && <CampaignProductPool title="固定赠品" subtitle={`${giftProducts.length} 款 · 不能换成正价款`} products={giftProducts} role="gift" candidate={false} onOpenProduct={onOpenProduct} />}
            {collection.needsHdCount > 0 && <section className="campaign-detail-section warning-section"><h3>待补高清</h3><p>{collection.needsHdCount} 款图片不够清晰，先不放进来。</p></section>}
          </>}
        </div>

        <footer className="campaign-detail-footer"><span>{isCandidate ? "只显示高清备选，还没定上架。" : "只显示图片合格的商品。"}</span><div><button type="button" className="button primary" data-testid={`detail-start-visual-${collection.id}`} aria-label={`去做图：${collection.title}`} onClick={onStartVisual}>去做图</button><button type="button" className="button ghost" onClick={onClose}>回到商品列表</button></div></footer>
      </aside>
    </div>
  );
}

function CampaignProductPool({ title, subtitle, products, role, candidate, onOpenProduct }: {
  title: string;
  subtitle: string;
  products: ProductSummary[];
  role: "choice" | "gift";
  candidate: boolean;
  onOpenProduct: (id: string) => void;
}) {
  return (
    <section className="campaign-detail-section campaign-product-pool" aria-label={title}>
      <div className="campaign-section-title"><div><h3>{title}</h3></div><small>{subtitle}</small></div>
      <div className="campaign-product-grid">
        {products.map((product) => (
          <button type="button" className="campaign-product" key={product.id} onClick={() => onOpenProduct(product.id)} aria-label={`查看商品详情：${product.name}`}>
            <div className="campaign-product-image"><ProductImage src={product.thumbnailUrl} alt="" /><span className={!product.imageQuality.uploadReady ? "poor" : role}>{!product.imageQuality.uploadReady ? "待补高清" : candidate ? "高清备选" : role === "gift" ? "赠品" : "正价款"}</span></div>
            <div className="campaign-product-copy"><strong>{product.name}</strong><small>{product.supplier.name} · {product.sourceCatalogId ?? product.offerId ?? product.id}</small><ImageQualityBadge product={product} compact /><div><b>{formatPrice(conservativeProductPrice(product))}</b><span>库存 {compactNumber(product.stockTotal)}</span></div></div>
          </button>
        ))}
      </div>
    </section>
  );
}

function Metric({ label, value, accent = false }: { label: string; value?: number; accent?: boolean }) {
  return <div className={accent ? "metric accent" : "metric"}><strong>{value === undefined ? "—" : compactNumber(value)}</strong><span>{label}</span></div>;
}

function ProductImage({ src, alt, className = "" }: { src: string | null; alt: string; className?: string }) {
  const [state, setState] = useState<ImageLoadState>({ attempt: 0, failures: 0, status: "loading" });
  const [shownSrc, setShownSrc] = useState(src);
  if (shownSrc !== src) {
    setShownSrc(src);
    setState({ attempt: 0, failures: 0, status: "loading" });
  }
  useEffect(() => {
    if (state.status !== "waiting") return;
    const timer = window.setTimeout(() => setState((current) => current.status === "waiting"
      ? { ...current, attempt: current.attempt + 1, status: "loading" }
      : current), IMAGE_RETRY_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [state]);
  function retryNow(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    setState((current) => ({ attempt: current.attempt + 1, failures: 0, status: "loading" }));
  }
  if (!src) return <div className={`image-fallback ${className}`}><span>🐾</span><small>暂无图片</small></div>;
  if (state.status === "waiting" || state.status === "failed") {
    const failed = state.status === "failed";
    return <div
      className={`image-fallback product-image-wait ${failed ? "failed" : "waiting"} ${className}`}
      title={failed ? "点一下重试" : "图片加载中"}
      onClick={failed ? retryNow : undefined}
    ><span>{failed ? "↻" : "…"}</span><small>{failed ? "图片还在路上 · 点一下重试" : "加载中…"}</small></div>;
  }
  const url = retryImageUrl(src, state.attempt);
  return <img
    key={url}
    className={`${className}${state.status === "loading" ? " product-image-loading" : ""}`}
    src={url}
    alt={alt}
    loading="lazy"
    decoding="async"
    onLoad={() => setState((current) => ({ ...current, status: "loaded" }))}
    onError={() => setState((current) => imageLoadAfterError(current))}
  />;
}

function ImageQualityBadge({ product, compact = false }: { product: ProductSummary; compact?: boolean }) {
  const quality = product.imageQuality;
  const dimensions = quality.mainWidth && quality.mainHeight ? `${quality.mainWidth}×${quality.mainHeight}` : "尺寸未知";
  const readiness = quality.uploadReady ? imageQualityLabels[quality.grade] : "待补高清";
  const badgeGrade = quality.uploadReady ? quality.grade : "poor";
  const title = `${readiness}，${quality.score} 分，主图 ${dimensions}，${quality.imageCount} 张图（${quality.highResImageCount} 张高清）`;
  return <span className={`image-quality-badge quality-${badgeGrade} ${compact ? "compact" : ""}`} title={title} aria-label={title}>{readiness} · {quality.score}{!compact && ` · ${quality.imageCount}图`}</span>;
}

function ProductCard({ product, campaignRole, campaignIsCandidate, canVote, onOpen, onVote }: { product: ProductSummary; campaignRole?: "choice" | "gift"; campaignIsCandidate: boolean; canVote: boolean; onOpen: (id: string) => void; onVote: (product: ProductSummary, decision: Decision, event: MouseEvent) => void }) {
  const issues = uniqueProductIssues(product);
  return (
    <article className="product-card" onClick={() => onOpen(product.id)} tabIndex={0} aria-label={`查看商品：${product.name}（${product.sourceCatalogId ?? product.offerId ?? product.id}）`} onKeyDown={(event) => { if (event.target === event.currentTarget && (event.key === "Enter" || event.key === " ")) { event.preventDefault(); onOpen(product.id); } }}>
      <div className="product-image-wrap">
        <ProductImage src={product.thumbnailUrl} alt={product.name} />
        <span className={`status-badge final-${product.finalStatus}`}>{finalLabels[product.finalStatus]}</span>
        {campaignRole && <span className={`campaign-role-badge ${campaignRole}`}>{campaignIsCandidate ? "主题备选" : campaignRole === "gift" ? "赠品" : "活动款"}</span>}
        {issues.length > 0 && <span className="quality-count" title={issues.map(issueLabel).join("；")} aria-label={`${issues.length} 个问题：${issues.map(issueLabel).join("；")}`} tabIndex={0}>! {issues.length}</span>}
        <ImageQualityBadge product={product} />
      </div>
      <div className="product-card-body">
        <div className="supplier-line"><span>{product.supplier.name}</span><small>{product.sourceCatalogId ?? product.offerId ?? product.id}</small></div>
        <h3>{product.name}</h3>
        <div className="category-line"><span>{product.category ?? "未分类"}</span><span>库存 {compactNumber(product.stockTotal)}</span></div>
        <div className="price-line"><strong>{formatPrice(product.priceMin)}</strong>{product.priceMax !== null && product.priceMax !== product.priceMin && <span>– {formatPrice(product.priceMax)}</span>}<small>/ {product.unit ?? "件"}</small></div>
        <div className="vote-summary" aria-label={`团队投票：${decisionLabels.selected} ${product.voteCounts.selected}，${decisionLabels.pending} ${product.voteCounts.pending}，${decisionLabels.rejected} ${product.voteCounts.rejected}`}><span><i className="dot selected" />{product.voteCounts.selected}</span><span><i className="dot pending" />{product.voteCounts.pending}</span><span><i className="dot rejected" />{product.voteCounts.rejected}</span></div>
        {canVote && <div className="quick-votes" onClick={(event) => event.stopPropagation()}>{(["selected", "pending", "rejected"] as Decision[]).map((value) => <button type="button" key={value} aria-label={`${product.name}：${decisionLabels[value]}`} className={product.myVote === value ? `active ${value}` : ""} onClick={(event) => onVote(product, value, event)}>{decisionLabels[value]}</button>)}</div>}
      </div>
    </article>
  );
}

function ProductTable({ products, campaignRoles, campaignIsCandidate, canVote, onOpen, onVote }: { products: ProductSummary[]; campaignRoles: Map<string, "choice" | "gift">; campaignIsCandidate: boolean; canVote: boolean; onOpen: (id: string) => void; onVote: (product: ProductSummary, decision: Decision, event: MouseEvent) => void }) {
  return (
    <div className="table-wrap"><table className="product-table"><thead><tr><th>商品</th><th>供应商 / 分类</th><th>图片</th><th>批发价</th><th>库存</th><th>团队投票</th><th>我的选择</th><th>最终决定</th></tr></thead><tbody>
      {products.map((product) => {
        const campaignRole = campaignRoles.get(product.id);
        const campaignLabel = campaignRole ? campaignIsCandidate ? "主题备选" : campaignRole === "gift" ? "赠品" : "活动款" : "";
        return <tr key={product.id} onClick={() => onOpen(product.id)}><td><div className="table-product"><ProductImage src={product.thumbnailUrl} alt="" /><div><strong>{product.name}</strong><small>{product.sourceCatalogId ?? product.offerId ?? product.id}{campaignLabel ? ` · ${campaignLabel}` : ""}</small></div></div></td><td><strong>{product.supplier.name}</strong><small>{product.category ?? "未分类"}</small></td><td><ImageQualityBadge product={product} compact /><small>{product.imageQuality.mainWidth && product.imageQuality.mainHeight ? `${product.imageQuality.mainWidth}×${product.imageQuality.mainHeight} · ${product.imageQuality.imageCount} 图` : `${product.imageQuality.imageCount} 图`}</small></td><td className="table-price">{formatPrice(product.priceMin)}</td><td>{compactNumber(product.stockTotal)}</td><td><div className="vote-counts"><span className="selected">选 {product.voteCounts.selected}</span><span className="pending">再看 {product.voteCounts.pending}</span><span className="rejected">不 {product.voteCounts.rejected}</span></div></td><td>{canVote ? <div className="quick-votes compact" onClick={(event) => event.stopPropagation()}>{(["selected", "pending", "rejected"] as Decision[]).map((value) => <button type="button" key={value} aria-label={`${product.name}：${decisionLabels[value]}`} className={product.myVote === value ? `active ${value}` : ""} onClick={(event) => onVote(product, value, event)}>{decisionLabels[value]}</button>)}</div> : "—"}</td><td><span className={`status-badge inline final-${product.finalStatus}`}>{finalLabels[product.finalStatus]}</span></td></tr>;
      })}
    </tbody></table></div>
  );
}

function Pagination({ pagination, onPage }: { pagination: CatalogData["pagination"]; onPage: (page: number) => void }) {
  if (pagination.pages <= 1) return null;
  return <nav className="pagination" aria-label="分页"><button disabled={pagination.page <= 1} onClick={() => onPage(pagination.page - 1)}>上一页</button><span>第 {pagination.page} / {pagination.pages} 页</span><button disabled={pagination.page >= pagination.pages} onClick={() => onPage(pagination.page + 1)}>下一页</button></nav>;
}

function ProductDrawer({ productId, session, onClose, onChanged, notify }: { productId: string; session: SessionInfo; onClose: () => void; onChanged: () => void; notify: (message: string) => void }) {
  const [data, setData] = useState<ProductDetailData | null>(null);
  const [error, setError] = useState("");
  const [imageIndex, setImageIndex] = useState(0);
  const [note, setNote] = useState("");
  const [draftTitle, setDraftTitle] = useState("");
  const [draftDescription, setDraftDescription] = useState("");
  const [copyState, setCopyState] = useState<CopyStatus>("draft_pending");
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const next = await api<ProductDetailData>(`/api/products/${encodeURIComponent(productId)}`);
      setData(next);
      setDraftTitle(next.product.draftTitle ?? "");
      setDraftDescription(next.product.draftDescription ?? "");
      setCopyState(next.product.copyStatus);
      setError("");
    } catch (caught) {
      setError(errorMessage(caught));
    }
  }, [productId]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => { setImageIndex(0); }, [productId]);
  useEffect(() => {
    const assetCount = data?.assets.length ?? 0;
    function handleKeyboard(event: KeyboardEvent) {
      if (event.key === "Escape") {
        onClose();
        return;
      }
      if (assetCount <= 1 || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      const target = event.target as HTMLElement | null;
      if (target?.closest("input, textarea, select, [contenteditable='true']")) return;
      if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
        event.preventDefault();
        const delta = event.key === "ArrowLeft" ? -1 : 1;
        setImageIndex((current) => (current + delta + assetCount) % assetCount);
      }
    }
    window.addEventListener("keydown", handleKeyboard);
    return () => window.removeEventListener("keydown", handleKeyboard);
  }, [data?.assets.length, onClose]);

  async function vote(value: Decision) {
    setBusy(`vote-${value}`);
    try {
      await api(`/api/products/${encodeURIComponent(productId)}/vote`, { method: "PUT", ...jsonBody({ decision: value }) });
      await load(); onChanged(); notify(`已记录“${decisionLabels[value]}”`);
    } catch (caught) { notify(errorMessage(caught)); } finally { setBusy(""); }
  }

  async function addNote(event: FormEvent) {
    event.preventDefault(); if (!note.trim()) return;
    setBusy("note");
    try {
      await api(`/api/products/${encodeURIComponent(productId)}/notes`, { method: "POST", ...jsonBody({ body: note }) });
      setNote(""); await load(); notify("备注已添加");
    } catch (caught) { notify(errorMessage(caught)); } finally { setBusy(""); }
  }

  async function setFinal(value: FinalStatus) {
    if (!data) return; setBusy("final");
    try {
      await api(`/api/products/${encodeURIComponent(productId)}/final`, { method: "PATCH", ...jsonBody({ finalStatus: value, version: data.product.version }) });
      await load(); onChanged(); notify(`最终决定：${finalLabels[value]}`);
    } catch (caught) { notify(caught instanceof ApiError && caught.code === "VERSION_CONFLICT" ? "别人刚改过这款，已刷新" : errorMessage(caught)); await load(); } finally { setBusy(""); }
  }

  async function saveCopy() {
    if (!data) return; setBusy("copy");
    try {
      await api(`/api/products/${encodeURIComponent(productId)}/copy`, { method: "PATCH", ...jsonBody({ copyStatus: copyState, draftTitle, draftDescription, version: data.product.version }) });
      await load(); onChanged(); notify("文案已保存");
    } catch (caught) { notify(caught instanceof ApiError && caught.code === "VERSION_CONFLICT" ? "别人刚改过这款，已刷新" : errorMessage(caught)); await load(); } finally { setBusy(""); }
  }

  const availableAssetCount = data?.assets.length ?? 0;
  const selectedAsset = data?.assets[imageIndex] ?? data?.assets[0];
  const selectedSourceSize = selectedAsset?.width && selectedAsset.height ? `原图 ${selectedAsset.width}×${selectedAsset.height}` : "尺寸未知";
  const gallerySummary = !data
    ? ""
    : availableAssetCount === 0
      ? data.totalAssetCount > 0 ? `只能看主图 · 共 ${data.totalAssetCount} 张` : "只能看主图"
      : data.unavailableAssetCount > 0
        ? `能看 ${availableAssetCount} 张 · 共 ${data.totalAssetCount} 张`
        : `${selectedSourceSize} · 共 ${availableAssetCount} 张`;

  function stepImage(delta: number) {
    if (availableAssetCount <= 1) return;
    setImageIndex((current) => (current + delta + availableAssetCount) % availableAssetCount);
  }

  const issues = data ? uniqueProductIssues(data.product) : [];
  return (
    <div className="drawer-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <aside className="product-drawer" aria-modal="true" role="dialog" aria-label="商品详情">
        <button className="drawer-close" onClick={onClose} aria-label="关闭">×</button>
        {error && <div className="state-panel"><strong>商品读取失败</strong><p>{error}</p><button className="button soft" onClick={() => void load()}>重试</button></div>}
        {!data && !error && <div className="centered-shell"><p>正在读取商品详情…</p></div>}
        {data && <>
          <div className="drawer-gallery">
            <div className="hero-image">
              <ProductImage src={selectedAsset?.previewUrl ?? data.product.thumbnailUrl} alt={`${data.product.name}，第 ${Math.min(imageIndex + 1, Math.max(availableAssetCount, 1))} 张`} />
              {availableAssetCount > 1 && <>
                <button type="button" className="gallery-nav previous" aria-label="查看上一张图片" title="上一张（←）" onClick={() => stepImage(-1)}>‹</button>
                <button type="button" className="gallery-nav next" aria-label="查看下一张图片" title="下一张（→）" onClick={() => stepImage(1)}>›</button>
                <span className="gallery-count" aria-live="polite" aria-atomic="true">{imageIndex + 1} / {availableAssetCount}</span>
              </>}
            </div>
            {availableAssetCount > 1 && <div className="thumbnail-strip" role="list" aria-label={`商品图片，共 ${availableAssetCount} 张`}>{data.assets.map((asset, index) => <button type="button" role="listitem" key={`${asset.id}-${asset.position}`} className={index === imageIndex ? "active" : ""} aria-label={`查看第 ${index + 1} 张图片，共 ${availableAssetCount} 张`} aria-current={index === imageIndex ? "true" : undefined} title={`第 ${index + 1} 张`} onClick={() => setImageIndex(index)}><ProductImage src={asset.thumbUrl} alt="" /></button>)}</div>}
            <div className="gallery-meta">
              <span>{gallerySummary}</span>
              {selectedAsset?.originalAvailable && selectedAsset.originalUrl ? <a href={selectedAsset.originalUrl}>下载原图 ↘</a> : <span>暂无原图</span>}
            </div>
          </div>
          <div className="drawer-content">
            <div className="supplier-line"><span>{data.product.supplier.name}</span><small>{data.product.sourceCatalogId ?? data.product.offerId ?? data.product.id}</small></div>
            <h2>{data.product.name}</h2>
            <div className="detail-price"><strong>{formatPrice(data.product.priceMin)}</strong>{data.product.priceMax !== null && data.product.priceMax !== data.product.priceMin && <span>– {formatPrice(data.product.priceMax)}</span>}<small>库存 {compactNumber(data.product.stockTotal)} {data.product.unit ?? "件"}</small></div>
            <div className="detail-badges"><span>{data.product.category ?? "未分类"}</span><span className={`final-${data.product.finalStatus}`}>{finalLabels[data.product.finalStatus]}</span><ImageQualityBadge product={data.product} compact />{session.role === "admin" && <span className={`copy-${data.product.copyStatus}`}>{copyLabels[data.product.copyStatus]}</span>}</div>

            <section className={`image-readiness quality-${data.product.imageQuality.uploadReady ? data.product.imageQuality.grade : "poor"}`} aria-label="图片分数">
              <div><span>图片分数</span><strong>{data.product.imageQuality.score}<small>/100</small></strong></div>
              <dl><div><dt>主图</dt><dd>{data.product.imageQuality.mainWidth && data.product.imageQuality.mainHeight ? `${data.product.imageQuality.mainWidth}×${data.product.imageQuality.mainHeight}` : "尺寸未知"}</dd></div><div><dt>图片</dt><dd>{data.product.imageQuality.imageCount} 张 · {data.product.imageQuality.highResImageCount} 张高清</dd></div><div><dt>清晰度</dt><dd>{data.product.imageQuality.sharpnessPercentile === null ? "未测" : `好于 ${data.product.imageQuality.sharpnessPercentile}% 商品`}</dd></div></dl>
              <p>{!data.product.imageQuality.uploadReady ? "图片不够清晰，先补高清图。" : data.product.imageQuality.grade === "ready" ? "图片很好。" : "图片够清晰，发布前再看一眼裁切。"}</p>
            </section>

            {session.role === "reviewer" && <section className="detail-section decision-section"><h3>我的判断</h3><div className="decision-buttons">{(["selected", "pending", "rejected"] as Decision[]).map((value) => <button key={value} className={data.product.myVote === value ? `active ${value}` : value} disabled={Boolean(busy)} onClick={() => void vote(value)}>{decisionLabels[value]}</button>)}</div></section>}

            <section className="detail-section"><h3>团队投票</h3><div className="team-votes">{session.profiles.map((profile) => { const found = data.votes.find((vote) => vote.profile_id === profile.id); return <div key={profile.id}><span style={{ background: profile.color }}>{profile.id}</span><strong>{profile.displayName}</strong><small>{found ? decisionLabels[found.decision] : "还没选"}</small></div>; })}</div></section>

            <section className="detail-section"><h3>货源信息</h3><dl className="facts"><div><dt>材质</dt><dd>{data.product.material ?? "待确认"}</dd></div><div><dt>规格</dt><dd>{data.product.specification ?? "待确认"}</dd></div><div><dt>款式 / 颜色</dt><dd>{data.product.styleSummary ?? "待确认"}</dd></div><div><dt>包装</dt><dd>{data.product.packageInfo ?? "待确认"}</dd></div>{data.product.sourcePriceRaw && <div className="wide-fact"><dt>原始报价</dt><dd>{data.product.sourcePriceRaw}</dd></div>}</dl>{data.product.sourceDescription && <p className="source-copy">{data.product.sourceDescription}</p>}{data.product.productUrl && <a href={data.product.productUrl} target="_blank" rel="noreferrer" className="source-link">打开 1688 货源链接 ↗</a>}</section>

            {data.priceTiers.length > 0 && <section className="detail-section"><h3>阶梯批发价</h3><div className="tier-list">{data.priceTiers.map((tier) => <div key={tier.minQuantity}><span>{tier.rawLabel ?? `${tier.minQuantity} 件起`}</span><strong>{formatPrice(tier.price)}</strong></div>)}</div></section>}
            {data.skus.length > 0 && <section className="detail-section"><h3>规格库存 <em>{data.skus.length}</em></h3><div className="sku-table"><div className="sku-head"><span>款式</span><span>颜色 / 尺寸</span><span>批发价</span><span>库存</span></div>{data.skus.slice(0, 60).map((sku) => <div key={sku.id}><span>{sku.style ?? "默认"}</span><span>{[sku.color, sku.size].filter(Boolean).join(" / ") || "—"}</span><strong>{formatPrice(sku.price)}</strong><span>{compactNumber(sku.stock)}</span></div>)}</div>{data.skus.length > 60 && <p className="muted">仅显示前 60 个规格。</p>}</section>}

            {issues.length > 0 && <section className="detail-section warning-section"><h3>要检查的问题</h3><ul>{issues.map((flag) => <li key={flag}>{issueLabel(flag)}</li>)}</ul></section>}

            {session.role === "admin" && <section className="detail-section admin-section"><div className="section-heading"><div><h3>最终决定和文案</h3></div></div><label className="field"><span>淘宝标题草稿</span><input value={draftTitle} maxLength={120} onChange={(event) => setDraftTitle(event.target.value)} /></label><label className="field"><span>详情描述草稿</span><textarea value={draftDescription} rows={7} maxLength={20000} onChange={(event) => setDraftDescription(event.target.value)} /></label><div className="admin-copy-actions"><select aria-label="文案状态" value={copyState} onChange={(event) => setCopyState(event.target.value as CopyStatus)}><option value="draft_pending">{copyLabels.draft_pending}</option><option value="approved">{copyLabels.approved}</option><option value="needs_edit">{copyLabels.needs_edit}</option></select><button className="button primary" disabled={Boolean(busy)} onClick={() => void saveCopy()}>保存文案</button></div><div className="final-actions"><span>最终决定</span>{(["selected", "pending", "rejected"] as FinalStatus[]).map((value) => <button key={value} className={data.product.finalStatus === value ? `active ${value}` : value} disabled={Boolean(busy)} onClick={() => void setFinal(value)}>{finalLabels[value]}</button>)}</div></section>}

            <section className="detail-section"><h3>备注</h3><form className="note-form" onSubmit={addNote}><textarea value={note} maxLength={2000} onChange={(event) => setNote(event.target.value)} placeholder="添加备注" rows={3} /><button className="button soft" disabled={busy === "note" || !note.trim()}>添加</button></form><div className="notes-list">{data.notes.map((item) => <article key={item.id}><header><strong>{item.author_kind === "admin" ? "管理员" : `伙伴 ${item.author_id}`}</strong><time>{item.created_at}</time></header><p>{item.body}</p></article>)}{data.notes.length === 0 && <p className="muted">暂无备注</p>}</div></section>
          </div>
        </>}
      </aside>
    </div>
  );
}
